import { useState, useEffect, useRef } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { PDFDocument } from "pdf-lib";
import { STATES_DATA, LAST_REVIEWED as STATES_DATA_LAST_REVIEWED } from "./data/states";

// Strip PDF metadata (author, title, dates, producer, etc.) before sending to the AI.
async function stripPdfMetadata(arrayBuffer) {
  try {
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    pdfDoc.setTitle("");
    pdfDoc.setAuthor("");
    pdfDoc.setSubject("");
    pdfDoc.setKeywords([]);
    pdfDoc.setProducer("");
    pdfDoc.setCreator("");
    pdfDoc.setCreationDate(new Date(0));
    pdfDoc.setModificationDate(new Date(0));
    return await pdfDoc.save({ useObjectStreams: false });
  } catch (e) {
    // If pdf-lib can't parse it (e.g. encrypted, malformed), fall back to the original bytes
    return new Uint8Array(arrayBuffer);
  }
}

// Re-render an image through a canvas to drop EXIF/GPS/camera metadata.
async function stripImageMetadata(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        if (!blob) { resolve(file); return; }
        blob.arrayBuffer().then((ab) => resolve(new Uint8Array(ab)));
      }, file.type === "image/png" ? "image/png" : "image/jpeg", 0.92);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

function uint8ToBase64(bytes) {
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}


// Storage helper — wraps localStorage for the same API as artifact storage
const storage = {
  async get(key, shared) {
    try {
      const val = localStorage.getItem(shared ? `shared:${key}` : key);
      return val !== null ? { value: val } : null;
    } catch(e) { return null; }
  },
  async set(key, value, shared) {
    try {
      localStorage.setItem(shared ? `shared:${key}` : key, value);
      return { key, value };
    } catch(e) { return null; }
  },
  async delete(key, shared) {
    try {
      localStorage.removeItem(shared ? `shared:${key}` : key);
      return { key, deleted: true };
    } catch(e) { return null; }
  },
};

/* ═══════════════════════════════════════════
   CONSTANTS & DATA
   ═══════════════════════════════════════════ */

const CATEGORIES = {
  abuse: { label: "Abuse / Incident", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", icon: "⚠" },
  system: { label: "DCF / CPS Contact", color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE", icon: "◉" },
  legal: { label: "Legal / Court", color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE", icon: "§" },
  police: { label: "Police / Report", color: "#0891B2", bg: "#ECFEFF", border: "#A5F3FC", icon: "★" },
  medical: { label: "Medical / Mental Health", color: "#059669", bg: "#F0FDF4", border: "#A7F3D0", icon: "+" },
  placement: { label: "Placement / Foster Care", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", icon: "⌂" },
  family: { label: "Family Event", color: "#BE185D", bg: "#FDF2F8", border: "#FBCFE8", icon: "◆" },
  other: { label: "Other", color: "#6B7280", bg: "#F9FAFB", border: "#E5E7EB", icon: "●" },
};

const FAILURE_TYPES = [
  { id: "investigated_closed", label: "Investigated and closed without action" },
  { id: "charges_dropped", label: "Charges dropped or reduced" },
  { id: "returned_home", label: "Child returned to dangerous home" },
  { id: "continued_after_contact", label: "Abuse continued after system contact" },
  { id: "ignored_report", label: "Reports or concerns ignored" },
  { id: "guardian_approved", label: "Dangerous guardian approved" },
  { id: "no_followup", label: "No follow-up after initial contact" },
  { id: "records_lost", label: "Records lost or unavailable" },
  { id: "multiple_agencies", label: "Multiple agencies failed to communicate" },
  { id: "retaliation", label: "Retaliation for disclosing" },
];

const AGENCY_TYPES = [
  "DCF / CPS", "Foster Care System", "Probate / Family Court",
  "Youth Organization (BSA, church, etc.)", "Juvenile Detention",
  "School System", "Law Enforcement", "Other",
];

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio",
  "Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota",
  "Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming","DC",
];

const CHART_COLORS = ["#2563EB","#7C3AED","#DC2626","#059669","#D97706","#0891B2","#BE185D","#6B7280"];

/* Seed reports disabled for launch — pattern database starts empty
const SEED_REPORTS = [...];
*/

/* ═══════════════════════════════════════════
   UTILITIES
   ═══════════════════════════════════════════ */

function genId() { return Date.now().toString(36) + Math.random().toString(36).substr(2, 5); }

function expandAcronyms(text) {
  if (!text) return text;
  return text
    .replace(/\bNo SOL\b/g, "No filing deadline")
    .replace(/\bcivil SOL\b/g, "civil filing deadline")
    .replace(/\bcriminal SOL\b/g, "criminal filing deadline")
    .replace(/\bSOL\b/g, "statute of limitations")
    .replace(/\bCSA\b/g, "childhood sexual abuse")
    .replace(/\bCVA\b/g, "Child Victims Act")
    .replace(/\bGMVA\b/g, "Gender-Motivated Violence Act")
    .replace(/\bICWA\b/g, "Indian Child Welfare Act")
    .replace(/\bANCRA\b/g, "Abused and Neglected Child Reporting Act");
}

function fmtDate(d) {
  if (!d) return "";
  const [y,m,day] = d.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(m)-1]} ${parseInt(day)}, ${y}`;
}

function detectClusters(reports) {
  const map = {};
  reports.forEach(r => {
    const k = `${r.state}—${r.agency}`;
    if (!map[k]) map[k] = { state: r.state, agency: r.agency, reports: [] };
    map[k].reports.push(r);
  });
  return Object.values(map).filter(c => c.reports.length >= 3).map(c => {
    const fc = {};
    c.reports.flatMap(r => r.failures).forEach(f => fc[f] = (fc[f]||0)+1);
    return {
      ...c,
      count: c.reports.length,
      avgContacts: Math.round(c.reports.reduce((s,r)=>s+r.contacts,0)/c.reports.length),
      avgYears: Math.round(c.reports.reduce((s,r)=>s+r.years,0)/c.reports.length),
      continuedPct: Math.round(c.reports.filter(r=>r.failures.includes("continued_after_contact")).length/c.reports.length*100),
      topFailures: Object.entries(fc).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([id,count])=>({
        id, label: FAILURE_TYPES.find(f=>f.id===id)?.label||id, pct: Math.round(count/c.reports.length*100)
      })),
    };
  }).sort((a,b) => b.count - a.count);
}

/* ═══════════════════════════════════════════
   STYLES
   ═══════════════════════════════════════════ */

const fonts = `'DM Sans', system-ui, sans-serif`;
const serifFonts = `'Source Serif 4', Georgia, serif`;
const inputSt = { width:"100%", padding:"11px 14px", borderRadius:8, border:"1px solid #D1D5DB", fontFamily:fonts, fontSize:14, color:"#1a1a1a", backgroundColor:"#fff", outline:"none", boxSizing:"border-box" };
const labelSt = { fontFamily:fonts, fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6, display:"block" };
const sectionTitle = { fontFamily:serifFonts, fontSize:22, fontWeight:700, color:"#1a1a1a", letterSpacing:"-0.02em", margin:"0 0 8px" };
const bodyText = { fontFamily:serifFonts, fontSize:15, color:"#374151", lineHeight:1.8 };
const cardSt = { backgroundColor:"#fff", borderRadius:10, border:"1px solid #E5E7EB", padding:16, marginBottom:14 };

/* ═══════════════════════════════════════════
   CRISIS BAR
   ═══════════════════════════════════════════ */

function CrisisBar({ expanded, onToggle, readingMode, onToggleReading }) {
  return (
    <div style={{ borderBottom: expanded ? "1px solid #FECACA" : "none" }}>
      <div style={{ display:"flex", alignItems:"center", backgroundColor: expanded ? "#FEF2F2" : "transparent" }}>
        <button onClick={onToggle} style={{
          flex:1, padding:"8px 12px 8px 20px", border:"none", backgroundColor:"transparent",
          cursor:"pointer", fontFamily:fonts, fontSize:12, color: expanded ? "#991B1B" : "#9CA3AF",
          textAlign:"left", fontWeight:500,
        }}>
          {expanded ? "▲ Close" : "Need help right now? Tap here."}
        </button>
        <div data-no-print="true" style={{ display:"flex", gap:4, alignItems:"center", paddingRight:10 }}>
          <button
            onClick={onToggleReading}
            aria-label={readingMode === "large" ? "Switch to normal text size" : "Switch to larger text size"}
            style={{
              padding:"5px 8px", borderRadius:14, border:"1px solid #D1D5DB",
              backgroundColor:"#fff", color:"#374151",
              fontFamily:fonts, fontSize:11, fontWeight:700, cursor:"pointer",
              display:"flex", alignItems:"center", gap:2,
              boxShadow:"0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            <span style={{ fontSize:12 }}>A</span>
            <span style={{ fontSize:10 }}>{readingMode === "large" ? "−" : "+"}</span>
          </button>
          <QuickExitButton />
        </div>
      </div>
      {expanded && (
        <div style={{ padding:"0 20px 14px", fontSize:13, lineHeight:1.8, color:"#991B1B", backgroundColor:"#FEF2F2", textAlign:"center" }}>
          <div><strong>911</strong> — if you're in immediate danger</div>
          <div><strong>988</strong> — Suicide & Crisis Lifeline (call or text)</div>
          <div><strong>1-800-656-4673</strong> — RAINN Sexual Assault Hotline</div>
          <div><strong>1-800-422-4453</strong> — Childhelp Abuse Hotline</div>
          <div><strong>Text HOME to 741741</strong> — Crisis Text Line</div>
        </div>
      )}
    </div>
  );
}

function LanguageBar() {
  const [show, setShow] = useState(false);
  const langs = [
    { code:"es", label:"Español" },
    { code:"zh-CN", label:"中文" },
    { code:"ht", label:"Kreyòl" },
    { code:"vi", label:"Tiếng Việt" },
    { code:"ko", label:"한국어" },
    { code:"ar", label:"العربية" },
    { code:"ru", label:"Русский" },
    { code:"pt", label:"Português" },
    { code:"fr", label:"Français" },
    { code:"tl", label:"Tagalog" },
    { code:"so", label:"Soomaali" },
    { code:"hi", label:"हिन्दी" },
    { code:"my", label:"မြန်မာ" },
    { code:"ne", label:"नेपाली" },
  ];
  const translate = (code) => {
    const url = `https://translate.google.com/translate?sl=en&tl=${code}&u=${encodeURIComponent(window.location.href)}`;
    window.open(url, "_blank");
  };
  return (
    <div>
      <button onClick={()=>setShow(!show)} style={{
        width:"100%", padding:"6px 20px", border:"none", borderBottom: show ? "none" : "1px solid #E5E7EB",
        backgroundColor: show ? "#F8FAFC" : "transparent",
        cursor:"pointer", fontFamily:fonts, fontSize:11, color:"#9CA3AF",
        textAlign:"center", fontWeight:500,
      }}>
        {show ? "▲ Close" : "🌐 Traducir · 翻译 · Translate"}
      </button>
      {show && (
        <div style={{ padding:"8px 20px 12px", backgroundColor:"#F8FAFC", borderBottom:"1px solid #E5E7EB" }}>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, justifyContent:"center" }}>
            {langs.map(l => (
              <button key={l.code} onClick={()=>translate(l.code)} style={{
                padding:"5px 10px", borderRadius:16, border:"1px solid #E5E7EB",
                backgroundColor:"#fff", color:"#374151", fontFamily:fonts, fontSize:11,
                cursor:"pointer", fontWeight:500,
              }}>
                {l.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize:10, color:"#9CA3AF", textAlign:"center", marginTop:8, lineHeight:1.5 }}>
            Translations are provided by Google Translate and may not be perfectly accurate. Legal terms should be verified with a professional in your language.
          </div>
        </div>
      )}
    </div>
  );
}

function BetaBanner({ setPage }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("gmr_beta_dismissed") === "1"; } catch { return false; }
  });
  if (dismissed) return null;
  const dismiss = () => {
    try { localStorage.setItem("gmr_beta_dismissed", "1"); } catch {}
    setDismissed(true);
  };
  return (
    <div style={{
      backgroundColor:"#FFFBEB", borderBottom:"1px solid #FDE68A",
      padding:"8px 16px", display:"flex", alignItems:"center", gap:8,
      fontFamily:fonts, fontSize:12, color:"#78350F", lineHeight:1.5,
    }}>
      <div style={{ flex:1 }}>
        <strong>This site is new — help us get it right.</strong>{" "}
        We're verifying state-specific legal data with attorneys and survivors. If something looks wrong or incomplete,{" "}
        <button onClick={()=>setPage("contribute")} style={{
          background:"none", border:"none", padding:0, color:"#92400E",
          textDecoration:"underline", cursor:"pointer", fontFamily:fonts, fontSize:12, fontWeight:600,
        }}>tell us</button>.
        Your feedback makes this better for the next person.
      </div>
      <button onClick={dismiss} aria-label="Dismiss" style={{
        background:"none", border:"none", padding:"2px 6px", cursor:"pointer",
        color:"#92400E", fontSize:16, fontWeight:700, lineHeight:1,
      }}>×</button>
    </div>
  );
}

/* ═══════════════════════════════════════════
   NAV BAR
   ═══════════════════════════════════════════ */

function NavBar({ page, setPage }) {
  const items = [
    { id:"home", label:"Home", icon:"⌂" },
    { id:"case", label:"My Case", icon:"◧" },
    { id:"records", label:"Records", icon:"◫" },
    { id:"about", label:"About", icon:"○" },
  ];
  return (
    <div style={{
      position:"fixed", bottom:0, left:0, right:0,
      backgroundColor:"#fff", borderTop:"1px solid #E5E7EB",
      display:"flex", zIndex:100,
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
    }}>
      {items.map(it => (
        <button key={it.id} onClick={() => setPage(it.id)} style={{
          flex:1, padding:"8px 0 6px", border:"none", backgroundColor:"transparent",
          cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:2,
        }}>
          <span style={{ fontSize:18, color: page===it.id ? "#1a1a1a" : "#D1D5DB" }}>{it.icon}</span>
          <span style={{ fontFamily:fonts, fontSize:10, fontWeight:600, color: page===it.id ? "#1a1a1a" : "#9CA3AF" }}>{it.label}</span>
        </button>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════
   HOME PAGE
   ═══════════════════════════════════════════ */

function HomePage({ setPage, reportCount }) {
  const paths = [
    { label:"I'm trying to understand what happened to me", sub:"You don't need to have it figured out. Start here.", color:"#7C3AED", page:"sense" },
    { label:"I know what happened and I want to do something", sub:"We'll walk you through it. Step by step.", color:"#2563EB", page:"case" },
    { label:"I'm helping someone I care about", sub:"A family member, a friend, or someone you work with.", color:"#059669", page:"case" },
    { label:"I need help right now", sub:"Crisis lines. No judgment. Available 24/7.", color:"#DC2626", page:"crisis" },
  ];
  return (
    <div>
      <div style={{
        background:"linear-gradient(160deg, #0f0f0f 0%, #1a1a1a 40%, #262626 100%)",
        padding:"36px 20px 32px",
      }}>
        <div style={{ maxWidth:640, margin:"0 auto" }}>
          <h1 style={{ fontFamily:serifFonts, fontSize:28, fontWeight:700, color:"#fff", margin:"0 0 12px", letterSpacing:"-0.025em", lineHeight:1.2 }}>
            The system kept records about you.<br/>They're yours.
          </h1>
          <p style={{ fontSize:15, color:"#9CA3AF", margin:0, lineHeight:1.8, maxWidth:520 }}>
            Request your records. Search for who else was harmed. Find out what the system knew and when they knew it. Build your case — or just understand what happened. Free. Private. No sign-up.
          </p>
          {/* reportCount badge disabled for launch
          {reportCount > 0 && (
            <button onClick={() => setPage("pattern")} style={{
              marginTop:16, padding:"8px 16px", borderRadius:20, border:"1px solid rgba(255,255,255,0.15)",
              backgroundColor:"rgba(255,255,255,0.06)", cursor:"pointer",
              fontFamily:fonts, fontSize:12, fontWeight:600, color:"#9CA3AF",
            }}>
              {reportCount} people have shared their experience →
            </button>
          )}
          */}
        </div>
      </div>
      <div style={{ maxWidth:640, margin:"0 auto", padding:"24px 20px" }}>
        <div style={{ fontSize:11, fontWeight:600, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>
          Who is this for?
        </div>
        <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.7, marginBottom:20 }}>
          Anyone who was abused as a child — whether it happened at home, in foster care, in a church, school, or youth organization — and anyone helping them. If a system was supposed to protect you and didn't, there are records. All 50 states. All free.
        </div>
        <div style={{ fontSize:11, fontWeight:600, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:12 }}>
          Where are you right now?
        </div>
        {paths.map((p,i) => (
          <button key={i} onClick={() => setPage(p.page)} style={{
            width:"100%", padding:"14px 16px", borderRadius:10, border:"1px solid #E5E7EB",
            backgroundColor:"#fff", cursor:"pointer", textAlign:"left", marginBottom:8,
            display:"flex", justifyContent:"space-between", alignItems:"center",
          }}>
            <div>
              <div style={{ fontFamily:fonts, fontSize:14, fontWeight:600, color:"#1a1a1a", marginBottom:2, lineHeight:1.4 }}>{p.label}</div>
              <div style={{ fontFamily:fonts, fontSize:12, color:"#9CA3AF" }}>{p.sub}</div>
            </div>
            <span style={{ color:"#D1D5DB", fontSize:18, marginLeft:8 }}>→</span>
          </button>
        ))}
        <div style={{ marginTop:12, padding:12, borderRadius:8, fontSize:12, color:"#9CA3AF", lineHeight:1.6 }}>
          🔒 This site doesn't track you and doesn't collect your information. Some tools save data on your device that you can delete anytime. If someone might look at your phone, use private/incognito mode.
        </div>

        <div style={{ marginTop:20, borderTop:"1px solid #E5E7EB", paddingTop:20 }}>
          <button onClick={()=>setPage("contribute")} style={{
            width:"100%", padding:"14px 16px", borderRadius:10,
            border:"1.5px solid #BFDBFE", backgroundColor:"#EFF6FF",
            cursor:"pointer", textAlign:"left",
          }}>
            <div style={{ fontFamily:fonts, fontSize:14, fontWeight:600, color:"#1E40AF", marginBottom:2 }}>Help build this</div>
            <div style={{ fontFamily:fonts, fontSize:12, color:"#6B7280" }}>Contribute corrections, real-world records request reports, or volunteer expertise. This site is maintained by the internet, together.</div>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   "MAKING SENSE" PAGE
   ═══════════════════════════════════════════ */

function SensePage({ setPage }) {
  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
      <h1 style={sectionTitle}>You're not crazy. It wasn't okay. And it wasn't your fault.</h1>
      <div style={{ ...bodyText, marginBottom:24 }}>
        <p>Maybe you've always known something was wrong but couldn't name it. Maybe someone told you it was normal, or that you should be grateful. Maybe you're just now realizing that what happened to you wasn't what happened to everyone.</p>
        <p>If CPS or a child welfare agency was involved in your family, that means someone reported a concern about your safety. The state looked into it. They wrote things down. Those records still exist, and the law says you can have them.</p>
        <p>You don't have to do anything right now. But when you're ready, your records can help. They can confirm what you remember. They can fill in things you didn't know. And sometimes they show that the people who were supposed to protect you made choices they shouldn't have.</p>
      </div>

      <div style={{ ...cardSt, backgroundColor:"#F8FAFC", marginBottom:16 }}>
        <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1a1a1a", marginBottom:6 }}>Is this safe?</div>
        <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.7 }}>
          <p style={{margin:"0 0 6px"}}>Yes. Requesting your records is not the same as re-entering the system. No one will show up at your home. No new case will be opened. You're just asking for papers that already exist about your own life.</p>
          <p style={{margin:0}}>This website doesn't track you, doesn't collect your information, and doesn't share anything with any agency. The letters are generated on your device and only you see them.</p>
        </div>
      </div>
      <button onClick={() => setPage("records")} style={{
        width:"100%", padding:"14px", borderRadius:10, border:"none", backgroundColor:"#1a1a1a",
        color:"#fff", fontFamily:fonts, fontSize:15, fontWeight:600, cursor:"pointer", marginBottom:10,
      }}>
        Learn how to request your records →
      </button>
      <button onClick={() => setPage("prep")} style={{
        width:"100%", padding:"12px", borderRadius:10, border:"1px solid #DDD6FE", backgroundColor:"#F5F3FF",
        color:"#5B21B6", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer",
      }}>
        Before you read your records — what to expect →
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════
   "BEFORE YOU READ YOUR RECORDS" GUIDE
   ═══════════════════════════════════════════ */

// Update this whenever you review the state-level legal data (statutes of limitations,
// agency addresses, lookback windows, etc.) and surface it in the LegalDisclaimer below.
// Last cross-referenced against CHILD USA SOL Tracker April 16, 2026.
const LEGAL_DATA_LAST_REVIEWED = "April 16, 2026";

function LegalDisclaimer() {
  return (
    <div role="note" aria-label="Not legal advice" style={{
      padding:"12px 14px", borderRadius:10, backgroundColor:"#FFFBEB",
      border:"1px solid #FDE68A", marginBottom:16,
      fontSize:12, color:"#92400E", lineHeight:1.6,
    }}>
      <strong>This is not legal advice.</strong> The information here is general guidance and is not a substitute for an attorney licensed in your state. If you are weighing legal action, deadlines, or records that may support a claim, consult an attorney. Many handle child welfare and abuse cases on contingency (no fee unless you win) and offer free consultations.
      <div style={{ marginTop:6, fontSize:11, color:"#B45309" }}>
        State-specific legal information on this site was last reviewed in <strong>{LEGAL_DATA_LAST_REVIEWED}</strong>. Laws change. Verify deadlines and procedures with an attorney before acting on them.
      </div>
    </div>
  );
}

function PrepPage({ setPage }) {
  const [openSection, setOpenSection] = useState(null);
  const toggle = (id) => setOpenSection(p => p === id ? null : id);

  const sections = [
    {
      id: "what_to_expect",
      title: "What it's like to read your records",
      content: [
        "Your records were written by caseworkers, investigators, and officers who documented your life in clinical, bureaucratic language. They describe the people you loved, the places you lived, and the things that happened to you in the same flat tone they'd use for a building inspection.",
        "Some people find this dehumanizing. Some find it validating — finally, someone wrote it down. Most feel both at the same time.",
        "You may read things you already know described in ways that make them feel new. You may learn things no one ever told you. You may see that a caseworker documented something terrible and then recommended closing the case. You may find gaps — months or years where nothing was recorded despite ongoing contact.",
        "All of these reactions are normal. There is no wrong way to respond to reading your own file."
      ]
    },
    {
      id: "common_reactions",
      title: "What people commonly feel",
      content: [
        "Anger — at the system, at specific people, at the clinical language, at how little was done. This is the most common reaction. It's appropriate.",
        "Grief — for the child in those pages, for what should have happened, for the protection that didn't come. Some people describe it as mourning a childhood they can finally prove they didn't have.",
        "Validation — seeing it written down by someone else can confirm what you've always known. For people who were told they were lying, exaggerating, or remembering wrong, this can be powerful.",
        "Confusion — caseworker notes can be hard to read. Acronyms, codes, legal jargon, unclear handwriting. The timeline might not make sense. That's the system, not you.",
        "Numbness — some people feel nothing at first. This is a normal protective response. The feelings may come later, or they may not. Both are okay.",
        "Relief — some people feel lighter after reading their records. Understanding what happened can reduce the power it holds."
      ]
    },
    {
      id: "before_you_start",
      title: "Before you open the envelope",
      content: [
        "Choose when. Don't read your records the day they arrive. Put them somewhere safe and choose a day when you have time and space — not before work, not late at night, not when you're already having a hard day.",
        "Choose where. Somewhere private where you can react however you need to. Your home. A therapist's office. Not a coffee shop, not at work, not on the bus.",
        "Have someone available. Not necessarily in the room — but someone you can call or text. A therapist, a trusted friend, a partner, a sibling who understands. Let them know what you're doing that day.",
        "You don't have to read it all at once. Some people read a few pages and stop for days or weeks. Some read the whole thing in one sitting. Some start at the end. Some never finish. There is no right way.",
        "Have comfort items nearby. Water, a blanket, your phone, a pet. Whatever grounds you.",
        "Write down what you want to know before you start reading. Having specific questions can help you navigate instead of being swept along."
      ]
    },
    {
      id: "while_reading",
      title: "While you're reading",
      content: [
        "You can stop at any time. Put the file down. Close the folder. Walk away. It will be there when you're ready.",
        "Your body will tell you when to take a break. If you notice your hands shaking, your breathing changing, your chest tightening, or a feeling of floating — those are signals. Honor them.",
        "Some things will be wrong. Caseworkers make mistakes. Dates will be wrong, names will be misspelled, details will be inaccurate. Your memory is also a record. Both can be true at the same time.",
        "You may want to write in the margins. Some people annotate their files — correcting errors, adding context the caseworker didn't have, writing what was really happening. This is your document now.",
        "If you find yourself unable to stop reading even though you want to, that's a trauma response. It doesn't mean you're weak — it means the material is activating your nervous system. Close the file. You can come back tomorrow."
      ]
    },
    {
      id: "after_reading",
      title: "After you've read them",
      content: [
        "The next few days may be harder than the reading itself. Memories you haven't thought about in years may surface. Sleep may be disrupted. You may feel angry, sad, or strangely calm.",
        "Talk to someone. A therapist who understands trauma. A friend who gets it. A support group. The 988 Suicide & Crisis Lifeline (call or text 988) is available 24/7 if you need it.",
        "You don't owe anyone access to your records. They're yours. You decide who sees them, when, and how much. No one is entitled to your story.",
        "Your records are a tool, not the whole truth. They document what the system saw and chose to write down. They don't capture what you felt, what you survived, or who you are. The file is about what happened to you. It is not you.",
        "Some people find it helpful to write their own version — the story behind the caseworker's notes, the context that's missing, the child's perspective that was never recorded.",
        "If your records reveal that the system failed to protect you, that information has power. It can support legal claims, inform advocacy, and help other people. But you don't have to do anything with it. You can just know."
      ]
    },
    {
      id: "for_supporters",
      title: "If you're supporting someone through this",
      content: [
        "Don't read their records without permission. Even if they ask you to read them first. Let them decide how they want to encounter their own story.",
        "Don't minimize what they find. 'At least they investigated' or 'they were trying their best' can feel like defending the system that failed them. Just listen.",
        "Don't push them to take action. They don't need to file a lawsuit, write a letter, or confront anyone. They just need to be allowed to feel whatever they feel.",
        "Check in the next day, and the day after that. The hardest part often comes after the initial reading, not during it.",
        "Take care of yourself too. Secondary trauma is real. Reading about what happened to someone you care about can be its own kind of hard."
      ]
    }
  ];

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
      <h1 style={sectionTitle}>Before You Read Your Records</h1>
      <p style={{ ...bodyText, fontSize:14, color:"#6B7280", marginBottom:24, lineHeight:1.7 }}>
        You requested your records. They're coming — or maybe they've already arrived. Before you open that envelope, read this.
      </p>

      <div style={{ padding:14, borderRadius:10, backgroundColor:"#F5F3FF", border:"1px solid #DDD6FE", marginBottom:20, fontSize:14, color:"#5B21B6", lineHeight:1.7 }}>
        <strong>This is not going to be easy.</strong> That's not a reason not to do it. But you deserve to be prepared.
      </div>

      {sections.map(s => (
        <div key={s.id} style={{ marginBottom:8 }}>
          <button onClick={() => toggle(s.id)} style={{
            width:"100%", padding:"14px 16px", borderRadius: openSection===s.id ? "10px 10px 0 0" : 10,
            border:"1px solid #E5E7EB", borderBottom: openSection===s.id ? "none" : "1px solid #E5E7EB",
            backgroundColor: openSection===s.id ? "#FAFAF9" : "#fff",
            cursor:"pointer", textAlign:"left", display:"flex", justifyContent:"space-between", alignItems:"center",
          }}>
            <span style={{ fontFamily:fonts, fontSize:14, fontWeight:600, color:"#1a1a1a" }}>{s.title}</span>
            <span style={{ fontSize:16, color:"#9CA3AF", transform: openSection===s.id ? "rotate(180deg)" : "none", transition:"transform 0.2s" }}>▾</span>
          </button>
          {openSection===s.id && (
            <div style={{ padding:"16px", borderRadius:"0 0 10px 10px", border:"1px solid #E5E7EB", borderTop:"none", backgroundColor:"#FAFAF9" }}>
              {s.content.map((p, i) => (
                <p key={i} style={{ fontSize:13, color:"#374151", lineHeight:1.8, margin: i===s.content.length-1 ? 0 : "0 0 12px" }}>
                  {p}
                </p>
              ))}
            </div>
          )}
        </div>
      ))}

      <div style={{ marginTop:24 }}>
        <button onClick={() => setPage("records")} style={{
          width:"100%", padding:"14px", borderRadius:10, border:"none", backgroundColor:"#1a1a1a",
          color:"#fff", fontFamily:fonts, fontSize:15, fontWeight:600, cursor:"pointer", marginBottom:10,
        }}>
          I'm ready — take me to Records →
        </button>
        <button onClick={() => setPage("records_guide")} style={{
          width:"100%", padding:"12px", borderRadius:10, border:"1px solid #BFDBFE", backgroundColor:"#EFF6FF",
          color:"#1E40AF", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer", marginBottom:10,
        }}>
          I already have my records — what do I do with them? →
        </button>
        <button onClick={() => setPage("crisis")} style={{
          width:"100%", padding:"12px", borderRadius:10, border:"1px solid #FECACA", backgroundColor:"#FEF2F2",
          color:"#991B1B", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer",
        }}>
          I need to talk to someone right now
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   "WHAT TO DO WITH YOUR RECORDS" GUIDE
   ═══════════════════════════════════════════ */

function RecordsGuidePage({ setPage }) {
  const [openSection, setOpenSection] = useState(null);
  const toggle = (id) => setOpenSection(p => p === id ? null : id);

  const sections = [
    {
      id: "reading_agency_language",
      title: "How to read agency language",
      content: [
        { type:"intro", text:"Child welfare records are written in a bureaucratic dialect designed for internal use. Here's what the key terms actually mean." },
        { type:"term", term:"Substantiated / Indicated / Founded", def:"The agency determined that abuse or neglect DID occur based on the evidence. This is the strongest finding. The exact word varies by state." },
        { type:"term", term:"Unsubstantiated / Unfounded / Not Indicated", def:"The agency decided the evidence was insufficient. This does NOT mean it didn't happen — it means they didn't find enough to meet their standard. Many legitimate cases are unfounded due to poor investigation." },
        { type:"term", term:"Closed at Intake / Screened Out", def:"Someone reported a concern, but the agency decided not to investigate. The report exists but was never followed up on. If this happened multiple times, that's a pattern of ignored reports." },
        { type:"term", term:"Assessed Out / Alternative Response", def:"The agency looked into it but handled it informally — no full investigation, no finding. Often used for lower-risk reports. The concern was real enough to trigger a response but not enough to open a case." },
        { type:"term", term:"Services Recommended / Voluntary Services", def:"The agency told the family to get help but didn't require it. There was no court order. If the family didn't follow through, there may have been no consequence." },
        { type:"term", term:"Reunification", def:"Returning a child to the parent or guardian they were removed from. If reunification happened and abuse continued, that's a system failure documented in the file." },
        { type:"term", term:"TPR (Termination of Parental Rights)", def:"The court permanently ended a parent's legal rights. This is the most extreme step the system takes. If TPR was discussed but not pursued, the file will explain why." },
        { type:"term", term:"Permanency Goal / Permanency Plan", def:"The agency's official plan for the child's future — reunification, adoption, guardianship, or aging out. This changed over time and each change is documented." },
        { type:"term", term:"SDM (Structured Decision-Making) Risk Level", def:"A score from a standardized assessment tool. High, moderate, low. If the risk level was high and the case was still closed, that's significant." },
        { type:"term", term:"LINK / SACWIS / CONNECTIONS / OASIS", def:"State database systems where caseworkers enter notes. Different states use different systems. These are the internal case management records — the caseworker's running diary." },
      ]
    },
    {
      id: "spotting_gaps",
      title: "How to spot what's missing",
      content: [
        { type:"intro", text:"What's NOT in your file can be as important as what is. Here's what to look for." },
        { type:"gap", label:"Timeline gaps", text:"If your file shows CPS contact in 2003 and then nothing until 2007 but you know they were involved during those years, records may be missing. Cross-reference with police records and court records to find what's not in the agency file." },
        { type:"gap", label:"Same-day closures", text:"An investigation opened and closed on the same day — or within 1-2 days — is a red flag. A real investigation takes time. Same-day closures suggest the caseworker made a decision before investigating." },
        { type:"gap", label:"Reports that reference prior reports", text:"If a report says 'this is the 4th referral for this family' but your file only contains 2 prior reports, the others may have been purged, lost, or filed under a different name. Request clarification in writing." },
        { type:"gap", label:"Missing attachments", text:"If a report references 'see attached photos,' 'attached medical report,' or 'see police report' but those attachments aren't in your file, they were either not included or lost. Request them specifically by name." },
        { type:"gap", label:"Redacted sections that seem too large", text:"Reporter names are always redacted — that's normal. But if entire paragraphs or pages are blacked out, the agency may be over-redacting. You can challenge this. Ask for a written explanation of the statutory basis for each redaction." },
        { type:"gap", label:"Different caseworker notes that contradict each other", text:"If one caseworker documented concerns and the next caseworker's notes show everything is fine, look at when the caseworker changed and whether the new one actually re-investigated or just assumed the case was resolved." },
        { type:"gap", label:"No notes during known crisis periods", text:"If you know the police came, the ER was visited, or a school made a report, but there's nothing in the agency file from that period — those records exist somewhere else. Use the police FOIA and court records tools to find them." },
      ]
    },
    {
      id: "legal_significance",
      title: "When your records support a legal claim",
      content: [
        { type:"intro", text:"Not every bad outcome is a legal case. But certain patterns in your records are exactly what attorneys look for." },
        { type:"pattern", label:"Multiple contacts with no protective action", text:"If the file shows 5, 10, 20+ contacts and the agency repeatedly investigated and closed, that's evidence of systemic failure. In one Connecticut case, a child's family was investigated by DCF 27 times over 12 years — the agency even approved the man who was raping her as her legal guardian. She filed a $30 million claim. The pattern of repeated contact with no protection was the core of the case." },
        { type:"pattern", label:"Known danger + approved placement", text:"If the agency knew about a dangerous person (criminal history, prior abuse findings, sex offender status) and still approved them as a guardian, foster parent, or allowed unsupervised access — that's negligence." },
        { type:"pattern", label:"High risk assessment + case closure", text:"If a structured decision-making tool scored the family as 'high risk' and the case was still closed or services were voluntary, the agency overrode its own risk assessment." },
        { type:"pattern", label:"Abuse continued after system contact", text:"If the file shows the agency was involved and the abuse continued — especially if subsequent reports document escalation — that's a failure to protect after notice." },
        { type:"pattern", label:"Retaliation documented", text:"If the file shows that a child disclosed abuse and was then punished, moved, or returned to the abusive home, that's documented retaliation." },
        { type:"pattern", label:"Failed communication between agencies", text:"If police, schools, and CPS all had separate pieces of the picture and never connected them, that failure is often documented across the different record sets. Cross-referencing your agency, police, and court records can reveal this." },
        { type:"intro", text:"If you see any of these patterns, consult an attorney who specializes in child welfare failure or childhood sexual abuse cases. Many offer free consultations. Check your state's statute of limitations (filing deadline) — use the legal deadlines checker in the Records section of this app." },
      ]
    },
    {
      id: "with_therapist",
      title: "Using your records with a therapist",
      content: [
        { type:"intro", text:"Some therapists are experienced at working through case records with clients. This can be a powerful therapeutic tool — but it works best with intention." },
        { type:"tip", text:"Tell your therapist you have your records before you bring them in. Let them prepare too. Not all therapists have experience with child welfare records — if yours doesn't, that's okay, but consider asking for a referral to one who does." },
        { type:"tip", text:"Some people read their records alone first and then process what they found in therapy. Others want their therapist to read them first and guide the discussion. Others read them together. There's no right order — discuss what feels right for you." },
        { type:"tip", text:"Your therapist can help you understand the difference between what the records say and what actually happened. Agency language can minimize, distort, or medicalize your experience. A therapist can help you hold both versions." },
        { type:"tip", text:"Some trauma therapists use specific techniques for processing records — EMDR, CPT, or narrative therapy can all be useful here. If your current therapist doesn't use these modalities, they may be able to refer you." },
        { type:"tip", text:"Pace yourselves. You don't need to go through the entire file in one session. Some people spend months working through their records in therapy. That's not slow — that's thorough." },
        { type:"tip", text:"If you don't have a therapist and want to find one experienced with this, search Psychology Today's directory and filter for 'childhood trauma,' 'abuse,' or 'PTSD.' RAINN (1-800-656-4673) can also provide referrals." },
      ]
    },
    {
      id: "with_attorney",
      title: "Sharing your records with an attorney",
      content: [
        { type:"intro", text:"If you think your records show a pattern of system failure, here's how to approach a legal consultation." },
        { type:"tip", text:"Before you call, use the legal deadlines checker in this app to see if you're within your state's filing deadline. If a lookback window is open or closing soon, mention that — urgency matters." },
        { type:"tip", text:"Look for attorneys who specialize in child sexual abuse, institutional abuse, or child welfare failure — not general personal injury. Many handle these cases on contingency (no fee unless you win). Search '[your state] child sexual abuse attorney' or contact RAINN for referrals." },
        { type:"tip", text:"When you call, you don't need to tell your whole story. You can say: 'I have my child welfare records. They show [number] contacts with CPS over [number] years with no protective action. I'd like to know if I have a case.'" },
        { type:"tip", text:"What attorneys look for: number of agency contacts, documented findings vs. actions taken, whether the agency followed its own policies, whether risk assessments were overridden, whether known dangers were ignored. Highlight these in your file before the meeting." },
        { type:"tip", text:"Bring everything — agency records, police records, court records. The more complete the picture, the better they can evaluate. If you used the timeline builder in this app, bring that too." },
        { type:"tip", text:"You own your records. An attorney reviewing them doesn't change that. You can consult multiple attorneys. Get a second opinion. You're not committed to anything by sharing your file." },
        { type:"tip", text:"If you're also part of a class action or bankruptcy claim (like the BSA settlement), mention that. It may affect your options for individual claims." },
      ]
    },
    {
      id: "organizing",
      title: "How to organize what you've collected",
      content: [
        { type:"intro", text:"If you've requested records from multiple sources — child welfare, police, courts — you now have pieces of a puzzle. Here's how to put them together." },
        { type:"tip", text:"Create a master timeline. The timeline builder in this app is designed for this. Enter every event from every record source with dates. The pattern will become visible — when reports were made, how long investigations took, how many times the same thing was documented." },
        { type:"tip", text:"Keep originals safe. Make copies of everything. Store originals in a fireproof safe, a safety deposit box, or with a trusted person. These documents are irreplaceable." },
        { type:"tip", text:"Scan everything to PDF if you can. Digital copies can't be water-damaged, lost in a move, or destroyed. Store them in a password-protected folder — Google Drive, Dropbox, or a USB drive you keep in a safe place." },
        { type:"tip", text:"Cross-reference across sources. A police report from March 2004 should have a corresponding CPS entry if a referral was made. If the police report exists but the CPS file has nothing from that date, that's a gap worth investigating." },
        { type:"tip", text:"Write your own annotations. Add sticky notes, margin notes, or a separate document with your corrections and context. The caseworker wrote the official version — you're writing the real one." },
        { type:"tip", text:"You don't have to do this all at once, or alone. Some people work through their records over months or years. Some do it with a therapist, a lawyer, a sibling, or a trusted friend. Some do it alone. All of these are valid." },
      ]
    },
  ];

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
      <h1 style={sectionTitle}>What to Do with Your Records</h1>
      <LegalDisclaimer />
      <p style={{ ...bodyText, fontSize:14, color:"#6B7280", marginBottom:24, lineHeight:1.7 }}>
        You got your records. Maybe a thick envelope, maybe a link to a portal, maybe a USB drive. Now what?
      </p>

      <div style={{ padding:14, borderRadius:10, backgroundColor:"#EFF6FF", border:"1px solid #BFDBFE", marginBottom:20, fontSize:14, color:"#1E40AF", lineHeight:1.7 }}>
        Your records are a tool. They document what the system saw, decided, and did. They can confirm what you remember, reveal what you didn't know, and — if the system failed you — provide evidence.
      </div>

      {sections.map(s => (
        <div key={s.id} style={{ marginBottom:8 }}>
          <button onClick={() => toggle(s.id)} style={{
            width:"100%", padding:"14px 16px", borderRadius: openSection===s.id ? "10px 10px 0 0" : 10,
            border:"1px solid #E5E7EB", borderBottom: openSection===s.id ? "none" : "1px solid #E5E7EB",
            backgroundColor: openSection===s.id ? "#FAFAF9" : "#fff",
            cursor:"pointer", textAlign:"left", display:"flex", justifyContent:"space-between", alignItems:"center",
          }}>
            <span style={{ fontFamily:fonts, fontSize:14, fontWeight:600, color:"#1a1a1a" }}>{s.title}</span>
            <span style={{ fontSize:16, color:"#9CA3AF", transform: openSection===s.id ? "rotate(180deg)" : "none", transition:"transform 0.2s" }}>▾</span>
          </button>
          {openSection===s.id && (
            <div style={{ padding:"16px", borderRadius:"0 0 10px 10px", border:"1px solid #E5E7EB", borderTop:"none", backgroundColor:"#FAFAF9" }}>
              {s.content.map((item, i) => {
                if (item.type === "intro") return (
                  <p key={i} style={{ fontSize:13, color:"#374151", lineHeight:1.8, margin: i===0 ? "0 0 12px" : "12px 0" }}>{item.text}</p>
                );
                if (item.type === "term") return (
                  <div key={i} style={{ marginBottom:10, padding:"10px 12px", borderRadius:8, backgroundColor:"#fff", border:"1px solid #E5E7EB" }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#1a1a1a", marginBottom:3 }}>{item.term}</div>
                    <div style={{ fontSize:12, color:"#6B7280", lineHeight:1.6 }}>{item.def}</div>
                  </div>
                );
                if (item.type === "gap") return (
                  <div key={i} style={{ marginBottom:10, padding:"10px 12px", borderRadius:8, backgroundColor:"#FFFBEB", border:"1px solid #FDE68A" }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#92400E", marginBottom:3 }}>{item.label}</div>
                    <div style={{ fontSize:12, color:"#92400E", lineHeight:1.6 }}>{item.text}</div>
                  </div>
                );
                if (item.type === "pattern") return (
                  <div key={i} style={{ marginBottom:10, padding:"10px 12px", borderRadius:8, backgroundColor:"#FEF2F2", border:"1px solid #FECACA" }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#991B1B", marginBottom:3 }}>{item.label}</div>
                    <div style={{ fontSize:12, color:"#991B1B", lineHeight:1.6 }}>{item.text}</div>
                  </div>
                );
                if (item.type === "tip") return (
                  <p key={i} style={{ fontSize:13, color:"#374151", lineHeight:1.8, margin:"0 0 10px", paddingLeft:12, borderLeft:"3px solid #E5E7EB" }}>{item.text}</p>
                );
                return null;
              })}
            </div>
          )}
        </div>
      ))}

      <div style={{ ...cardSt, backgroundColor:"#F0FDF4", borderColor:"#A7F3D0", marginTop:24 }}>
        <div style={{ fontFamily:fonts, fontSize:15, fontWeight:700, color:"#065F46", marginBottom:8 }}>Want help understanding your specific file?</div>
        <p style={{ fontSize:13, color:"#065F46", lineHeight:1.7, margin:"0 0 12px" }}>
          Everything above is a general guide. But your records are specific — full of acronyms, codes, and decisions that are hard to decode on your own. Our AI tool can read your actual file and explain it in plain language.
        </p>
        <div style={{ fontSize:12, color:"#065F46", lineHeight:1.7, marginBottom:12 }}>
          <p style={{margin:"0 0 4px"}}><strong>Upload your records</strong> and choose what you need:</p>
          <p style={{margin:"0 0 4px"}}>• <strong>Plain language summary</strong> — what's in your file, translated from agency jargon</p>
          <p style={{margin:"0 0 4px"}}>• <strong>Timeline extraction</strong> — every dated event pulled into chronological order</p>
          <p style={{margin:"0 0 4px"}}>• <strong>Gap detection</strong> — find missing records, quick closures, contradictions</p>
          <p style={{margin:"0 0 4px"}}>• <strong>Legal pattern analysis</strong> — identify patterns that attorneys look for</p>
          <p style={{margin:0}}>• <strong>Full analysis</strong> — all of the above in one report</p>
        </div>
        <p style={{ fontSize:11, color:"#059669", lineHeight:1.5, margin:"0 0 12px" }}>
          Your documents go directly from your browser to the AI. Nothing passes through our servers. Nothing is stored. Most analyses cost under $1 with your own API key.
        </p>
        <button onClick={() => setPage("analyze")} style={{
          width:"100%", padding:"12px", borderRadius:8, border:"none", backgroundColor:"#065F46",
          color:"#fff", fontFamily:fonts, fontSize:14, fontWeight:600, cursor:"pointer",
        }}>
          Analyze My Records →
        </button>
      </div>

      <div style={{ marginTop:12 }}>
        <button onClick={() => setPage("records")} style={{
          width:"100%", padding:"14px", borderRadius:10, border:"none", backgroundColor:"#1a1a1a",
          color:"#fff", fontFamily:fonts, fontSize:15, fontWeight:600, cursor:"pointer", marginBottom:10,
        }}>
          Back to Records →
        </button>
        <button onClick={() => setPage("timeline")} style={{
          width:"100%", padding:"12px", borderRadius:10, border:"1px solid #E5E7EB", backgroundColor:"#fff",
          color:"#374151", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer",
        }}>
          Build your timeline with what you've found →
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   GET YOUR ID — Documents Guide
   ═══════════════════════════════════════════ */

function DocumentsGuidePage({ setPage }) {
  const [openSection, setOpenSection] = useState(null);
  const toggle = (id) => setOpenSection(p => p === id ? null : id);

  const docs = [
    {
      id: "birth_cert",
      title: "Birth certificate — get this first",
      content: [
        { type:"intro", text:"This is document #1. You need it to get everything else. If you don't have it, start here." },
        { type:"tip", text:"Request from the vital records office in the state where you were born — not where you live now. Search \"[state] vital records birth certificate\" to find the office." },
        { type:"tip", text:"Cost is usually $10-30. BUT if you were in foster care, many states waive the fee entirely. Ask when you call. Mention you were a former foster youth — some states have specific programs." },
        { type:"tip", text:"You'll need to know: your full name at birth, date of birth, city/county of birth, and your parents' names (if known). If you don't know your parents' names, explain that you were in foster care — they can often search by your name and DOB alone." },
        { type:"tip", text:"If you were adopted, your original birth certificate may be sealed. Many states now allow adult adoptees to access their original birth certificate — search \"[state] adoptee birth certificate access.\" Your amended birth certificate (with adoptive parents' names) is available through normal channels." },
        { type:"tip", text:"Processing takes 2-8 weeks by mail. Many states offer expedited processing for an extra fee. Some vital records offices have walk-in service for same-day or next-day certificates." },
        { type:"tip", text:"Federal law (Chafee Foster Care Program) requires states to help foster youth obtain birth certificates. If you aged out of care and the state didn't give you one, contact your state's independent living program — they may be required to help you get it for free." },
      ]
    },
    {
      id: "social_security",
      title: "Social Security card",
      content: [
        { type:"intro", text:"You need this for employment, benefits, and many records requests. Replacing it is free." },
        { type:"tip", text:"Replacement Social Security cards are FREE — always. Apply online at ssa.gov/myaccount or visit your local Social Security office. You can get up to 3 replacement cards per year and 10 in a lifetime." },
        { type:"tip", text:"To apply you'll need: a birth certificate OR a valid state ID/driver's license. If you have neither, go to the SSA office in person and explain your situation — they have procedures for people without documentation." },
        { type:"tip", text:"If you don't know your Social Security number at all, SSA can look it up with your name and date of birth. Bring any identification you have — even an expired ID, a school record, or a medical card." },
        { type:"tip", text:"If someone has been using your Social Security number (identity theft is common for foster youth — sometimes foster parents or group homes misuse children's SSNs), report it to SSA and request a review. The Foster Care Independence Act requires states to help with credit checks for youth leaving care." },
      ]
    },
    {
      id: "state_id",
      title: "State ID or driver's license",
      content: [
        { type:"intro", text:"You need a photo ID to request most records, open a bank account, get housing, and function in daily life." },
        { type:"tip", text:"Go to your state's DMV/RMV with your birth certificate and Social Security card. Some states accept other documents — check your state DMV website for acceptable ID combinations." },
        { type:"tip", text:"Many states waive the ID fee for former foster youth. Ask about fee waivers when you go. Some states (CA, TX, NY, IL, and others) have specific programs that provide free state IDs to youth aging out of care." },
        { type:"tip", text:"If you don't have a birth certificate yet, some states accept other proof of identity for a state ID — such as a passport, school records, medical records, or a letter from a social worker. Call your DMV before going to ask what alternatives they accept." },
        { type:"tip", text:"If you're experiencing homelessness, some DMVs accept shelter addresses or allow you to use a P.O. Box. Homeless service providers can often help you navigate the DMV process." },
        { type:"tip", text:"REAL ID requirements (needed for flying and federal buildings after 2025) require additional documents — typically a birth certificate, SSN, and two proofs of address. If you can't meet REAL ID requirements, a standard state ID still works for records requests." },
      ]
    },
    {
      id: "no_address",
      title: "I don't have a stable address",
      content: [
        { type:"intro", text:"You need an address to receive mail — including records that agencies send back to you. Here are options if you don't have a permanent address." },
        { type:"tip", text:"USPS General Delivery — free. Any post office will hold mail for you for up to 30 days. Address it to: [Your Name], General Delivery, [City], [State] [ZIP]. Pick it up at the counter with your ID. No cost, no application." },
        { type:"tip", text:"P.O. Box — costs $20-60 for 6 months depending on location. Apply at your local post office with your ID. This gives you a permanent mailing address that doesn't change when you move." },
        { type:"tip", text:"Shelter or transitional housing address — if you're staying at a shelter, most will let you receive mail there. Ask the front desk about their mail policy." },
        { type:"tip", text:"A trusted friend or family member's address — with their permission, you can use their address for mail. Just make sure it's someone stable who will hold your mail for you." },
        { type:"tip", text:"Some legal aid offices and social service agencies will receive mail on behalf of clients. Ask if they offer this service." },
        { type:"tip", text:"When sending records requests, you can also include an email address and phone number as alternative contact methods. Some agencies (especially Texas DFPS) deliver records electronically through an online portal — no mailing address needed." },
      ]
    },
    {
      id: "credit_freeze",
      title: "Check your credit — identity theft is common",
      content: [
        { type:"intro", text:"Foster youth are 3x more likely to be victims of identity theft than the general population. Foster parents, group home staff, and even biological parents sometimes open accounts, take out loans, or file taxes using a foster child's Social Security number. You may have debt you don't know about." },
        { type:"tip", text:"Get your free credit report at annualcreditreport.com — this is the official site, not a scam. You can check all three bureaus (Equifax, Experian, TransUnion) for free once per year. If you find accounts you didn't open, that's identity theft." },
        { type:"tip", text:"Federal law (Preventing Sex Trafficking and Strengthening Families Act of 2014) requires child welfare agencies to pull credit reports for foster youth at age 14 and annually after that, and to help resolve any fraud found. If your agency never did this, that's a failure you can document." },
        { type:"tip", text:"If you find identity theft: file a report at identitytheft.gov (FTC), place a fraud alert with all three credit bureaus (one call does all three), and file a police report. All of this is free." },
        { type:"tip", text:"You can freeze your credit for free to prevent future misuse. This doesn't affect your ability to use existing accounts — it just prevents new accounts from being opened in your name." },
        { type:"tip", text:"If the identity theft was committed by a foster parent or group home staff, that's a criminal act. Report it. It's also evidence of institutional failure if the agency didn't catch it during required credit checks." },
      ]
    },
    {
      id: "other_docs",
      title: "Other documents you might need",
      content: [
        { type:"intro", text:"Depending on your situation, you may also need these. All are obtainable." },
        { type:"term", term:"Medical records", def:"Contact each doctor, hospital, and clinic directly. Under HIPAA, you have an absolute right to your own medical records. They must provide copies within 30 days. They can charge a reasonable copying fee but many waive it if you ask." },
        { type:"term", term:"School transcripts", def:"Contact each school district where you were enrolled. Under FERPA, you have a right to your own educational records after age 18. If you moved schools frequently (common in foster care), you may need to contact multiple districts." },
        { type:"term", term:"Immunization records", def:"Your state health department maintains immunization records. Search \"[state] immunization registry\" — many have online portals. Your child welfare agency file may also contain copies." },
        { type:"term", term:"Passport", def:"Requires a birth certificate, photo ID, passport photo, and $130. Apply at a post office or passport agency. If you're in a hurry, expedited processing is available for an additional fee. Some nonprofits help foster youth get passports for free." },
        { type:"term", term:"Court records from your case", def:"Use the Court Records guide in this app. Your juvenile court records may contain placement orders, custody decisions, and other documents that prove your history in the system." },
        { type:"term", term:"Proof you were in foster care", def:"Some benefits (tuition waivers, Medicaid extensions, housing programs) require proof you were in the system. Request a letter from your state child welfare agency confirming your dates in care. Some states provide a \"verification of foster care\" letter on request." },
      ]
    },
    {
      id: "free_help",
      title: "Free help getting your documents",
      content: [
        { type:"intro", text:"You shouldn't have to do all of this alone. These organizations help foster youth get their documents." },
        { type:"tip", text:"Your state's Independent Living Program (ILP) / Chafee program — every state has one. They're federally funded to help foster youth ages 14-23 (sometimes up to 26) with life skills, documents, housing, and education. Search \"[your state] independent living program foster youth.\"" },
        { type:"tip", text:"FosterClub (fosterclub.com) — national nonprofit with resources and peer connections for current and former foster youth. They have state-by-state guides to benefits and services." },
        { type:"tip", text:"Legal aid organizations — many provide free help with document recovery, name changes, identity theft, and benefits applications. Search \"[your state] legal aid foster youth\" or call 211." },
        { type:"tip", text:"211 — call or text 211 from any phone. It's a free national helpline that connects you to local services including document assistance, housing, food, and more." },
        { type:"tip", text:"If you aged out of foster care, you may be eligible for extended Medicaid (up to age 26 in most states), education and training vouchers (ETV — up to $5,000/year for college), and housing assistance. These benefits exist because of federal law — they're not charity, they're your right." },
      ]
    },
  ];

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
      <h1 style={sectionTitle}>Get Your ID & Documents</h1>
      <p style={{ ...bodyText, fontSize:14, color:"#6B7280", marginBottom:20, lineHeight:1.7 }}>
        To request your records, you usually need a photo ID. If you aged out of foster care without your documents — or if you never had them — here's how to get them. Everything on this page is free or low-cost, and many fees are waived for former foster youth.
      </p>

      <div style={{ padding:14, borderRadius:10, backgroundColor:"#EFF6FF", border:"1px solid #BFDBFE", marginBottom:20, fontSize:13, color:"#1E40AF", lineHeight:1.7 }}>
        <strong>The order matters.</strong> Get your birth certificate first → then Social Security card → then state ID. Each one requires the previous one.
      </div>

      {docs.map(s => (
        <div key={s.id} style={{ marginBottom:8 }}>
          <button onClick={() => toggle(s.id)} style={{
            width:"100%", padding:"14px 16px", borderRadius: openSection===s.id ? "10px 10px 0 0" : 10,
            border:"1px solid #E5E7EB", borderBottom: openSection===s.id ? "none" : "1px solid #E5E7EB",
            backgroundColor: openSection===s.id ? "#FAFAF9" : "#fff",
            cursor:"pointer", textAlign:"left", display:"flex", justifyContent:"space-between", alignItems:"center",
          }}>
            <span style={{ fontFamily:fonts, fontSize:14, fontWeight:600, color:"#1a1a1a" }}>{s.title}</span>
            <span style={{ fontSize:16, color:"#9CA3AF", transform: openSection===s.id ? "rotate(180deg)" : "none", transition:"transform 0.2s" }}>▾</span>
          </button>
          {openSection===s.id && (
            <div style={{ padding:"16px", borderRadius:"0 0 10px 10px", border:"1px solid #E5E7EB", borderTop:"none", backgroundColor:"#FAFAF9" }}>
              {s.content.map((item, i) => {
                if (item.type === "intro") return (
                  <p key={i} style={{ fontSize:13, color:"#374151", lineHeight:1.8, margin: i===0 ? "0 0 12px" : "12px 0" }}>{item.text}</p>
                );
                if (item.type === "term") return (
                  <div key={i} style={{ marginBottom:10, padding:"10px 12px", borderRadius:8, backgroundColor:"#fff", border:"1px solid #E5E7EB" }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#1a1a1a", marginBottom:3 }}>{item.term}</div>
                    <div style={{ fontSize:12, color:"#6B7280", lineHeight:1.6 }}>{item.def}</div>
                  </div>
                );
                if (item.type === "tip") return (
                  <p key={i} style={{ fontSize:13, color:"#374151", lineHeight:1.8, margin:"0 0 10px", paddingLeft:12, borderLeft:"3px solid #E5E7EB" }}>{item.text}</p>
                );
                return null;
              })}
            </div>
          )}
        </div>
      ))}

      <div style={{ marginTop:24 }}>
        <button onClick={() => setPage("records")} style={{
          width:"100%", padding:"14px", borderRadius:10, border:"none", backgroundColor:"#1a1a1a",
          color:"#fff", fontFamily:fonts, fontSize:15, fontWeight:600, cursor:"pointer", marginBottom:10,
        }}>
          I have my ID — take me to Records →
        </button>
        <button onClick={() => setPage("case")} style={{
          width:"100%", padding:"12px", borderRadius:10, border:"1px solid #E5E7EB", backgroundColor:"#fff",
          color:"#374151", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer",
        }}>
          Build My Case — step by step →
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   TIMELINE PAGE
   ═══════════════════════════════════════════ */

function TimelinePage() {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date:"", category:"abuse", title:"", description:"", outcome:"", flagged:false });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await storage.get("gyr-timeline");
        if (r?.value) setEvents(JSON.parse(r.value));
      } catch(e) {}
      setLoaded(true);
    })();
  }, []);

  useEffect(() => {
    if (!loaded) return;
    (async () => { try { await storage.set("gyr-timeline", JSON.stringify(events)); } catch(e){} })();
  }, [events, loaded]);

  const addEvent = () => {
    if (!form.date || !form.title) return;
    setEvents(p => [...p, { ...form, id: genId() }]);
    setForm({ date:"", category:"abuse", title:"", description:"", outcome:"", flagged:false });
    setShowForm(false);
  };

  const sorted = [...events].sort((a,b) => new Date(a.date) - new Date(b.date));
  const systemCount = events.filter(e=>e.category==="system").length;
  const flaggedCount = events.filter(e=>e.flagged).length;

  const exportTimeline = () => {
    const lines = sorted.map(ev => {
      const cat = CATEGORIES[ev.category];
      return `${ev.date}  |  ${cat.label}${ev.flagged?" [FLAGGED]":""}\n${ev.title}${ev.description?"\n"+ev.description:""}${ev.outcome?"\nOutcome: "+ev.outcome:""}\n`;
    });
    const header = `TIMELINE — ${events.length} events · ${systemCount} system contacts · ${flaggedCount} flagged\nExported from getmyrecords.org\n${"=".repeat(60)}\n\n`;
    const text = header + lines.join("\n" + "-".repeat(40) + "\n\n");
    const b = new Blob([text], {type:"text/plain"});
    const u = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = u;
    a.download = "my-timeline.txt";
    a.click();
    URL.revokeObjectURL(u);
  };

  const exportTimelinePDF = () => {
    const lines = sorted.map(ev => {
      const cat = CATEGORIES[ev.category];
      return `<div style="margin-bottom:16px;padding:12px;border-left:3px solid ${cat.color};background:#fafafa;">
        <div style="font-size:11px;color:${cat.color};font-weight:bold;text-transform:uppercase;">${ev.flagged?"⚑ ":""}${cat.label}</div>
        <div style="font-size:11px;color:#999;margin-bottom:4px;">${ev.date}</div>
        <div style="font-size:14px;font-weight:600;color:#1a1a1a;">${ev.title}</div>
        ${ev.description?`<div style="font-size:12px;color:#666;margin-top:4px;">${ev.description}</div>`:""}
        ${ev.outcome?`<div style="font-size:11px;color:#999;margin-top:4px;padding:4px 8px;background:#f0f0f0;border-radius:4px;">Outcome: ${ev.outcome}</div>`:""}
      </div>`;
    });
    const html = `<html><head><title>My Timeline</title><style>body{font-family:system-ui,sans-serif;max-width:640px;margin:40px auto;padding:0 20px;}h1{font-size:22px;}p{color:#666;font-size:13px;}.stats{font-size:12px;color:#999;margin-bottom:24px;}</style></head><body>
      <h1>My Timeline</h1>
      <p>Exported from getmyrecords.org</p>
      <div class="stats">${events.length} events · ${systemCount} system contacts · ${flaggedCount} flagged</div>
      ${lines.join("")}
      <div style="margin-top:24px;font-size:10px;color:#ccc;">This document was generated for personal use. It is not an official record.</div>
    </body></html>`;
    const b = new Blob([html], {type:"text/html"});
    const u = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = u;
    a.download = "my-timeline.html";
    a.click();
    URL.revokeObjectURL(u);
  };

  const deleteEvent = (id) => {
    setEvents(p => p.filter(e => e.id !== id));
  };

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"24px 20px 40px" }}>
      <h1 style={sectionTitle}>Your Timeline</h1>
      <p style={{ fontSize:13, color:"#6B7280", lineHeight:1.7, marginBottom:16 }}>
        Build a chronological record of everything that happened — abuse, system contacts, placements, legal actions. When you're done, export it as a document you can bring to an attorney, a therapist, or keep for yourself.
      </p>

      <div style={{ display:"flex", gap:8, marginBottom:16 }}>
        <button onClick={() => setShowForm(!showForm)} style={{
          flex:1, padding:"12px", borderRadius:8, border:"none", backgroundColor:"#1a1a1a",
          color:"#fff", fontFamily:fonts, fontSize:14, fontWeight:600, cursor:"pointer",
        }}>
          + Add Event
        </button>
        {events.length > 0 && (
          <button onClick={exportTimeline} style={{
            padding:"12px 14px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff",
            color:"#374151", fontFamily:fonts, fontSize:13, fontWeight:500, cursor:"pointer",
          }}>
            Export
          </button>
        )}
      </div>

      {showForm && (
        <div style={{ ...cardSt, marginBottom:20 }}>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:14 }}>
            {Object.entries(CATEGORIES).map(([k,c]) => (
              <button key={k} onClick={() => setForm(p=>({...p,category:k}))} style={{
                padding:"5px 10px", borderRadius:16, border:`1.5px solid ${form.category===k?c.color:"#E5E7EB"}`,
                backgroundColor: form.category===k?c.bg:"#fff", color: form.category===k?c.color:"#6B7280",
                fontFamily:fonts, fontSize:11, fontWeight:600, cursor:"pointer",
              }}>
                {c.icon} {c.label}
              </button>
            ))}
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={labelSt}>Date</label>
            <input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))} style={inputSt} />
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={labelSt}>What happened?</label>
            <input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} placeholder="Brief description" style={inputSt} />
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={labelSt}>Details (optional)</label>
            <textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder="Names, locations, what was said..." rows={2} style={{...inputSt, resize:"vertical"}} />
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={labelSt}>Outcome (optional)</label>
            <input value={form.outcome} onChange={e=>setForm(p=>({...p,outcome:e.target.value}))} placeholder="Case closed, charges dropped..." style={inputSt} />
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={addEvent} disabled={!form.date||!form.title} style={{
              flex:1, padding:"11px", borderRadius:8, border:"none",
              backgroundColor: !form.date||!form.title?"#D1D5DB":"#1a1a1a", color:"#fff",
              fontFamily:fonts, fontSize:14, fontWeight:600, cursor: !form.date||!form.title?"default":"pointer",
            }}>
              Add to Timeline
            </button>
            <button onClick={() => setForm(p=>({...p,flagged:!p.flagged}))} style={{
              padding:"11px 14px", borderRadius:8, border:`1.5px solid ${form.flagged?"#DC2626":"#D1D5DB"}`,
              backgroundColor: form.flagged?"#FEF2F2":"#fff", color: form.flagged?"#DC2626":"#9CA3AF",
              cursor:"pointer", fontSize:16,
            }} title="Flag as critical">⚑</button>
          </div>
        </div>
      )}

      {events.length === 0 && !showForm && (
        <div style={{ textAlign:"center", padding:"30px 20px", color:"#9CA3AF" }}>
          <div style={{ fontFamily:serifFonts, fontSize:18, fontWeight:600, color:"#6B7280", marginBottom:10 }}>Start with what you remember</div>
          <p style={{ fontSize:13, lineHeight:1.7, maxWidth:400, margin:"0 auto 16px" }}>
            You don't need exact dates. Start with one event — the thing you remember most clearly — and build from there.
          </p>
          <div style={{ ...cardSt, textAlign:"left", maxWidth:400, margin:"0 auto" }}>
            <div style={{ fontFamily:fonts, fontSize:12, fontWeight:700, color:"#1a1a1a", marginBottom:6 }}>Why build a timeline?</div>
            <div style={{ fontSize:12, color:"#6B7280", lineHeight:1.7 }}>
              <p style={{margin:"0 0 4px"}}>• Attorneys need a chronological summary to evaluate your case</p>
              <p style={{margin:"0 0 4px"}}>• Therapists use timelines to structure trauma processing</p>
              <p style={{margin:"0 0 4px"}}>• Cross-referencing with your records reveals gaps and patterns</p>
              <p style={{margin:0}}>• Exporting it gives you a document you own and control</p>
            </div>
          </div>
        </div>
      )}

      {sorted.map((ev) => {
        const cat = CATEGORIES[ev.category];
        return (
          <div key={ev.id} style={{ display:"flex", gap:12, marginBottom:12 }}>
            <div style={{ width:28, display:"flex", flexDirection:"column", alignItems:"center", paddingTop:14 }}>
              <div style={{ width:10, height:10, borderRadius:"50%", backgroundColor:cat.color, border: ev.flagged?"2px solid #DC2626":"2px solid #FAFAF9" }} />
            </div>
            <div style={{
              flex:1, ...cardSt, marginBottom:0,
              borderLeft:`3px solid ${cat.color}`,
              border: ev.flagged ? `1px solid #FECACA` : `1px solid #E5E7EB`,
              borderLeftWidth: 3, borderLeftColor: cat.color, borderLeftStyle: "solid",
            }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                <div style={{ fontSize:11, fontWeight:600, color:cat.color, textTransform:"uppercase", letterSpacing:"0.04em" }}>
                  {ev.flagged && "⚑ "}{cat.label}
                </div>
                <div style={{ fontSize:11, color:"#9CA3AF" }}>{fmtDate(ev.date)}</div>
              </div>
              <div style={{ fontFamily:serifFonts, fontSize:14, fontWeight:600, color:"#1a1a1a", lineHeight:1.4, marginBottom: ev.description||ev.outcome?6:0 }}>
                {ev.title}
              </div>
              {ev.description && <div style={{ fontSize:12, color:"#6B7280", lineHeight:1.6, marginBottom: ev.outcome?6:0 }}>{ev.description}</div>}
              {ev.outcome && (
                <div style={{ fontSize:11, color:"#9CA3AF", padding:"5px 8px", backgroundColor:"#F9FAFB", borderRadius:6 }}>
                  <strong>Outcome:</strong> {ev.outcome}
                </div>
              )}
              <button onClick={()=>deleteEvent(ev.id)} style={{ marginTop:6, padding:"4px 8px", border:"none", backgroundColor:"transparent", color:"#D1D5DB", fontFamily:fonts, fontSize:10, cursor:"pointer", float:"right" }}>remove</button>
            </div>
          </div>
        );
      })}

      {events.length > 0 && (
        <div style={{ marginTop:20 }}>
          <div style={{ fontSize:12, color:"#9CA3AF", textAlign:"center", marginBottom:16 }}>
            {events.length} event{events.length!==1?"s":""} · {systemCount} system contacts · {flaggedCount} flagged
          </div>
          <div style={{ ...cardSt, backgroundColor:"#EFF6FF", borderColor:"#BFDBFE" }}>
            <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1E40AF", marginBottom:8 }}>Export your timeline</div>
            <p style={{ fontSize:12, color:"#1E40AF", lineHeight:1.6, margin:"0 0 12px" }}>
              Download your timeline as a document you can print, email to an attorney, or bring to a therapist. Your data stays on your device — exporting creates a file only you have.
            </p>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={exportTimeline} style={{ flex:1, padding:"10px", borderRadius:8, border:"1px solid #BFDBFE", backgroundColor:"#fff", color:"#1E40AF", fontFamily:fonts, fontSize:12, fontWeight:600, cursor:"pointer" }}>
                Download as Text
              </button>
              <button onClick={exportTimelinePDF} style={{ flex:1, padding:"10px", borderRadius:8, border:"1px solid #BFDBFE", backgroundColor:"#fff", color:"#1E40AF", fontFamily:fonts, fontSize:12, fontWeight:600, cursor:"pointer" }}>
                Download as HTML
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   CONTRIBUTE — Community Knowledge
   ═══════════════════════════════════════════ */

const CONTRIBUTE_TYPES = [
  { id: "records_experience", label: "I requested my records", desc: "Tell us what happened — which state, what the agency required, what worked, what didn't. This is the single most valuable thing you can contribute.", priority: true },
  { id: "sol_correction", label: "Legal info is wrong", desc: "A statute of limitations, lookback window, or legal deadline on the site is incorrect or outdated. Tell us the state and what needs to change." },
  { id: "agency_correction", label: "Agency info is wrong", desc: "An agency name, address, phone number, or required form has changed." },
  { id: "optout_broken", label: "Data broker link is broken", desc: "An opt-out URL on the Hide Yourself page doesn't work or has moved." },
  { id: "attorney_advocate", label: "I'm an attorney or advocate", desc: "I can review a state's legal data, letter template, or records procedures. I want to volunteer expertise.", priority: true },
  { id: "translate", label: "I can help translate", desc: "I speak a language other than English and want to help translate the site or review an existing translation." },
  { id: "new_resource", label: "There's a resource you're missing", desc: "A court records search tool, a state program, an advocacy org, or a guide that should be on the site." },
  { id: "other", label: "Something else", desc: "Anything that doesn't fit the categories above." },
];

function ContributePage({ setPage }) {
  const [contType, setContType] = useState("");
  const [state, setState] = useState("");
  const [details, setDetails] = useState("");
  const [source, setSource] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!contType || !details || sending) return;
    setSending(true);
    setError("");
    const label = CONTRIBUTE_TYPES.find(t => t.id === contType)?.label || contType;
    try {
      const res = await fetch("https://formspree.io/f/mvzdzvjn", {
        method: "POST",
        headers: { "Accept": "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          contribution_type: label,
          state: state || "(not state-specific)",
          details,
          source_or_citation: source,
          email: email || "(no contact provided)",
          _subject: `getmyrecords contribution: ${label}${state ? ` — ${state}` : ""}`,
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSent(true);
    } catch (e) {
      setError("Couldn't send. Check your connection and try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
        <h1 style={sectionTitle}>Thank you</h1>
        <div style={{ padding:20, borderRadius:10, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", fontSize:14, color:"#065F46", lineHeight:1.7, marginBottom:20, textAlign:"center" }}>
          Your contribution has been received. We'll review it and update the site if it checks out. Every correction makes this safer for the next person.
        </div>
        <button onClick={()=>{setSent(false); setContType(""); setDetails(""); setSource(""); setState(""); setEmail("");}} style={{
          width:"100%", padding:"12px", borderRadius:10, border:"1px solid #D1D5DB", backgroundColor:"#fff",
          color:"#374151", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer", marginBottom:8,
        }}>Submit another</button>
        <button onClick={()=>setPage("home")} style={{
          width:"100%", padding:"12px", borderRadius:10, border:"none", backgroundColor:"transparent",
          color:"#9CA3AF", fontFamily:fonts, fontSize:12, cursor:"pointer",
        }}>← Back to home</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
      <h1 style={sectionTitle}>Help Build This</h1>
      <p style={{ fontSize:14, color:"#6B7280", lineHeight:1.7, marginBottom:16 }}>
        This site is maintained by a small team. We can't visit every state agency, test every letter, or catch every URL that breaks. But together, the internet can.
      </p>

      <div style={{ padding:14, borderRadius:10, backgroundColor:"#EFF6FF", border:"1px solid #BFDBFE", marginBottom:20, fontSize:13, color:"#1E40AF", lineHeight:1.7 }}>
        <strong>What we need most right now:</strong> people who have actually requested their records telling us what happened. Which state, what the agency required, whether our letter worked or got rejected, and what they had to change. Real-world reports from real people are worth more than any amount of desk research.
      </div>

      <div style={{ marginBottom:20 }}>
        <label style={labelSt}>What are you contributing?</label>
        {CONTRIBUTE_TYPES.map(t => (
          <button key={t.id} onClick={()=>setContType(t.id)} style={{
            width:"100%", padding:"12px 14px", borderRadius:8, textAlign:"left", cursor:"pointer", marginBottom:6,
            border: contType === t.id ? "1.5px solid #2563EB" : "1px solid #E5E7EB",
            backgroundColor: contType === t.id ? "#EFF6FF" : "#fff",
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
              <span style={{ fontSize:13, fontWeight:600, color: contType === t.id ? "#2563EB" : "#374151" }}>{t.label}</span>
              {t.priority && <span style={{ fontSize:9, fontWeight:700, padding:"2px 6px", borderRadius:4, backgroundColor:"#DC2626", color:"#fff", textTransform:"uppercase" }}>High need</span>}
            </div>
            <div style={{ fontSize:11, color:"#9CA3AF" }}>{t.desc}</div>
          </button>
        ))}
      </div>

      {contType && (<>
        {(contType === "sol_correction" || contType === "agency_correction" || contType === "records_experience") && (
          <div style={{ marginBottom:14 }}>
            <label style={labelSt}>Which state?</label>
            <select value={state} onChange={e=>setState(e.target.value)} style={{ ...inputSt, cursor:"pointer", appearance:"auto" }}>
              <option value="">Select a state</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}

        <div style={{ marginBottom:14 }}>
          <label style={labelSt}>Details</label>
          <textarea value={details} onChange={e=>setDetails(e.target.value)} rows={5} placeholder={
            contType === "records_experience" ? "What state? What did the agency require? Did our letter work? What did they ask for that we didn't include? How long did it take?"
            : contType === "sol_correction" ? "What's currently wrong on the site, and what should it say? Include a citation or bill number if you have one."
            : contType === "optout_broken" ? "Which site's link is broken? What happens when you click it? Do you know the new URL?"
            : contType === "attorney_advocate" ? "What states or topics can you review? What's your area of practice? How should we reach you?"
            : "Be as specific as you can."
          } style={{ ...inputSt, resize:"vertical" }} />
        </div>

        <div style={{ marginBottom:14 }}>
          <label style={labelSt}>Source or citation (optional)</label>
          <input value={source} onChange={e=>setSource(e.target.value)} placeholder="Link to state statute, agency page, bill number, or how you know this" style={inputSt} />
          <div style={{ fontSize:11, color:"#9CA3AF", marginTop:4 }}>Helps us verify faster. A URL, a bill number, or "I work at this agency" all count.</div>
        </div>

        <div style={{ marginBottom:20 }}>
          <label style={labelSt}>Your email (optional)</label>
          <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Only if you want us to follow up or credit you" style={inputSt} />
          <div style={{ fontSize:11, color:"#9CA3AF", marginTop:4 }}>We won't share it. Leave blank to contribute anonymously.</div>
        </div>

        {error && <div style={{ padding:10, borderRadius:8, backgroundColor:"#FEF2F2", border:"1px solid #FECACA", fontSize:12, color:"#991B1B", marginBottom:12 }}>{error}</div>}

        <button onClick={submit} disabled={!details || sending} style={{
          width:"100%", padding:"14px", borderRadius:10, border:"none",
          backgroundColor: details && !sending ? "#1a1a1a" : "#D1D5DB",
          color:"#fff", fontFamily:fonts, fontSize:15, fontWeight:600,
          cursor: details && !sending ? "pointer" : "default",
        }}>
          {sending ? "Sending…" : "Submit"}
        </button>
      </>)}

      <div style={{ marginTop:24, padding:14, borderRadius:10, backgroundColor:"#F8FAFC", border:"1px solid #E2E8F0", fontSize:12, color:"#475569", lineHeight:1.6 }}>
        <strong>For developers:</strong> The code and legal data for this site are open source. If you'd rather submit a pull request directly, the state data lives in <code style={{ backgroundColor:"#E5E7EB", padding:"1px 4px", borderRadius:3 }}>src/data/states.js</code> — each state is one readable stanza. The schema is documented at the top of the file.
      </div>

      <button onClick={()=>setPage("home")} style={{
        width:"100%", marginTop:16, padding:"12px", borderRadius:10, border:"1px solid #D1D5DB",
        backgroundColor:"#fff", color:"#374151", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer",
      }}>← Back to home</button>
    </div>
  );
}

/* ═══════════════════════════════════════════
   RECORDS PAGE — ALL 50 STATES
   ═══════════════════════════════════════════ */

const RECORD_TYPES = [
  { id:"cps_referrals", label:"CPS/Abuse Referrals & Reports", desc:"Every report/complaint received about your family", default:true },
  { id:"investigation_protocol", label:"Investigation Reports & Protocols", desc:"Assessment, findings, and outcome of each investigation", default:true },
  { id:"case_narrative", label:"Case Narratives / Running Notes", desc:"Daily notes — child description, living situation, placement history", default:true },
  { id:"sdm_tools", label:"Structured Decision-Making / Risk Assessments", desc:"Risk, safety, and reunification assessments used by caseworkers", default:true },
  { id:"family_plan", label:"Family Treatment / Service Plans", desc:"Goals and objectives for the family", default:true },
  { id:"individual_plan", label:"Individual Treatment Plans", desc:"Goals and objectives specific to the child", default:true },
  { id:"admin_review", label:"Administrative Case Reviews", desc:"Federally mandated periodic reviews", default:true },
  { id:"social_studies", label:"Social Studies / Home Studies", desc:"Home studies, living situation descriptions, birth family history", default:true },
  { id:"placement", label:"Placement Records", desc:"Foster homes, group homes, residential facilities — dates and reasons", default:true },
  { id:"court", label:"Court Reports & Petitions", desc:"Reports filed with the court by the agency", default:false },
  { id:"medical", label:"Medical Records", desc:"Multi-disciplinary evaluations, primary care records held by agency", default:false },
  { id:"psych", label:"Psychiatric / Psychological Reports", desc:"Evaluations, testing, residential treatment reports", default:false },
  { id:"education", label:"Educational Records / Reports", desc:"IEPs, school reports maintained by agency", default:false },
];

const STATE_ABBREVS = {
  "Alabama":"AL","Alaska":"AK","Arizona":"AZ","Arkansas":"AR","California":"CA","Colorado":"CO","Connecticut":"CT","Delaware":"DE","Florida":"FL","Georgia":"GA","Hawaii":"HI","Idaho":"ID","Illinois":"IL","Indiana":"IN","Iowa":"IA","Kansas":"KS","Kentucky":"KY","Louisiana":"LA","Maine":"ME","Maryland":"MD","Massachusetts":"MA","Michigan":"MI","Minnesota":"MN","Mississippi":"MS","Missouri":"MO","Montana":"MT","Nebraska":"NE","Nevada":"NV","New Hampshire":"NH","New Jersey":"NJ","New Mexico":"NM","New York":"NY","North Carolina":"NC","North Dakota":"ND","Ohio":"OH","Oklahoma":"OK","Oregon":"OR","Pennsylvania":"PA","Rhode Island":"RI","South Carolina":"SC","South Dakota":"SD","Tennessee":"TN","Texas":"TX","Utah":"UT","Vermont":"VT","Virginia":"VA","Washington":"WA","West Virginia":"WV","Wisconsin":"WI","Wyoming":"WY"
};

function RecordsPage({ setPage }) {
  const [mode, setMode] = useState("menu"); // menu, generator, letter, sol_info
  const [selState, setSelState] = useState("");
  const [recStep, setRecStep] = useState(0);
  const [rec, setRec] = useState({
    requestType:"", fullName:"", dob:"", previousNames:"", addresses:"",
    caseNumbers:"", dateRange:"", selectedRecords: RECORD_TYPES.filter(r=>r.default).map(r=>r.id),
    additionalNotes:"", relationship:"", childName:"",
  });
  const updR = (f,v) => setRec(p=>({...p,[f]:v}));
  const toggleRec = id => setRec(p=>({...p, selectedRecords: p.selectedRecords.includes(id)?p.selectedRecords.filter(r=>r!==id):[...p.selectedRecords,id]}));

  const today = new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"});

  const sd = selState ? STATES_DATA[STATE_ABBREVS[selState]] : null;

  // Compute the response-by date based on per-state rr.responseDays when present.
  // Default to 30 calendar days if the state doesn't have records-request data yet.
  const computeResponseDate = (s) => {
    const days = s?.rr?.responseDays ?? 30;
    const isBusiness = s?.rr?.responseDaysType === "business";
    let d = new Date();
    if (isBusiness) {
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day !== 0 && day !== 6) added++;
      }
    } else {
      d.setDate(d.getDate() + days);
    }
    return d.toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"});
  };
  const respDate = computeResponseDate(sd);

  const canProceedRec = () => {
    if (recStep===0) return rec.requestType;
    if (recStep===1) return rec.fullName && rec.dob;
    if (recStep===2) return rec.selectedRecords.length > 0;
    if (recStep===3) return true;
    return false;
  };

  const genLetter = () => {
    if (!sd) return "";
    const labels = rec.selectedRecords.map(id=>RECORD_TYPES.find(r=>r.id===id)?.label).filter(Boolean);
    const isOwn = rec.requestType==="own";
    const subj = isOwn ? rec.fullName : rec.childName;
    const addr = sd.ad || "";
    const agencyFull = sd.af || "";

    // Prefer the records-request authority statute when available; fall back to the
    // generic `st` field (which may be the SOL statute — not ideal, but better than nothing).
    const authority = sd.rr?.authority || sd.st || "";
    const responseDays = sd.rr?.responseDays;
    const responseDaysType = sd.rr?.responseDaysType;
    const responseLabel = responseDays
      ? `${responseDays} ${responseDaysType === "business" ? "business" : "calendar"} day${responseDays === 1 ? "" : "s"}`
      : "30 days";

    const responseLine = responseDays
      ? `Under ${authority || "the applicable public records law"}, please respond to this request within ${responseLabel} (by ${respDate}).`
      : `I respectfully request a response within 30 days (by ${respDate}).`;

    const thirdPartyClause = isOwn
      ? "As the subject of these records, I have the right to access my own child welfare records."
      : `I am requesting access as ${rec.relationship} of the subject. ${
          sd.rr?.thirdParty === "court_order"
            ? "I understand that this state generally requires a court order for third-party requests, and I am attaching/citing the appropriate authorization."
            : sd.rr?.thirdParty === "no"
              ? "I am providing the documentation required for non-subject access under state law."
              : "I am requesting access under the applicable provisions of state law."
        }`;

    return `${today}

${agencyFull}
${addr}

Re: Request for Case Records${authority ? " Pursuant to " + authority : ""}

Dear Records Custodian:

I am writing to request copies of records maintained by ${sd.a || agencyFull} pertaining to ${isOwn ? "myself" : `${rec.childName}, for whom I am ${rec.relationship}`}.

${thirdPartyClause}

${authority ? "Legal authority for this request: " + authority + ".\n\n" : ""}IDENTIFYING INFORMATION:
${isOwn ? "Full Name" : "Subject's Name"}: ${subj}
Date of Birth: ${rec.dob}${rec.previousNames ? "\nPrevious Names: " + rec.previousNames : ""}${rec.addresses ? "\nAddresses during agency involvement: " + rec.addresses : ""}${rec.caseNumbers ? "\nCase Numbers (if known): " + rec.caseNumbers : ""}${rec.dateRange ? "\nApproximate Dates of Involvement: " + rec.dateRange : ""}${!isOwn ? "\n\nRequestor: " + rec.fullName + "\nRelationship: " + rec.relationship : ""}

RECORDS REQUESTED:
I am requesting the following specific records individually, not solely a case summary or running narrative:

${labels.map((l, i) => (i + 1) + ". " + l).join("\n")}${rec.additionalNotes ? "\n\nADDITIONAL NOTES:\n" + rec.additionalNotes : ""}

${responseLine}

If any records are withheld or redacted, please provide a written explanation citing the specific statutory authority for each withholding.

If there are fees for copying, please notify me of the estimated cost before processing the request.

Please confirm receipt of this request. I can be reached at the contact information below.

Sincerely,

______________________________
${rec.fullName}

[YOUR ADDRESS — or P.O. Box, General Delivery, or shelter address]
[YOUR PHONE]
[YOUR EMAIL — include this so they can reach you electronically]`;
  };

  const [letterText, setLetterText] = useState("");
  const [letterEdited, setLetterEdited] = useState(false);
  const [copied, setCopied] = useState(false);

  const initLetter = () => { const t = genLetter(); setLetterText(t); setLetterEdited(false); };
  const copyLetter = () => { navigator.clipboard.writeText(letterText).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false), 2000); }).catch(()=>{}); };
  const downloadLetter = () => { const b=new Blob([letterText],{type:"text/plain"}); const u=URL.createObjectURL(b); const a=document.createElement("a"); a.href=u; a.download=`${sd?.a||"Agency"}-Records-Request.txt`; a.click(); URL.revokeObjectURL(u); };

  // === Police FOIA State ===
  const [polStep, setPolStep] = useState(0);
  const [pol, setPol] = useState({
    fullName:"", dob:"", deptName:"", deptAddress:"", deptCity:"", deptState:"",
    incidentDates:"", incidentAddresses:"", officerNames:"", caseNumbers:"",
    personsInvolved:"", additionalContext:"",
    selectedTypes: ["incident_reports","arrest_reports","call_logs"],
  });
  const updP = (f,v) => setPol(p=>({...p,[f]:v}));

  // === Follow-Up / Escalation State ===
  const [fuType, setFuType] = useState("no_response");
  const [fuDate, setFuDate] = useState("");
  const [fuDetails, setFuDetails] = useState("");

  // === Prior History Request State ===
  const [priorName, setPriorName] = useState("");
  const [priorAltNames, setPriorAltNames] = useState("");
  const [priorDOB, setPriorDOB] = useState("");
  const [priorRelationship, setPriorRelationship] = useState("");
  const [priorContext, setPriorContext] = useState("");

  const POLICE_RECORD_TYPES = [
    { id:"incident_reports", label:"Incident Reports / Police Reports", desc:"Written narrative of what officers found and documented at the scene" },
    { id:"arrest_reports", label:"Arrest Reports", desc:"Details of any arrests made in connection with the incidents" },
    { id:"call_logs", label:"Dispatch / Call Logs (CAD Records)", desc:"Records of every 911 call and dispatch to your address — even ones that didn't result in a report" },
    { id:"supplemental", label:"Supplemental Reports", desc:"Follow-up reports filed after the initial incident report" },
    { id:"detective_notes", label:"Detective / Investigative Notes", desc:"Notes from any follow-up investigation or detective assignment" },
    { id:"evidence_logs", label:"Evidence Logs / Property Records", desc:"Records of evidence collected — photos, clothing, physical evidence" },
    { id:"victim_statements", label:"Victim / Witness Statements", desc:"Transcripts or summaries of statements taken from victims and witnesses" },
    { id:"body_cam", label:"Body Camera / Dashboard Camera Footage", desc:"Video recordings from responding officers — may require separate request" },
    { id:"protective_orders", label:"Protective / Restraining Orders", desc:"Records of any protective orders filed or served" },
    { id:"referrals", label:"CPS/DCF Referral Records", desc:"Records of any referrals the police department made to child welfare agencies" },
  ];

  const togglePol = id => setPol(p=>({...p, selectedTypes: p.selectedTypes.includes(id)?p.selectedTypes.filter(r=>r!==id):[...p.selectedTypes,id]}));

  const canProceedPol = () => {
    if (polStep===0) return pol.fullName;
    if (polStep===1) return pol.deptName;
    if (polStep===2) return pol.selectedTypes.length > 0;
    if (polStep===3) return true;
    return false;
  };

  const genPolLetter = () => {
    const labels = pol.selectedTypes.map(id=>POLICE_RECORD_TYPES.find(r=>r.id===id)?.label).filter(Boolean);
    const deptAddr = [pol.deptName, pol.deptAddress, [pol.deptCity, pol.deptState].filter(Boolean).join(", ")].filter(Boolean).join("\n");
    return `${today}\n\nRecords Division / FOIA Officer\n${deptAddr}\n\nRe: Public Records Request — Police Reports and Related Records\n\nDear Records Custodian:\n\nPursuant to my state's Freedom of Information Act / Open Records Act, I am requesting copies of the following records.\n\nREQUESTOR INFORMATION:\nName: ${pol.fullName}${pol.dob?"\nDate of Birth: "+pol.dob:""}\n\nINCIDENT INFORMATION:${pol.incidentDates?"\nApproximate Dates: "+pol.incidentDates:""}${pol.incidentAddresses?"\nAddresses / Locations: "+pol.incidentAddresses:""}${pol.personsInvolved?"\nPersons Involved: "+pol.personsInvolved:""}${pol.officerNames?"\nOfficer Names (if known): "+pol.officerNames:""}${pol.caseNumbers?"\nCase / Report Numbers (if known): "+pol.caseNumbers:""}\n\nRECORDS REQUESTED:\n${labels.map((l,i)=>(i+1)+". "+l).join("\n")}${pol.additionalContext?"\n\nADDITIONAL CONTEXT:\n"+pol.additionalContext:""}\n\nI am requesting ALL records matching the above criteria, including any records that may have been filed under different names, addresses, or case numbers related to the same incidents or individuals.\n\nPlease note: I am requesting records about incidents involving myself ${pol.personsInvolved?"and/or my family members":""} and I have a right to access these records as a person directly involved.\n\nIf any records are exempt from disclosure, please provide a written explanation identifying each record withheld and the specific statutory exemption claimed.\n\nIf there are fees associated with this request, please notify me of the estimated cost before processing. I request a fee waiver if available, as these records are being sought for personal, non-commercial use.\n\nPlease respond within the timeframe required by your state's open records law.\n\nThank you for your assistance.\n\nSincerely,\n\n______________________________\n${pol.fullName}\n\n[YOUR ADDRESS — or P.O. Box, General Delivery, or shelter address]\n[YOUR PHONE]\n[YOUR EMAIL — include this so they can respond electronically]`;
  };

  const copyPolLetter = () => { navigator.clipboard.writeText(genPolLetter()).catch(()=>{}); };
  const downloadPolLetter = () => { const b=new Blob([genPolLetter()],{type:"text/plain"}); const u=URL.createObjectURL(b); const a=document.createElement("a"); a.href=u; a.download="Police-Records-FOIA-Request.txt"; a.click(); URL.revokeObjectURL(u); };


  // === Letter View ===
  if (mode === "letter" && sd) {
    const rr = sd.rr;
    const isOwn = rec.requestType === "own";
    const thirdPartyHardWarning =
      !isOwn && rr && (rr.thirdParty === "no" || rr.thirdParty === "court_order");

    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"24px 20px 40px" }}>
        {/* Verification status banner */}
        {!rr ? (
          <div style={{ padding:14, borderRadius:10, backgroundColor:"#FEF2F2", border:"1.5px solid #FECACA", marginBottom:16, fontSize:13, color:"#991B1B", lineHeight:1.6 }}>
            <strong>{selState}'s records-request procedures haven't been verified yet.</strong> The letter below uses a generic template that works in most states but doesn't account for {selState}-specific forms, deadlines, or restrictions. Before sending, check {selState}'s child welfare agency website for their preferred records-request process. Consider sending a test request first.
          </div>
        ) : !rr.verified ? (
          <div style={{ padding:14, borderRadius:10, backgroundColor:"#FFFBEB", border:"1.5px solid #FDE68A", marginBottom:16, fontSize:13, color:"#92400E", lineHeight:1.6 }}>
            <strong>{selState}'s records-request data is staged but not yet verified against current agency guidance.</strong> The letter and the procedural notes below are based on what we cross-referenced from public sources, but procedures change. Verify with the agency before relying on the deadline or required forms.
          </div>
        ) : (
          <div style={{ padding:14, borderRadius:10, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", marginBottom:16, fontSize:13, color:"#065F46", lineHeight:1.6 }}>
            <strong>✓ {selState}'s records-request procedures verified.</strong> Last reviewed: {STATES_DATA_LAST_REVIEWED}.
          </div>
        )}

        {/* Hard third-party warning */}
        {thirdPartyHardWarning && (
          <div style={{ padding:14, borderRadius:10, backgroundColor:"#FEF2F2", border:"1.5px solid #DC2626", marginBottom:16, fontSize:13, color:"#991B1B", lineHeight:1.6 }}>
            <strong>{selState} requires a court order for non-subject requests like this one.</strong> {rr.thirdPartyNote || ""} The letter below may be rejected without that order. Consult an attorney or legal aid organization before sending.
          </div>
        )}

        {/* Before you send this letter */}
        {rr && (
          <div style={{ ...cardSt, backgroundColor:"#F5F3FF", borderColor:"#DDD6FE", marginBottom:16 }}>
            <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#5B21B6", marginBottom:10 }}>Before you send this letter</div>
            <div style={{ fontSize:13, color:"#374151", lineHeight:1.7 }}>
              {rr.formRequired && (
                <div style={{ marginBottom:10, padding:10, backgroundColor:"#fff", borderRadius:8, border:"1px solid #E5E7EB" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#1a1a1a", marginBottom:4 }}>📄 Required form: {rr.formRequired}</div>
                  {rr.formNote && <div style={{ fontSize:12, color:"#6B7280", marginBottom:6 }}>{rr.formNote}</div>}
                  {rr.formUrl && (
                    <a href={rr.formUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:"#1E40AF", fontWeight:600 }}>
                      Open the form ↗
                    </a>
                  )}
                </div>
              )}
              {rr.responseDays && (
                <div style={{ marginBottom:8 }}>
                  <strong>Expected response time:</strong> {rr.responseDays} {rr.responseDaysType === "business" ? "business" : "calendar"} day{rr.responseDays === 1 ? "" : "s"}
                  {rr.responseNote && <div style={{ fontSize:12, color:"#6B7280", marginTop:2 }}>{rr.responseNote}</div>}
                </div>
              )}
              {rr.idRequired && (
                <div style={{ marginBottom:8 }}>
                  <strong>📇 Photo ID required.</strong> {rr.idNote || "Include a copy of your photo ID with the request."}
                </div>
              )}
              {rr.submitMethods && rr.submitMethods.length > 0 && (
                <div style={{ marginBottom:8 }}>
                  <strong>📬 How to submit:</strong>{" "}
                  {rr.submitPortal && <a href={rr.submitPortal} target="_blank" rel="noopener noreferrer" style={{ color:"#1E40AF", fontWeight:600 }}>Online portal ↗</a>}
                  {rr.submitPortal && rr.submitEmail && " · "}
                  {rr.submitEmail && <a href={`mailto:${rr.submitEmail}`} style={{ color:"#1E40AF", fontWeight:600 }}>{rr.submitEmail}</a>}
                  {(rr.submitPortal || rr.submitEmail) && rr.submitMethods.includes("mail") && " · "}
                  {rr.submitMethods.includes("mail") && <span>or mail to the address in the letter</span>}
                </div>
              )}
              {!isOwn && rr.thirdParty === "limited" && (
                <div style={{ marginBottom:8, padding:8, backgroundColor:"#FFFBEB", borderRadius:6, fontSize:12, color:"#92400E" }}>
                  <strong>Third-party request notice:</strong> {rr.thirdPartyNote || "This state restricts who can request CPS records on behalf of someone else. Verify you qualify before sending."}
                </div>
              )}
              {rr.notes && (
                <div style={{ marginTop:8, padding:8, backgroundColor:"#fff", borderRadius:6, fontSize:12, color:"#374151", border:"1px solid #E5E7EB" }}>
                  {rr.notes}
                </div>
              )}
              {rr.source && (
                <div style={{ marginTop:8, fontSize:11, color:"#9CA3AF" }}>
                  Source: {rr.source}
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ padding:14, borderRadius:10, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", marginBottom:12, fontSize:13, color:"#065F46", lineHeight:1.6 }}>
          <strong>Edit this letter directly below.</strong> Fill in your address, phone, and email. Change anything that doesn't apply. When you're done, copy or download it. Everything stays in your browser — nothing is saved or sent until you mail it yourself.
        </div>
        {letterEdited && (
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <span style={{ fontSize:11, color:"#059669", fontWeight:600 }}>Edited</span>
            <button onClick={initLetter} style={{ border:"none", backgroundColor:"transparent", color:"#9CA3AF", fontFamily:fonts, fontSize:11, cursor:"pointer", textDecoration:"underline", padding:0 }}>Reset to original</button>
          </div>
        )}
        <div style={{ display:"flex", gap:8, marginBottom:12 }}>
          <button onClick={copyLetter} style={{ flex:1, padding:"12px", borderRadius:8, border:"none", backgroundColor: copied ? "#059669" : "#1a1a1a", color:"#fff", fontFamily:fonts, fontSize:14, fontWeight:600, cursor:"pointer", transition:"background-color 0.2s" }}>{copied ? "Copied" : "Copy Letter"}</button>
          <button onClick={downloadLetter} style={{ flex:1, padding:"12px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#374151", fontFamily:fonts, fontSize:14, fontWeight:500, cursor:"pointer" }}>Download</button>
        </div>
        <textarea
          value={letterText}
          onChange={e=>{setLetterText(e.target.value); setLetterEdited(true);}}
          spellCheck={true}
          style={{
            width:"100%", minHeight:480, padding:16, borderRadius:10,
            backgroundColor:"#fff", border:"1px solid #E5E7EB",
            fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize:12, lineHeight:1.8, color:"#374151",
            resize:"vertical", boxSizing:"border-box",
          }}
        />
        <div style={cardSt}>
          <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>What to do with this letter</div>
          <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.8 }}>
            <p style={{margin:"0 0 6px"}}><strong>1.</strong> Edit the letter above — fill in your address, phone, and email where the brackets are. Change anything else that needs changing.</p>
            <p style={{margin:"0 0 6px"}}><strong>2.</strong> Copy or download the finished letter.</p>
            <p style={{margin:"0 0 6px"}}><strong>3.</strong> Mail it to the address at the top of the letter. If you can, use certified mail so you have proof you sent it. If you can't afford certified mail, regular mail works too.</p>
            <p style={{margin:"0 0 6px"}}><strong>4.</strong> Keep a copy. Take a photo with your phone if you don't have a printer.</p>
            <p style={{margin:"0 0 6px"}}><strong>5.</strong> Write down the date you mailed it. {sd?.rr?.responseDays ? `They have ${sd.rr.responseDays} ${sd.rr.responseDaysType === "business" ? "business" : "calendar"} day${sd.rr.responseDays === 1 ? "" : "s"} to respond` : "They have about 30 days to respond"} — mark {respDate} on your calendar.</p>
            <p style={{margin:0}}><strong>6.</strong> If they don't respond, or they send back a short summary instead of everything you asked for, we have a follow-up letter for that too.</p>
          </div>
        </div>
        <div style={{ ...cardSt, backgroundColor:"#EFF6FF", borderColor:"#BFDBFE" }}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1E40AF", marginBottom:6 }}>No stable address?</div>
          <div style={{ fontSize:12, color:"#1E40AF", lineHeight:1.7 }}>
            You need somewhere to receive the agency's response. Options: use <strong>USPS General Delivery</strong> (free — any post office will hold mail for 30 days, just pick it up with your ID), a <strong>P.O. Box</strong> ($20-60 for 6 months), a shelter address, or a trusted friend's address. Include an email address and phone number in the letter as backup — some agencies can respond electronically. Some states (like Texas) deliver records through an online portal, no mailing address needed.
          </div>
        </div>
        {sd.rn && <div style={{ ...cardSt, backgroundColor:"#FFFBEB", borderColor:"#FDE68A" }}>
          <div style={{ fontFamily:fonts, fontSize:12, fontWeight:700, color:"#92400E", marginBottom:4 }}>Important for {selState}</div>
          <div style={{ fontSize:12, color:"#92400E", lineHeight:1.6 }}>{expandAcronyms(sd.rn)}</div>
        </div>}
        <button onClick={()=>setMode("followup")} style={{ width:"100%", marginTop:8, padding:"14px", borderRadius:10, border:"1.5px solid #DC2626", backgroundColor:"#FEF2F2", color:"#991B1B", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer" }}>
          They didn't respond or sent incomplete records? Generate a follow-up letter →
        </button>
        <button onClick={()=>setPage("contribute")} style={{ width:"100%", marginTop:12, padding:"10px", borderRadius:8, border:"1px solid #BFDBFE", backgroundColor:"#EFF6FF", color:"#1E40AF", fontFamily:fonts, fontSize:12, fontWeight:600, cursor:"pointer" }}>
          Did this letter work? Tell us what happened — help the next person →
        </button>
        <button onClick={()=>{setMode("menu");setRecStep(0);setRec(p=>({...p,selectedRecords:RECORD_TYPES.filter(r=>r.default).map(r=>r.id)}));}} style={{ width:"100%", marginTop:8, padding:"12px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#6B7280", fontFamily:fonts, fontSize:13, cursor:"pointer" }}>← Back to Records</button>
      </div>
    );
  }

  // === SOL Info View ===
  if (mode === "sol_info" && sd) {
    const hasActiveLookback = sd.lk && (sd.lk.includes("ACTIVE") || sd.lk.includes("PERMANENT"));
    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
        <button onClick={()=>setMode("menu")} style={{ marginBottom:16, padding:"8px 16px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#6B7280", fontFamily:fonts, fontSize:13, cursor:"pointer" }}>← Back</button>
        <h1 style={sectionTitle}>{selState} — Legal Deadlines</h1>
        <p style={{ fontSize:13, color:"#6B7280", marginBottom:16 }}>{sd.af}</p>
        <LegalDisclaimer />

        <div style={{ ...cardSt, backgroundColor:"#F8FAFC", borderColor:"#E2E8F0", marginBottom:20 }}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1a1a1a", marginBottom:6 }}>What are these deadlines?</div>
          <div style={{ fontSize:12, color:"#6B7280", lineHeight:1.7 }}>
            <p style={{margin:"0 0 6px"}}>Every state sets a deadline for taking legal action over abuse. After this deadline passes, courts generally won't hear your case — even if the abuse is well-documented. These deadlines are called "statutes of limitations."</p>
            <p style={{margin:"0 0 6px"}}><strong>Civil deadline</strong> = how long you have to file a lawsuit seeking money damages from the person who hurt you or the institution that let it happen.</p>
            <p style={{margin:"0 0 6px"}}><strong>Criminal deadline</strong> = how long prosecutors have to file criminal charges. You don't control this — it's up to the district attorney. But you can report at any time and let them decide.</p>
            <p style={{margin:0, fontStyle:"italic", color:"#9CA3AF"}}>The deadlines shown below are for sexual abuse specifically. Physical abuse, neglect, and other forms of harm have separate deadlines — usually the state's general personal injury statute (typically 2-6 years). An attorney can tell you which deadlines apply to your situation.</p>
          </div>
        </div>

        {hasActiveLookback && (
          <div style={{ padding:14, borderRadius:10, backgroundColor:"#FEF2F2", border:"1.5px solid #DC2626", marginBottom:16, fontSize:13, color:"#991B1B", lineHeight:1.6 }}>
            <strong>Your state has reopened the deadline or removed it entirely.</strong> This means you may be able to take legal action even if you thought your time had passed.<br/><br/>{expandAcronyms(sd.lk)}
          </div>
        )}
        <div style={cardSt}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1a1a1a", marginBottom:4 }}>Civil Deadline — How long you have to sue</div>
          <div style={{ fontSize:11, color:"#9CA3AF", marginBottom:8 }}>This is the deadline for filing a civil lawsuit for money damages against an abuser or an institution.</div>
          <div style={{ fontSize:13, color:"#374151", lineHeight:1.7 }}>{expandAcronyms(sd.sc)}</div>
        </div>
        <div style={cardSt}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1a1a1a", marginBottom:4 }}>Criminal Deadline — How long prosecutors have to charge</div>
          <div style={{ fontSize:11, color:"#9CA3AF", marginBottom:8 }}>You can report a crime at any time. This deadline applies to prosecutors, not to you.</div>
          <div style={{ fontSize:13, color:"#374151", lineHeight:1.7 }}>{expandAcronyms(sd.sk)}</div>
        </div>
        <div style={cardSt}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1a1a1a", marginBottom:4 }}>Lookback Window — Has the deadline been reopened?</div>
          <div style={{ fontSize:11, color:"#9CA3AF", marginBottom:8 }}>Some states temporarily or permanently reopen the deadline so survivors can file lawsuits they were previously locked out of.</div>
          <div style={{ fontSize:13, color:"#374151", lineHeight:1.7 }}>{expandAcronyms(sd.lk)}</div>
        </div>
        <div style={cardSt}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>Agency & Contact</div>
          <div style={{ fontSize:13, color:"#374151", lineHeight:1.7 }}>{sd.ad}</div>
          {sd.p && <div style={{ fontSize:12, color:"#6B7280", marginTop:6 }}>{sd.p}</div>}
        </div>
        {sd.nt && <div style={cardSt}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>Notes</div>
          <div style={{ fontSize:13, color:"#374151", lineHeight:1.7 }}>{expandAcronyms(sd.nt)}</div>
        </div>}

        <div style={{ ...cardSt, backgroundColor:"#F8FAFC", borderColor:"#E2E8F0" }}>
          <div style={{ fontFamily:fonts, fontSize:12, fontWeight:700, color:"#374151", marginBottom:6 }}>Sources & verification</div>
          <div style={{ fontSize:12, color:"#6B7280", lineHeight:1.7 }}>
            {sd.st && <div style={{ marginBottom:4 }}><strong>Statute cited:</strong> {sd.st}</div>}
            {sd.src && <div style={{ marginBottom:4 }}><strong>Cross-referenced against:</strong> {sd.src}</div>}
            {!sd.src && <div style={{ marginBottom:4 }}><strong>Cross-referenced against:</strong> CHILD USA SOL Tracker (April 2026 review). Some states still need a primary-source verification — see disclaimer above.</div>}
            <div style={{ marginTop:6 }}>
              Verify yourself at:{" "}
              <a href="https://childusa.org/2025-sol-tracker/" target="_blank" rel="noopener noreferrer" style={{ color:"#1E40AF" }}>CHILD USA SOL Tracker</a>
              {" · "}
              <a href={`https://www.google.com/search?q=${encodeURIComponent((sd.n||selState) + " statute of limitations childhood sexual abuse civil")}`} target="_blank" rel="noopener noreferrer" style={{ color:"#1E40AF" }}>Search the state code</a>
            </div>
          </div>
        </div>

        <div style={{ ...cardSt, backgroundColor:"#EFF6FF", borderColor:"#BFDBFE" }}>
          <div style={{ fontFamily:serifFonts, fontSize:16, fontWeight:700, color:"#1E40AF", marginBottom:10 }}>What to do right now</div>
          <div style={{ fontSize:13, color:"#374151", lineHeight:1.8, marginBottom:14 }}>
            {hasActiveLookback
              ? "Your state has an open window or has eliminated the deadline. This means you may have legal options right now — but some windows close soon. Start by getting your records, then talk to an attorney."
              : "Even without a lookback window, you may still be within your state's filing deadline. Start by getting your records — they're the foundation for any legal action."
            }
          </div>
          <button onClick={()=>{setMode("generator");setRecStep(0);setRec(p=>({...p,selectedRecords:RECORD_TYPES.filter(r=>r.default).map(r=>r.id)}));}} style={{
            width:"100%", padding:"12px", borderRadius:8, border:"none", backgroundColor:"#1E40AF",
            color:"#fff", fontFamily:fonts, fontSize:14, fontWeight:700, cursor:"pointer", marginBottom:8,
          }}>
            Request your {selState} records now →
          </button>
          <button onClick={()=>setPage("search_tool")} style={{
            width:"100%", padding:"12px", borderRadius:8, border:"1px solid #BFDBFE", backgroundColor:"#fff",
            color:"#1E40AF", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer", marginBottom:8,
          }}>
            Search public records for your abuser →
          </button>
          <button onClick={()=>setPage("case")} style={{
            width:"100%", padding:"12px", borderRadius:8, border:"1px solid #BFDBFE", backgroundColor:"#fff",
            color:"#1E40AF", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer",
          }}>
            Build my full case — step by step →
          </button>
        </div>

        <div style={{ padding:10, borderRadius:8, backgroundColor:"#FFFBEB", border:"1px solid #FDE68A", fontSize:11, color:"#92400E", lineHeight:1.5, marginTop:8 }}>
          This is general information, not legal advice. Laws change. An attorney who handles childhood abuse cases can tell you exactly where you stand — many offer free consultations.
        </div>
        <button onClick={()=>setPage("contribute")} style={{ marginTop:8, border:"none", backgroundColor:"transparent", padding:0, cursor:"pointer", fontFamily:fonts, fontSize:11, color:"#1E40AF", textDecoration:"underline" }}>
          Something wrong on this page? Help us fix it →
        </button>
      </div>
    );
  }

  // === Generator Flow ===
  if (mode === "generator") {
    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <h2 style={{ fontFamily:serifFonts, fontSize:18, fontWeight:700, color:"#1a1a1a", margin:0 }}>{selState} Records Request</h2>
          <span style={{ fontSize:11, color:"#9CA3AF" }}>Step {recStep+1} of 4</span>
        </div>
        <div style={{ display:"flex", gap:4, marginBottom:20 }}>{[0,1,2,3].map(i=><div key={i} style={{ flex:1, height:3, borderRadius:2, backgroundColor:i<=recStep?"#1a1a1a":"#E5E7EB" }} />)}</div>

        {recStep===0 && (<div>
          <h3 style={{ fontFamily:serifFonts, fontSize:20, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>Whose records?</h3>
          <p style={{ fontSize:13, color:"#9CA3AF", marginBottom:16, lineHeight:1.6 }}>Are you asking for your own file, or someone else's?</p>
          {[{v:"own",l:"My own records",d:"I was in foster care, or CPS looked into my family"},{v:"other",l:"Someone else's records",d:"I'm their parent, guardian, or legal representative"}].map(o=>(
            <button key={o.v} onClick={()=>updR("requestType",o.v)} style={{ width:"100%", padding:"14px 16px", borderRadius:10, border:`1.5px solid ${rec.requestType===o.v?"#2563EB":"#E5E7EB"}`, backgroundColor:rec.requestType===o.v?"#EFF6FF":"#fff", textAlign:"left", marginBottom:8, cursor:"pointer" }}>
              <div style={{ fontFamily:fonts, fontSize:14, fontWeight:600, color:rec.requestType===o.v?"#1E40AF":"#374151", marginBottom:2 }}>{o.l}</div>
              <div style={{ fontSize:12, color:"#9CA3AF" }}>{o.d}</div>
            </button>
          ))}
          {rec.requestType==="other" && (<div style={{marginTop:12}}>
            <div style={{marginBottom:12}}><label style={labelSt}>Your relationship</label><input value={rec.relationship} onChange={e=>updR("relationship",e.target.value)} placeholder="e.g., parent, guardian, attorney" style={inputSt} /></div>
            <div><label style={labelSt}>Their full name</label><input value={rec.childName} onChange={e=>updR("childName",e.target.value)} placeholder="Full legal name" style={inputSt} /></div>
          </div>)}
        </div>)}

        {recStep===1 && (<div>
          <h3 style={{ fontFamily:serifFonts, fontSize:20, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>Your information</h3>
          <p style={{ fontSize:13, color:"#9CA3AF", marginBottom:16, lineHeight:1.6 }}>This helps them find your file. If you don't remember exact dates, that's okay — put what you know.</p>
          <div style={{marginBottom:12}}><label style={labelSt}>Full legal name</label><input value={rec.fullName} onChange={e=>updR("fullName",e.target.value)} style={inputSt} /></div>
          <div style={{marginBottom:12}}><label style={labelSt}>Date of birth</label><input type="date" value={rec.dob} onChange={e=>updR("dob",e.target.value)} style={inputSt} /></div>
          <div style={{marginBottom:12}}><label style={labelSt}>Previous names (optional)</label><input value={rec.previousNames} onChange={e=>updR("previousNames",e.target.value)} placeholder="Maiden name, married names" style={inputSt} /></div>
          <div style={{marginBottom:12}}><label style={labelSt}>Addresses from that time (optional)</label><textarea value={rec.addresses} onChange={e=>updR("addresses",e.target.value)} placeholder="Where you lived during agency involvement" rows={2} style={{...inputSt,resize:"vertical"}} /></div>
          <div style={{marginBottom:12}}><label style={labelSt}>Case numbers (optional)</label><input value={rec.caseNumbers} onChange={e=>updR("caseNumbers",e.target.value)} placeholder="If you have them" style={inputSt} /></div>
          <div><label style={labelSt}>Approximate dates of involvement (optional)</label><input value={rec.dateRange} onChange={e=>updR("dateRange",e.target.value)} placeholder="e.g., 1998-2009" style={inputSt} /></div>
        </div>)}

        {recStep===2 && (<div>
          <h3 style={{ fontFamily:serifFonts, fontSize:20, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>What records do you want?</h3>
          <div style={{ padding:10, borderRadius:8, backgroundColor:"#FFFBEB", border:"1px solid #FDE68A", fontSize:12, color:"#92400E", lineHeight:1.5, marginBottom:14 }}>
            We already picked the most important ones for you. If you just ask for "your records" without being specific, agencies often send a short summary and leave out the details. Our letter names each record type so they can't do that.
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {RECORD_TYPES.map(r=>(
              <button key={r.id} onClick={()=>toggleRec(r.id)} style={{ padding:"10px 12px", borderRadius:8, textAlign:"left", cursor:"pointer", border:`1.5px solid ${rec.selectedRecords.includes(r.id)?"#2563EB":"#E5E7EB"}`, backgroundColor:rec.selectedRecords.includes(r.id)?"#EFF6FF":"#fff" }}>
                <div style={{ fontSize:12, fontWeight:600, color:rec.selectedRecords.includes(r.id)?"#1E40AF":"#374151", marginBottom:1 }}>{rec.selectedRecords.includes(r.id)?"✓ ":""}{r.label}</div>
                <div style={{ fontSize:11, color:"#9CA3AF" }}>{r.desc}</div>
              </button>
            ))}
          </div>
          <button onClick={()=>setRec(p=>({...p,selectedRecords:p.selectedRecords.length===RECORD_TYPES.length?RECORD_TYPES.filter(r=>r.default).map(r=>r.id):RECORD_TYPES.map(r=>r.id)}))} style={{ marginTop:8, padding:"6px 12px", border:"none", backgroundColor:"transparent", color:"#2563EB", fontFamily:fonts, fontSize:12, fontWeight:600, cursor:"pointer" }}>
            {rec.selectedRecords.length===RECORD_TYPES.length?"Reset to recommended":"Select all"}
          </button>
        </div>)}

        {recStep===3 && (<div>
          <h3 style={{ fontFamily:serifFonts, fontSize:20, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>Anything else to mention?</h3>
          <p style={{ fontSize:13, color:"#9CA3AF", marginBottom:14, lineHeight:1.6 }}>This is optional. You can mention a specific caseworker, a time period, or anything else you want them to know.</p>
          <textarea value={rec.additionalNotes} onChange={e=>updR("additionalNotes",e.target.value)} placeholder="Optional additional context..." rows={3} style={{...inputSt,resize:"vertical",marginBottom:16}} />
          <div style={{ padding:12, borderRadius:10, backgroundColor:"#F8FAFC", border:"1px solid #E2E8F0", fontSize:12, color:"#374151", lineHeight:1.8, marginBottom:12 }}>
            <div style={{fontWeight:700,marginBottom:4}}>Summary:</div>
            <div><strong>Type:</strong> {rec.requestType==="own"?"Own records":`Records for ${rec.childName}`}</div>
            <div><strong>Name:</strong> {rec.fullName}</div>
            <div><strong>Records:</strong> {rec.selectedRecords.length} types (individually named)</div>
            <div><strong>To:</strong> {sd?.af}</div>
          </div>
          <div style={{ padding:10, borderRadius:8, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", fontSize:11, color:"#065F46", lineHeight:1.5 }}>
            We don't save any of this. Your answers are used to write the letter and that's it. The letter only exists on your phone or computer — we never see it.
          </div>
        </div>)}

        <div style={{ display:"flex", gap:8, marginTop:20 }}>
          {recStep>0 && <button onClick={()=>setRecStep(s=>s-1)} style={{ padding:"12px 18px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#374151", fontFamily:fonts, fontSize:14, fontWeight:500, cursor:"pointer" }}>Back</button>}
          <button onClick={()=>{if(recStep===3){initLetter();setMode("letter");}else setRecStep(s=>s+1);}} disabled={!canProceedRec()} style={{
            flex:1, padding:"12px", borderRadius:8, border:"none",
            backgroundColor:canProceedRec()?(recStep===3?"#059669":"#1a1a1a"):"#D1D5DB",
            color:"#fff", fontFamily:fonts, fontSize:14, fontWeight:600, cursor:canProceedRec()?"pointer":"default",
          }}>
            {recStep===3?"Generate My Letter":"Continue"}
          </button>
        </div>
        <button onClick={()=>{setMode("menu");setRecStep(0);}} style={{ width:"100%", marginTop:10, padding:"10px", borderRadius:8, border:"none", backgroundColor:"transparent", color:"#9CA3AF", fontFamily:fonts, fontSize:12, cursor:"pointer" }}>Cancel</button>
      </div>
    );
  }

  // === Follow-Up / Escalation Letter ===

  const genFollowUp = () => {
    if (!sd) return "";
    const addr = sd.ad || "";
    const agencyFull = sd.af || "";
    const statute = sd.st || "";
    const deadlineDate = fuDate ? new Date(new Date(fuDate).getTime()+30*24*60*60*1000).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"}) : "[30 days from original request]";

    if (fuType === "no_response") {
      return `${today}\n\n${agencyFull}\n${addr}\n\nRe: SECOND REQUEST — Records Request Follow-Up — No Response Received\n\nTo Whom It May Concern:\n\nOn ${fuDate || "[DATE OF ORIGINAL REQUEST]"}, I submitted a written request for my child welfare records pursuant to ${statute || "state law"}. As of today's date, I have not received any response — no acknowledgment, no records, and no explanation for the delay.\n\nThis request is now ${fuDate ? Math.floor((Date.now()-new Date(fuDate).getTime())/(1000*60*60*24)) + " days" : "[NUMBER] days"} past the date of submission, well beyond the response time required by law.\n\nI am resubmitting my original request and asking for immediate attention. If there are any issues with my request, please contact me at the information below so they can be resolved.\n\nIf I do not receive a substantive response within 14 days of this letter, I will file a formal complaint with the state ombudsman and consult with legal counsel regarding my rights under ${statute || "applicable state law"}.\n\nI have attached a copy of my original request for your reference.\n\nSincerely,\n\n______________________________\n${rec.fullName || "[YOUR NAME]"}\n\n[YOUR ADDRESS]\n[YOUR PHONE]\n[YOUR EMAIL]\n\ncc: State Ombudsman\n    [Your state representative — optional but effective]`;
    }

    if (fuType === "incomplete") {
      return `${today}\n\n${agencyFull}\n${addr}\n\nRe: INCOMPLETE RESPONSE — Request for Remaining Records\n\nTo Whom It May Concern:\n\nOn ${fuDate || "[DATE OF ORIGINAL REQUEST]"}, I submitted a request for my child welfare records pursuant to ${statute || "state law"}. I received a response, but the records provided are incomplete.\n\n${fuDetails || "Specifically, the following records that I requested by name were not included:\n\n[LIST THE SPECIFIC RECORD TYPES THAT WERE MISSING]\n\nAdditionally, the records I did receive contain the following issues:\n\n[DESCRIBE: e.g., only a running narrative was provided instead of individual record types; large sections are redacted without explanation; timeline gaps exist where records should be present]"}\n\nMy original request specifically named each record type individually. Providing only a case summary or running narrative does not satisfy this request. I am entitled to the specific records I requested.\n\nFor any records that are being withheld, I require a written explanation identifying each record withheld and the specific statutory authority for the withholding, as required by law.\n\nPlease provide the remaining records within 14 days of this letter.\n\nSincerely,\n\n______________________________\n${rec.fullName || "[YOUR NAME]"}\n\n[YOUR ADDRESS]\n[YOUR PHONE]\n[YOUR EMAIL]\n\ncc: State Ombudsman`;
    }

    if (fuType === "denied") {
      return `${today}\n\n${agencyFull}\n${addr}\n\nRe: APPEAL OF RECORDS DENIAL\n\nTo Whom It May Concern:\n\nOn ${fuDate || "[DATE OF ORIGINAL REQUEST]"}, I submitted a request for my child welfare records pursuant to ${statute || "state law"}. My request was denied.\n\n${fuDetails || "[DESCRIBE THE REASON GIVEN FOR DENIAL, OR STATE: No specific reason was provided.]"}\n\nI am the subject of these records and have a legal right to access them under ${statute || "applicable state law"}. The denial of my request does not cite a valid statutory exemption that applies to a records subject requesting their own file.\n\nI am formally appealing this denial. I request that this appeal be reviewed by a supervisor or the agency's legal counsel, and that I receive a written response within 14 days explaining:\n\n1. The specific statutory authority for the denial\n2. Which specific records are being denied (not a blanket denial)\n3. The process for further appeal if this decision is upheld\n\nIf this appeal is not resolved satisfactorily, I will file a complaint with the state ombudsman, the state attorney general's office, and consult with legal counsel.\n\nSincerely,\n\n______________________________\n${rec.fullName || "[YOUR NAME]"}\n\n[YOUR ADDRESS]\n[YOUR PHONE]\n[YOUR EMAIL]\n\ncc: State Ombudsman\n    State Attorney General — Public Records Division\n    [Your state representative — optional]`;
    }

    return "";
  };

  const copyFollowUp = () => { navigator.clipboard.writeText(genFollowUp()).catch(()=>{}); };
  const downloadFollowUp = () => { const b=new Blob([genFollowUp()],{type:"text/plain"}); const u=URL.createObjectURL(b); const a=document.createElement("a"); a.href=u; a.download="Records-Follow-Up-Letter.txt"; a.click(); URL.revokeObjectURL(u); };

  if (mode === "followup") {
    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
        <h1 style={sectionTitle}>They Didn't Respond?</h1>
        <LegalDisclaimer />
        <p style={{ fontSize:14, color:"#6B7280", lineHeight:1.7, marginBottom:20 }}>
          This happens a lot. You sent a letter and heard nothing. Or they sent back something incomplete. Or they said no. Here's what to do.
        </p>

        <div style={{ marginBottom:20 }}>
          <label style={labelSt}>Which state's agency did you write to?</label>
          <select value={selState} onChange={e=>setSelState(e.target.value)} style={{ ...inputSt, fontSize:15, padding:"13px 14px", cursor:"pointer", appearance:"auto" }}>
            <option value="">Choose a state...</option>
            {US_STATES.filter(s=>s!=="DC").map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {!sd && (
          <div style={{ padding:14, borderRadius:10, backgroundColor:"#FEF3C7", border:"1px solid #FDE68A", fontSize:13, color:"#78350F", lineHeight:1.7, marginBottom:16 }}>
            Pick a state above to generate the follow-up letter. The letter pulls in the right agency address and the legal authority for your state.
          </div>
        )}

        <div style={{ marginBottom:20 }}>
          <label style={labelSt}>What happened?</label>
          {[
            { id:"no_response", label:"No response at all", desc:"I sent my request and heard nothing back", color:"#DC2626" },
            { id:"incomplete", label:"Incomplete records", desc:"They sent something but it's missing records I asked for, or they only sent a summary", color:"#D97706" },
            { id:"denied", label:"Request was denied", desc:"They said I can't have my records", color:"#991B1B" },
          ].map(opt => (
            <button key={opt.id} onClick={()=>setFuType(opt.id)} style={{
              width:"100%", padding:"12px 14px", borderRadius:8, textAlign:"left", cursor:"pointer", marginBottom:6,
              border:`1.5px solid ${fuType===opt.id?opt.color:"#E5E7EB"}`,
              backgroundColor:fuType===opt.id?"#FEF2F2":"#fff",
            }}>
              <div style={{ fontSize:13, fontWeight:600, color:fuType===opt.id?opt.color:"#374151", marginBottom:1 }}>
                {fuType===opt.id?"✓ ":""}{opt.label}
              </div>
              <div style={{ fontSize:11, color:"#9CA3AF" }}>{opt.desc}</div>
            </button>
          ))}
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={labelSt}>When did you send your original request?</label>
          <input type="date" value={fuDate} onChange={e=>setFuDate(e.target.value)} style={inputSt} />
        </div>

        {fuType !== "no_response" && (
          <div style={{ marginBottom:16 }}>
            <label style={labelSt}>{fuType==="incomplete" ? "What's missing or wrong?" : "What reason did they give?"}</label>
            <textarea value={fuDetails} onChange={e=>setFuDetails(e.target.value)} placeholder={fuType==="incomplete" ? "e.g., They only sent the running narrative, not the individual record types I requested. No investigation protocols were included. There's a gap from 2004-2007." : "e.g., They said the records are confidential and can't be released, but didn't cite a specific statute."} rows={4} style={{...inputSt,resize:"vertical"}} />
          </div>
        )}

        <div style={{ display:"flex", gap:8, marginBottom:16 }}>
          <button onClick={copyFollowUp} style={{ flex:1, padding:"12px", borderRadius:8, border:"none", backgroundColor:"#DC2626", color:"#fff", fontFamily:fonts, fontSize:14, fontWeight:600, cursor:"pointer" }}>Copy Letter</button>
          <button onClick={downloadFollowUp} style={{ flex:1, padding:"12px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#374151", fontFamily:fonts, fontSize:14, fontWeight:500, cursor:"pointer" }}>Download</button>
        </div>

        <div style={{ padding:16, borderRadius:10, backgroundColor:"#fff", border:"1px solid #E5E7EB", fontFamily:"monospace", fontSize:11, lineHeight:1.8, color:"#374151", whiteSpace:"pre-wrap", wordBreak:"break-word", marginBottom:16 }}>
          {genFollowUp()}
        </div>

        <div style={cardSt}>
          <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>Escalation tactics</div>
          <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.8 }}>
            <p style={{margin:"0 0 8px"}}><strong>CC the ombudsman.</strong> Every state has one. The letter includes a cc line for this. Agencies respond faster when they know someone is watching. Search "[your state] child welfare ombudsman" or "children's ombudsman."</p>
            <p style={{margin:"0 0 8px"}}><strong>CC your state legislator.</strong> A letter from a constituent about a state agency gets attention. Find yours at openstates.org. You don't need to explain your whole story — just say the agency isn't complying with your records request.</p>
            <p style={{margin:"0 0 8px"}}><strong>File a formal complaint.</strong> Most states have a public records complaint process through the attorney general or a records ombudsman. This creates an official paper trail.</p>
            <p style={{margin:0}}><strong>Consult a lawyer.</strong> If the agency continues to stonewall, an attorney letter often breaks the logjam. Many child welfare attorneys will write a demand letter as part of a free consultation.</p>
          </div>
        </div>

        <button onClick={()=>setMode("menu")} style={{ width:"100%", marginTop:16, padding:"12px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#6B7280", fontFamily:fonts, fontSize:13, cursor:"pointer" }}>← Back to Records</button>
      </div>
    );
  }

  // === Prior History / Central Registry Request ===
  const genPriorHistory = () => {
    if (!sd || !priorName) return "";
    const addr = sd.ad || "";
    const agencyFull = sd.af || "";
    const statute = sd.st || "";
    return `${today}\n\n${agencyFull}\n${addr}\n\nRe: Request for Prior Reports and Investigation History — Named Individual\n\nTo Whom It May Concern:\n\nI am writing to request information regarding any prior reports, investigations, findings, or records maintained by ${sd.a || agencyFull} concerning the following individual:\n\nName: ${priorName}${priorAltNames?"\nAlternate Names / Spellings: "+priorAltNames:""}${priorDOB?"\nDate of Birth (if known): "+priorDOB:""}\n\nRelationship to me: ${priorRelationship || "[YOUR RELATIONSHIP — e.g., parent, guardian, foster parent, family member, caretaker]"}\n\nI am requesting this information because this individual had access to me as a child while the agency was involved with my family. Specifically, I am requesting:\n\n1. Whether this individual was the subject of any prior child abuse or neglect reports (indicated, substantiated, or otherwise) — at any time, including before and after my case\n2. Whether this individual was the subject of any child protective services investigation\n3. Whether this individual appears in the state Central Registry of substantiated child abuse/neglect findings\n4. Whether this individual was approved, licensed, or otherwise vetted by the agency as a foster parent, guardian, caretaker, or placement resource — and if so, the results of any background checks conducted\n5. Whether any prior reports or findings regarding this individual were available to the caseworkers assigned to my case\n\n${priorContext?priorContext+"\n\n":""}I am the subject of a child welfare case handled by this agency, and I am requesting this information in connection with my own case records. This individual's prior history — or the absence of a proper background check — is directly relevant to understanding how and why the agency made the placement and supervision decisions documented in my file.\n\n${statute ? "Legal authority: " + statute + ".\n\n" : ""}If any of this information is restricted, please provide a written explanation identifying the specific statutory basis for the restriction and advise me on the process for obtaining a court order to access it.\n\nPlease confirm receipt of this request.\n\nSincerely,\n\n______________________________\n${rec.fullName || "[YOUR NAME]"}\n\n[YOUR ADDRESS]\n[YOUR PHONE]\n[YOUR EMAIL]`;
  };

  const copyPrior = () => { navigator.clipboard.writeText(genPriorHistory()).catch(()=>{}); };
  const downloadPrior = () => { const b=new Blob([genPriorHistory()],{type:"text/plain"}); const u=URL.createObjectURL(b); const a=document.createElement("a"); a.href=u; a.download="Prior-History-Request.txt"; a.click(); URL.revokeObjectURL(u); };

  if (mode === "prior_history") {
    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
        <button onClick={()=>setMode("menu")} style={{ marginBottom:16, padding:"8px 16px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#6B7280", fontFamily:fonts, fontSize:13, cursor:"pointer" }}>← Back</button>
        <h1 style={sectionTitle}>Were They Reported Before You?</h1>
        <LegalDisclaimer />
        <p style={{ fontSize:14, color:"#6B7280", lineHeight:1.7, marginBottom:16 }}>
          If the person who harmed you had prior reports, prior investigations, or prior substantiated findings BEFORE they got access to you — that changes everything. It means the system had warning and failed to act on it.
        </p>

        <div style={{ padding:14, borderRadius:10, backgroundColor:"#FEF2F2", border:"1px solid #FECACA", marginBottom:20, fontSize:13, color:"#991B1B", lineHeight:1.7 }}>
          <strong>Why this matters.</strong> If a foster parent, guardian, or caretaker had prior abuse findings and the agency still placed a child with them — or if prior reports existed and caseworkers weren't told — that's evidence of negligence. In one Connecticut case, a woman filed a $30 million claim against DCF after discovering that the man who raped her thousands of times starting at age 9 had been approved as her guardian — despite the family being investigated 27 times. The agency's failure to act on its own records was central to the claim.
        </div>

        {!selState && (
          <div style={{ marginBottom:20 }}>
            <label style={labelSt}>Select the state where your case was handled</label>
            <select value={selState} onChange={e=>setSelState(e.target.value)} style={{ ...inputSt, fontSize:15, padding:"13px 14px", cursor:"pointer", appearance:"auto" }}>
              <option value="">Choose a state...</option>
              {US_STATES.filter(s=>s!=="DC").map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}

        {(selState || sd) && (<div>
          <div style={{ marginBottom:14 }}>
            <label style={labelSt}>Name of the person you want to check</label>
            <input value={priorName} onChange={e=>setPriorName(e.target.value)} placeholder="Full name of the abuser, foster parent, guardian, or caretaker" style={inputSt} />
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={labelSt}>Alternate names or spellings (optional)</label>
            <input value={priorAltNames} onChange={e=>setPriorAltNames(e.target.value)} placeholder="Maiden name, married name, nicknames" style={inputSt} />
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={labelSt}>Their date of birth (if known)</label>
            <input type="date" value={priorDOB} onChange={e=>setPriorDOB(e.target.value)} style={inputSt} />
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={labelSt}>Their relationship to you</label>
            <input value={priorRelationship} onChange={e=>setPriorRelationship(e.target.value)} placeholder="e.g., foster parent, guardian, stepfather, family friend, group home staff" style={inputSt} />
          </div>

          <div style={{ marginBottom:20 }}>
            <label style={labelSt}>Additional context (optional)</label>
            <textarea value={priorContext} onChange={e=>setPriorContext(e.target.value)} placeholder="e.g., This individual was approved as my legal guardian in 2003 despite the family being under active DCF investigation. I want to know if prior reports about this person existed before the guardianship was granted." rows={3} style={{...inputSt,resize:"vertical"}} />
          </div>

          {priorName && (
            <div>
              <div style={{ display:"flex", gap:8, marginBottom:16 }}>
                <button onClick={copyPrior} style={{ flex:1, padding:"12px", borderRadius:8, border:"none", backgroundColor:"#991B1B", color:"#fff", fontFamily:fonts, fontSize:14, fontWeight:600, cursor:"pointer" }}>Copy Letter</button>
                <button onClick={downloadPrior} style={{ flex:1, padding:"12px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#374151", fontFamily:fonts, fontSize:14, fontWeight:500, cursor:"pointer" }}>Download</button>
              </div>

              <div style={{ padding:16, borderRadius:10, backgroundColor:"#fff", border:"1px solid #E5E7EB", fontFamily:"monospace", fontSize:11, lineHeight:1.8, color:"#374151", whiteSpace:"pre-wrap", wordBreak:"break-word", marginBottom:16 }}>
                {genPriorHistory()}
              </div>
            </div>
          )}

          <div style={cardSt}>
            <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>What to expect</div>
            <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.8 }}>
              <p style={{margin:"0 0 8px"}}><strong>They may refuse.</strong> Many states restrict access to another person's CPS history, even when you were the victim. If they refuse, the letter asks for the specific legal basis — and you can challenge it with a court order. An attorney can help file a motion.</p>
              <p style={{margin:"0 0 8px"}}><strong>The Central Registry is key.</strong> Most states maintain a central registry of substantiated abuse/neglect findings. If your abuser is on it, that means the state formally determined they abused or neglected a child — possibly before you. Some states allow victims to request this check; others require a court order.</p>
              <p style={{margin:"0 0 8px"}}><strong>Background check records matter too.</strong> If the agency approved this person as a foster parent, guardian, or placement — there should be a background check on file. If the check was never done, or if it turned up red flags that were ignored, that's negligence.</p>
              <p style={{margin:0}}><strong>Pair this with the "Who Else?" search tool.</strong> While you wait for the agency to respond, use the public records search tool to check court records, sex offender registries, and news coverage for this person's name.</p>
            </div>
          </div>

          <div style={{ ...cardSt, backgroundColor:"#F5F3FF", borderColor:"#DDD6FE" }}>
            <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#5B21B6", marginBottom:6 }}>Why abusers and institutions fear this request</div>
            <div style={{ fontSize:12, color:"#5B21B6", lineHeight:1.7 }}>
              <p style={{margin:"0 0 6px"}}>If prior reports existed and the agency still approved this person — the agency can't claim they didn't know.</p>
              <p style={{margin:"0 0 6px"}}>If background checks were never conducted — that's a policy violation in every state.</p>
              <p style={{margin:"0 0 6px"}}>If prior findings were in the Central Registry and caseworkers weren't informed — that's a systemic failure with a paper trail.</p>
              <p style={{margin:0}}>This information, combined with your own case records, can establish that the harm you experienced was preventable — and that someone in a position of authority chose not to prevent it.</p>
            </div>
          </div>
        </div>)}
      </div>
    );
  }

  // === Court Records Guide ===
  if (mode === "court_guide") {
    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
        <button onClick={()=>setMode("menu")} style={{ marginBottom:16, padding:"8px 16px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#6B7280", fontFamily:fonts, fontSize:13, cursor:"pointer" }}>← Back</button>
        <h1 style={sectionTitle}>Court Records</h1>
        <LegalDisclaimer />
        <p style={{ fontSize:14, color:"#6B7280", lineHeight:1.7, marginBottom:20 }}>
          Courts document what happened in ways that other systems don't. Most court records are public and many are searchable online right now — for free.
        </p>

        <div style={{ ...cardSt, backgroundColor:"#EFF6FF", borderColor:"#BFDBFE" }}>
          <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1E40AF", marginBottom:10 }}>Start here — free online searches</div>
          <div style={{ fontSize:13, color:"#374151", lineHeight:1.8 }}>
            <p style={{margin:"0 0 10px"}}>Before you contact any court, try searching online. You can find a surprising amount from your couch.</p>
            <p style={{margin:"0 0 10px"}}><strong>1. Google the person's name in quotes.</strong> Search <span style={{fontFamily:"monospace",backgroundColor:"#F3F4F6",padding:"2px 6px",borderRadius:4,fontSize:12}}>"First Last" court</span> or <span style={{fontFamily:"monospace",backgroundColor:"#F3F4F6",padding:"2px 6px",borderRadius:4,fontSize:12}}>"First Last" arrest</span> or <span style={{fontFamily:"monospace",backgroundColor:"#F3F4F6",padding:"2px 6px",borderRadius:4,fontSize:12}}>"First Last" convicted</span>. The quotes force an exact name match. Add the state or city to narrow results. Try maiden names, nicknames, and middle names too.</p>
            <p style={{margin:"0 0 10px"}}><strong>2. Judyrecords.com</strong> — Free search engine indexing 760M+ US state court records. Open the homepage and type the name into the search box yourself — the site does not support pre-filled search links, so you can't deep-link to it. Often the fastest way to find a state court history. <strong>CourtListener.com</strong> is a good free alternative, especially for federal cases (it does support deep-linked search).</p>
            <p style={{margin:"0 0 10px"}}><strong>3. Your state's court records portal.</strong> Every state has one. Search <span style={{fontFamily:"monospace",backgroundColor:"#F3F4F6",padding:"2px 6px",borderRadius:4,fontSize:12}}>[your state] court records search</span> in Google. Most let you search by name for free. Some common ones:</p>
          </div>
          <div style={{ fontSize:12, color:"#374151", lineHeight:1.9, padding:"8px 12px", backgroundColor:"#F8FAFC", borderRadius:8 }}>
            <div><strong>CT:</strong> jud.ct.gov/crim.htm</div>
            <div><strong>NY:</strong> iapps.courts.state.ny.us/webcivil</div>
            <div><strong>CA:</strong> Check your county superior court website</div>
            <div><strong>TX:</strong> re.search.txcourts.gov</div>
            <div><strong>FL:</strong> myflcourtaccess.com</div>
            <div><strong>PA:</strong> ujsportal.pacourts.us</div>
            <div><strong>IL:</strong> Check your county circuit clerk website</div>
            <div><strong>OH:</strong> Check your county common pleas court</div>
            <div style={{marginTop:4, color:"#6B7280"}}>Can't find yours? Google <span style={{fontFamily:"monospace",backgroundColor:"#fff",padding:"1px 4px",borderRadius:3}}>[your state] court case search</span></div>
          </div>
          <div style={{ fontSize:13, color:"#374151", lineHeight:1.8, marginTop:10 }}>
            <p style={{margin:"0 0 10px"}}><strong>4. PACER</strong> (pacer.uscourts.gov) — Federal court records. Searching the index is free; viewing documents costs $0.10/page (waived under $30/quarter). <strong>Federal courts only</strong> — it does not cover state child welfare, family court, or most criminal cases. <strong>Registration requires your real name, address, and a credit card</strong> on file even if you're never charged. If you're hiding from an abuser, consider that paper trail before signing up.</p>
            <p style={{margin:"0 0 10px"}}><strong>5. Sex offender registries.</strong> Go to <strong>nsopw.gov</strong> to search all state registries at once. If your abuser was convicted of a qualifying offense, they'll be listed with photo, address, and offense details.</p>
            <p style={{margin:0}}><strong>6. Google News search.</strong> Search the person's name in Google News. Arrests and convictions are often covered by local newspapers, especially in smaller communities. Try <span style={{fontFamily:"monospace",backgroundColor:"#F3F4F6",padding:"2px 6px",borderRadius:4,fontSize:12}}>"First Last" site:newspapers.com</span> for historical archives.</p>
          </div>
        </div>

        <div style={{ fontFamily:fonts, fontSize:11, fontWeight:600, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:"0.08em", marginTop:24, marginBottom:12 }}>
          Types of court records and how to get them
        </div>

        {[
          {
            title: "Criminal Cases Against Your Abuser",
            access: "Public record",
            color: "#DC2626",
            content: "Criminal cases are public in every state. If someone was charged — even if the charges were dropped, reduced, or they were acquitted — the case file exists. It includes the charging document (indictment or information), plea records, sentencing, and often victim impact statements. Start with the online searches above. If you find a case, you can request the full file from the clerk of the court that handled it. Docket entries are often available online; the underlying documents (police affidavits, witness statements) usually require a request to the clerk."
          },
          {
            title: "Juvenile Dependency / Child Welfare Court Cases",
            access: "Sealed — but you can request your own",
            color: "#7C3AED",
            content: "When CPS removes a child or opens a court case, there's a parallel juvenile court proceeding — separate from the agency file. These records include court petitions, hearing transcripts, judge's orders, guardian ad litem reports, and court-appointed attorney notes. These are sealed from the public but as the subject you can request your own. Contact the clerk of the juvenile or family court in the county where the case was heard. Some states require a motion to unseal — a lawyer can help. Your child welfare agency records (from the letter generator above) often reference the court case number, which makes finding them easier. Vermont's H.644 now grants automatic access to both DCF and court records."
          },
          {
            title: "Protective / Restraining Orders",
            access: "Generally public",
            color: "#0891B2",
            content: "Protective orders are generally searchable through court records systems. Search by the name of the person the order was filed against OR the name of the person who filed it. Many states maintain a domestic violence protective order registry — Google your state's. If a protective order was filed on your behalf when you were a child, it may be in the juvenile court file. Emergency (ex parte) orders and final orders may be filed separately."
          },
          {
            title: "Divorce / Custody Records",
            access: "Mostly public — some portions sealed",
            color: "#BE185D",
            content: "Divorce and custody cases are in family or superior court. The basic file is public — filings, motions, orders. But portions involving children may be sealed, including custody evaluations, guardian ad litem reports, and in-camera testimony about abuse. These records often contain abuse allegations in sworn affidavits that document what was happening in the home. If your parents divorced and abuse was alleged, this can be powerful corroboration. Search the family court records in the county where the divorce was filed. You may need a motion to access sealed child-related portions."
          },
          {
            title: "Civil Lawsuits",
            access: "Public record",
            color: "#6B7280",
            content: "If anyone has previously sued your abuser or the institution that enabled them, those cases are public. Search by the defendant's name (abuser or institution) on Judyrecords (manual search — open the site and type into the search box), CourtListener, or your state court portal. Other survivors may have already filed against the same person or entity — finding these can connect you with specialized attorneys, reveal patterns, and provide evidence. Settlements are sometimes sealed, but the existence of the case and the claims made are usually public."
          },
        ].map((ct, i) => (
          <div key={i} style={{ ...cardSt, marginBottom:10 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
              <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a" }}>{ct.title}</div>
              <span style={{ fontSize:10, fontWeight:600, color:ct.color, whiteSpace:"nowrap", marginLeft:8 }}>{ct.access}</span>
            </div>
            <div style={{ fontSize:13, color:"#374151", lineHeight:1.7 }}>{ct.content}</div>
          </div>
        ))}

        <div style={{ ...cardSt, backgroundColor:"#FFFBEB", borderColor:"#FDE68A" }}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#92400E", marginBottom:6 }}>Search tips</div>
          <div style={{ fontSize:12, color:"#92400E", lineHeight:1.7 }}>
            <p style={{margin:"0 0 6px"}}>Try every name variation — maiden names, married names, middle names, nicknames, common misspellings.</p>
            <p style={{margin:"0 0 6px"}}>If the person moved between counties or states, search each jurisdiction separately.</p>
            <p style={{margin:"0 0 6px"}}>If you find a case number, write it down — you'll need it to request the full file from the clerk.</p>
            <p style={{margin:0}}>Some older records aren't digitized. If online searches come up empty but you know there was a case, call or visit the clerk's office. They may have paper files that aren't in the online system.</p>
          </div>
        </div>

        <div style={{ ...cardSt, backgroundColor:"#F5F3FF", borderColor:"#DDD6FE" }}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#5B21B6", marginBottom:6 }}>A note about what you might find</div>
          <div style={{ fontSize:12, color:"#5B21B6", lineHeight:1.6 }}>
            Court records can contain graphic details — victim statements, police testimony, medical evidence. If you haven't read the "Before You Read Your Records" guide yet, the same preparation applies here.
          </div>
        </div>
      </div>
    );
  }

  // === Police FOIA Letter View ===
  if (mode === "police_letter") {
    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"24px 20px 40px" }}>
        <div style={{ padding:14, borderRadius:10, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", marginBottom:16, fontSize:13, color:"#065F46", lineHeight:1.6 }}>
          <strong>This request names every record type individually.</strong> Dispatch logs and call records are often missed — they show every time police came to your address, even calls that didn't result in a written report.
        </div>
        <div style={{ display:"flex", gap:8, marginBottom:16 }}>
          <button onClick={copyPolLetter} style={{ flex:1, padding:"12px", borderRadius:8, border:"none", backgroundColor:"#1a1a1a", color:"#fff", fontFamily:fonts, fontSize:14, fontWeight:600, cursor:"pointer" }}>Copy Letter</button>
          <button onClick={downloadPolLetter} style={{ flex:1, padding:"12px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#374151", fontFamily:fonts, fontSize:14, fontWeight:500, cursor:"pointer" }}>Download</button>
        </div>
        <div style={{ padding:16, borderRadius:10, backgroundColor:"#fff", border:"1px solid #E5E7EB", fontFamily:"monospace", fontSize:11, lineHeight:1.8, color:"#374151", whiteSpace:"pre-wrap", wordBreak:"break-word", marginBottom:16 }}>
          {genPolLetter()}
        </div>
        <div style={cardSt}>
          <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>What to do next</div>
          <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.8 }}>
            <p style={{margin:"0 0 6px"}}><strong>1.</strong> Fill in your contact info at the bottom.</p>
            <p style={{margin:"0 0 6px"}}><strong>2.</strong> Mail or deliver to the police department's records division.</p>
            <p style={{margin:"0 0 6px"}}><strong>3.</strong> Many departments also accept FOIA requests by email or online portal — check their website.</p>
            <p style={{margin:"0 0 6px"}}><strong>4.</strong> Keep a copy. Note the date you submitted.</p>
            <p style={{margin:"0 0 6px"}}><strong>5.</strong> Response times vary by state — typically 5-30 days. Follow up in writing if no response.</p>
            <p style={{margin:0}}><strong>6.</strong> If they deny records or charge excessive fees, you can appeal. Most states have a public records ombudsman or attorney general process.</p>
          </div>
        </div>
        <div style={{ ...cardSt, backgroundColor:"#EFF6FF", borderColor:"#BFDBFE" }}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1E40AF", marginBottom:6 }}>Multiple departments?</div>
          <div style={{ fontSize:12, color:"#1E40AF", lineHeight:1.6 }}>
            If police from different towns, cities, or agencies responded over the years, you need to send a separate request to each one. State police, local police, and county sheriff are all different agencies with different records.
          </div>
        </div>
        <div style={{ ...cardSt, backgroundColor:"#FFFBEB", borderColor:"#FDE68A" }}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#92400E", marginBottom:6 }}>Body camera and 911 audio</div>
          <div style={{ fontSize:12, color:"#92400E", lineHeight:1.6 }}>
            Body camera footage and 911 call recordings are public records in most states but may require a separate, specific request. Some departments have short retention periods for video (30-180 days), so request quickly if the incidents were recent. Older footage may have been deleted per retention policy.
          </div>
        </div>
        <div style={{ ...cardSt, backgroundColor:"#EFF6FF", borderColor:"#BFDBFE" }}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1E40AF", marginBottom:6 }}>No stable address?</div>
          <div style={{ fontSize:12, color:"#1E40AF", lineHeight:1.6 }}>
            Use USPS General Delivery (free — any post office holds mail 30 days), a P.O. Box, a shelter address, or a trusted friend's address. Many police departments also accept email requests and can send records electronically — check their website or call the records division.
          </div>
        </div>
        <button onClick={()=>{setMode("menu");setPolStep(0);}} style={{ width:"100%", marginTop:16, padding:"12px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#6B7280", fontFamily:fonts, fontSize:13, cursor:"pointer" }}>← Back to Records</button>
      </div>
    );
  }

  // === Police FOIA Generator Flow ===
  if (mode === "police_foia") {
    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <h2 style={{ fontFamily:serifFonts, fontSize:18, fontWeight:700, color:"#1a1a1a", margin:0 }}>Police Records Request</h2>
          <span style={{ fontSize:11, color:"#9CA3AF" }}>Step {polStep+1} of 4</span>
        </div>
        <div style={{ display:"flex", gap:4, marginBottom:20 }}>{[0,1,2,3].map(i=><div key={i} style={{ flex:1, height:3, borderRadius:2, backgroundColor:i<=polStep?"#0891B2":"#E5E7EB" }} />)}</div>

        {polStep===0 && (<div>
          <h3 style={{ fontFamily:serifFonts, fontSize:20, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>Your information</h3>
          <p style={{ fontSize:13, color:"#9CA3AF", marginBottom:16, lineHeight:1.6 }}>This identifies you as the requestor. Police reports are generally public records under your state's open records law, with limited exceptions.</p>
          <div style={{marginBottom:12}}><label style={labelSt}>Your full name</label><input value={pol.fullName} onChange={e=>updP("fullName",e.target.value)} style={inputSt} /></div>
          <div style={{marginBottom:12}}><label style={labelSt}>Date of birth (optional — helps locate records)</label><input type="date" value={pol.dob} onChange={e=>updP("dob",e.target.value)} style={inputSt} /></div>
        </div>)}

        {polStep===1 && (<div>
          <h3 style={{ fontFamily:serifFonts, fontSize:20, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>Which police department?</h3>
          <p style={{ fontSize:13, color:"#9CA3AF", marginBottom:16, lineHeight:1.6 }}>Which police department came to your house or handled the incidents? If it was more than one department, you'll need to send a separate letter to each one.</p>
          <div style={{marginBottom:12}}><label style={labelSt}>Department name</label><input value={pol.deptName} onChange={e=>updP("deptName",e.target.value)} placeholder="e.g., Hartford Police Department" style={inputSt} /></div>
          <div style={{marginBottom:12}}><label style={labelSt}>Department address</label><input value={pol.deptAddress} onChange={e=>updP("deptAddress",e.target.value)} placeholder="Street address" style={inputSt} /></div>
          <div style={{ display:"flex", gap:8 }}>
            <div style={{flex:2}}><label style={labelSt}>City</label><input value={pol.deptCity} onChange={e=>updP("deptCity",e.target.value)} style={inputSt} /></div>
            <div style={{flex:1}}><label style={labelSt}>State</label><input value={pol.deptState} onChange={e=>updP("deptState",e.target.value)} placeholder="e.g., CT" style={inputSt} /></div>
          </div>
        </div>)}

        {polStep===2 && (<div>
          <h3 style={{ fontFamily:serifFonts, fontSize:20, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>What records do you want?</h3>
          <div style={{ padding:10, borderRadius:8, backgroundColor:"#ECFEFF", border:"1px solid #A5F3FC", fontSize:12, color:"#155E75", lineHeight:1.5, marginBottom:14 }}>
            We already picked the most important ones. Pay attention to dispatch logs — they record every time someone called the police to your address, even if no report was written. Those calls are proof that something was happening.
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {POLICE_RECORD_TYPES.map(r=>(
              <button key={r.id} onClick={()=>togglePol(r.id)} style={{ padding:"10px 12px", borderRadius:8, textAlign:"left", cursor:"pointer", border:`1.5px solid ${pol.selectedTypes.includes(r.id)?"#0891B2":"#E5E7EB"}`, backgroundColor:pol.selectedTypes.includes(r.id)?"#ECFEFF":"#fff" }}>
                <div style={{ fontSize:12, fontWeight:600, color:pol.selectedTypes.includes(r.id)?"#155E75":"#374151", marginBottom:1 }}>{pol.selectedTypes.includes(r.id)?"✓ ":""}{r.label}</div>
                <div style={{ fontSize:11, color:"#9CA3AF" }}>{r.desc}</div>
              </button>
            ))}
          </div>
          <button onClick={()=>setPol(p=>({...p,selectedTypes:p.selectedTypes.length===POLICE_RECORD_TYPES.length?["incident_reports","arrest_reports","call_logs"]:POLICE_RECORD_TYPES.map(r=>r.id)}))} style={{ marginTop:8, padding:"6px 12px", border:"none", backgroundColor:"transparent", color:"#0891B2", fontFamily:fonts, fontSize:12, fontWeight:600, cursor:"pointer" }}>
            {pol.selectedTypes.length===POLICE_RECORD_TYPES.length?"Reset to recommended":"Select all"}
          </button>
        </div>)}

        {polStep===3 && (<div>
          <h3 style={{ fontFamily:serifFonts, fontSize:20, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>Incident details</h3>
          <p style={{ fontSize:13, color:"#9CA3AF", marginBottom:16, lineHeight:1.6 }}>The more specific you can be, the faster they'll find your records. Approximate dates and addresses are fine.</p>
          <div style={{marginBottom:12}}><label style={labelSt}>Approximate dates of incidents</label><textarea value={pol.incidentDates} onChange={e=>updP("incidentDates",e.target.value)} placeholder="e.g., 1998-2009, or specific dates if you know them" rows={2} style={{...inputSt,resize:"vertical"}} /></div>
          <div style={{marginBottom:12}}><label style={labelSt}>Addresses / locations of incidents</label><textarea value={pol.incidentAddresses} onChange={e=>updP("incidentAddresses",e.target.value)} placeholder="Street addresses where incidents occurred" rows={2} style={{...inputSt,resize:"vertical"}} /></div>
          <div style={{marginBottom:12}}><label style={labelSt}>Persons involved (optional)</label><input value={pol.personsInvolved} onChange={e=>updP("personsInvolved",e.target.value)} placeholder="Names of people involved in the incidents" style={inputSt} /></div>
          <div style={{marginBottom:12}}><label style={labelSt}>Officer names (optional)</label><input value={pol.officerNames} onChange={e=>updP("officerNames",e.target.value)} placeholder="If you remember any responding officers" style={inputSt} /></div>
          <div style={{marginBottom:12}}><label style={labelSt}>Case or report numbers (optional)</label><input value={pol.caseNumbers} onChange={e=>updP("caseNumbers",e.target.value)} placeholder="If you have them" style={inputSt} /></div>
          <div><label style={labelSt}>Additional context (optional)</label><textarea value={pol.additionalContext} onChange={e=>updP("additionalContext",e.target.value)} placeholder="Any other details that would help locate records" rows={2} style={{...inputSt,resize:"vertical"}} /></div>
          <div style={{ padding:12, borderRadius:10, backgroundColor:"#F8FAFC", border:"1px solid #E2E8F0", fontSize:12, color:"#374151", lineHeight:1.8, marginTop:16, marginBottom:12 }}>
            <div style={{fontWeight:700,marginBottom:4}}>Summary:</div>
            <div><strong>Requestor:</strong> {pol.fullName}</div>
            <div><strong>Department:</strong> {pol.deptName}</div>
            <div><strong>Records:</strong> {pol.selectedTypes.length} types requested</div>
          </div>
          <div style={{ padding:10, borderRadius:8, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", fontSize:11, color:"#065F46", lineHeight:1.5 }}>
            Your info is only used to generate this letter. Nothing is stored or sent.
          </div>
        </div>)}

        <div style={{ display:"flex", gap:8, marginTop:20 }}>
          {polStep>0 && <button onClick={()=>setPolStep(s=>s-1)} style={{ padding:"12px 18px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#374151", fontFamily:fonts, fontSize:14, fontWeight:500, cursor:"pointer" }}>Back</button>}
          <button onClick={()=>{if(polStep===3){setMode("police_letter");}else setPolStep(s=>s+1);}} disabled={!canProceedPol()} style={{
            flex:1, padding:"12px", borderRadius:8, border:"none",
            backgroundColor:canProceedPol()?(polStep===3?"#059669":"#0891B2"):"#D1D5DB",
            color:"#fff", fontFamily:fonts, fontSize:14, fontWeight:600, cursor:canProceedPol()?"pointer":"default",
          }}>
            {polStep===3?"Generate My Letter":"Continue"}
          </button>
        </div>
        <button onClick={()=>{setMode("menu");setPolStep(0);}} style={{ width:"100%", marginTop:10, padding:"10px", borderRadius:8, border:"none", backgroundColor:"transparent", color:"#9CA3AF", fontFamily:fonts, fontSize:12, cursor:"pointer" }}>Cancel</button>
      </div>
    );
  }

  // === Records Menu (main view) ===
  const activeLookbacks = Object.entries(STATES_DATA).filter(([_,s])=>s.lk&&(s.lk.includes("ACTIVE")||s.lk.includes("PERMANENT"))).sort((a,b)=>a[1].n.localeCompare(b[1].n));

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
      <h1 style={sectionTitle}>Get Your Records</h1>
      <LegalDisclaimer />
      <p style={{ fontSize:15, color:"#374151", lineHeight:1.8, marginBottom:16 }}>
        When CPS, DCF, or a child welfare agency got involved in your life, they wrote things down. Reports. Case notes. Decisions they made about you. Those records belong to you. You can ask for them.
      </p>

      <div style={{ ...cardSt, backgroundColor:"#F8FAFC", borderColor:"#E2E8F0", marginBottom:24 }}>
        <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>We know this is hard.</div>
        <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.8 }}>
          <p style={{margin:"0 0 8px"}}>You're being asked to write a formal letter to the same system that was supposed to protect you and didn't. That takes courage. It's okay to feel angry about that.</p>
          <p style={{margin:"0 0 8px"}}><strong>This is not re-entering the system.</strong> You're not opening a case. No one is going to show up at your door. You're requesting documents that already exist — documents about YOUR life — and the law says they have to give them to you.</p>
          <p style={{margin:0}}>You don't have to write the letter yourself. This tool writes it for you. You answer a few simple questions, and we generate a letter that asks for the right things in the right way. All you have to do is mail it.</p>
        </div>
      </div>

      {/* === SECTION 1: Request Records === */}
      <div style={{ fontSize:11, fontWeight:700, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>
        Request your records
      </div>

      <div style={{ marginBottom:8 }}>
        <label style={labelSt}>Select your state</label>
        <select value={selState} onChange={e=>setSelState(e.target.value)} style={{ ...inputSt, fontSize:15, padding:"13px 14px", cursor:"pointer", appearance:"auto" }}>
          <option value="">Choose a state...</option>
          {US_STATES.filter(s=>s!=="DC").map(s=><option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {selState && sd && (
        <div style={{ marginBottom:8 }}>
          <button onClick={()=>{setMode("generator");setRecStep(0);setRec(p=>({...p,selectedRecords:RECORD_TYPES.filter(r=>r.default).map(r=>r.id)}));}} style={{
            width:"100%", padding:"16px", borderRadius:10, border:"none",
            backgroundColor:"#1a1a1a", cursor:"pointer", textAlign:"left", marginBottom:8,
          }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontFamily:fonts, fontSize:15, fontWeight:700, color:"#fff", marginBottom:2 }}>Generate your {selState} records request letter</div>
                <div style={{ fontSize:12, color:"#9CA3AF" }}>Answer a few questions → get a ready-to-mail letter</div>
              </div>
              <span style={{ fontSize:18, color:"#fff", marginLeft:12 }}>→</span>
            </div>
          </button>
          <button onClick={()=>setMode("sol_info")} style={{
            width:"100%", padding:"10px 16px", borderRadius:8, border:"1px solid #E5E7EB",
            backgroundColor:"#fff", cursor:"pointer", textAlign:"left", marginBottom:8,
          }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontFamily:fonts, fontSize:13, color:"#374151" }}>Check {selState} legal deadlines</span>
              <span style={{ fontSize:14, color:"#9CA3AF" }}>→</span>
            </div>
          </button>
          {sd.rn && <div style={{ padding:8, borderRadius:6, fontSize:11, color:"#9CA3AF", lineHeight:1.5 }}>{expandAcronyms(sd.rn)}</div>}
        </div>
      )}

      {[
        { label:"Police records request", desc:"Incident reports, dispatch logs, body camera footage", action:()=>{setMode("police_foia");setPolStep(0);} },
        { label:"Court records — how to search", desc:"Criminal cases, protective orders, custody records, civil lawsuits", action:()=>setMode("court_guide") },
        { label:"Were they reported before you?", desc:"Check if the system already knew about the person who harmed you", action:()=>setMode("prior_history") },
        { label:"They didn't respond or sent incomplete records?", desc:"Generate a follow-up or escalation letter", action:()=>setMode("followup") },
      ].map((item,i) => (
        <button key={i} onClick={item.action} style={{
          width:"100%", padding:"12px 16px", borderRadius:8,
          border:"1px solid #E5E7EB",
          backgroundColor:"#fff", cursor:"pointer", textAlign:"left", marginBottom:6,
        }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ fontFamily:fonts, fontSize:13, fontWeight:600, color:"#1a1a1a", marginBottom:1 }}>{item.label}</div>
              <div style={{ fontSize:11, color:"#9CA3AF" }}>{item.desc}</div>
            </div>
            <span style={{ fontSize:14, color:"#D1D5DB", marginLeft:8 }}>→</span>
          </div>
        </button>
      ))}

      <button onClick={()=>setPage("documents")} style={{
        width:"100%", padding:"10px 16px", borderRadius:8, border:"none",
        backgroundColor:"transparent", cursor:"pointer", textAlign:"left", marginBottom:24,
      }}>
        <span style={{ fontFamily:fonts, fontSize:12, color:"#9CA3AF", textDecoration:"underline" }}>Don't have an ID? Get your documents first</span>
      </button>

      {/* === SECTION 2: Understand & Use Your Records === */}
      <div style={{ fontSize:11, fontWeight:700, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>
        Already have your records?
      </div>

      {[
        { label:"Understand your records", desc:"How to read agency language, spot gaps, know when your file supports a legal claim", action:()=>setPage("records_guide") },
        { label:"Analyze with AI", desc:"Upload your file — get a plain-language summary, timeline, and gap analysis", action:()=>setPage("analyze") },
        { label:"Build your timeline", desc:"Put every event in order — from your records and your memory. Export it for an attorney.", action:()=>setPage("timeline") },
        { label:"Who else? — search public records", desc:"Find court cases, convictions, or other survivors who filed against the same person", action:()=>setPage("search_tool") },
        { label:"Hide yourself", desc:"Remove your address and contact info from people-search sites — a guided checklist for survivors", action:()=>setPage("hide") },
        { label:"Help build this", desc:"Contribute corrections, real-world records request reports, or volunteer expertise. The internet maintains this together.", action:()=>setPage("contribute") },
      ].map((item,i) => (
        <button key={i} onClick={item.action} style={{
          width:"100%", padding:"12px 16px", borderRadius:8,
          border:"1px solid #E5E7EB",
          backgroundColor:"#fff", cursor:"pointer", textAlign:"left", marginBottom:6,
        }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ fontFamily:fonts, fontSize:13, fontWeight:600, color:"#1a1a1a", marginBottom:1 }}>{item.label}</div>
              <div style={{ fontSize:11, color:"#9CA3AF" }}>{item.desc}</div>
            </div>
            <span style={{ fontSize:14, color:"#D1D5DB", marginLeft:8 }}>→</span>
          </div>
        </button>
      ))}

      <button onClick={()=>setPage("prep")} style={{
        width:"100%", padding:"10px 16px", borderRadius:8, border:"none",
        backgroundColor:"transparent", cursor:"pointer", textAlign:"left", marginBottom:24,
      }}>
        <span style={{ fontFamily:fonts, fontSize:12, color:"#9CA3AF", textDecoration:"underline" }}>Not ready yet? Read the emotional preparation guide</span>
      </button>

      {/* === SECTION 3: Lookback alerts === */}
      {activeLookbacks.length > 0 && (
        <div style={{ ...cardSt, backgroundColor:"#FEF2F2", borderColor:"#FECACA" }}>
          <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#991B1B", marginBottom:6 }}>You may be able to sue — even if you thought it was too late</div>
          <p style={{ fontSize:12, color:"#991B1B", lineHeight:1.7, margin:"0 0 10px" }}>
            Some states have recently reopened the filing deadline or removed it permanently. If you're in one of these states, you may be able to take legal action even if your deadline passed years ago.
          </p>
          <div style={{ padding:8, borderRadius:6, backgroundColor:"#fff", border:"1px solid #FECACA", marginBottom:10, fontSize:11, color:"#6B7280", lineHeight:1.5 }}>
            Most of these apply to sexual abuse specifically. Physical abuse and neglect have separate deadlines. An attorney can tell you which apply to your situation.
          </div>
          <div style={{ fontSize:12, color:"#991B1B", lineHeight:1.9 }}>
            {activeLookbacks.map(([ab,s])=>{
              const isPermanent = s.lk.includes("PERMANENT") || s.lk.includes("permanently") || s.lk.includes("eliminated");
              return (
                <div key={ab} style={{ marginBottom:6, padding:"8px 10px", borderRadius:6, backgroundColor:"#fff", border:"1px solid #FECACA", cursor:"pointer" }} onClick={()=>{setSelState(s.n);setMode("sol_info");}}>
                  <div style={{ fontWeight:700, marginBottom:2 }}>{s.n} {isPermanent ? "— no deadline" : "— closing soon"}</div>
                  <div style={{ fontSize:11, color:"#6B7280" }}>{expandAcronyms(s.lk).length>120?expandAcronyms(s.lk).substring(0,120)+"...":expandAcronyms(s.lk)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ padding:10, borderRadius:8, backgroundColor:"#F8FAFC", border:"1px solid #E5E7EB", fontSize:11, color:"#9CA3AF", lineHeight:1.5, marginTop:4 }}>
        This tool provides general information and generates template letters — it is not legal advice. An attorney can tell you exactly where you stand.
      </div>
      <button onClick={()=>setPage("about")} style={{ marginTop:6, border:"none", backgroundColor:"transparent", padding:0, cursor:"pointer", fontFamily:fonts, fontSize:11, color:"#9CA3AF", textDecoration:"underline" }}>
        Something wrong? Report it
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════
   PATTERN PAGE
   ═══════════════════════════════════════════ */

function PatternPage({ reports, setReports }) {
  const [sub, setSub] = useState("dashboard");
  const [activeCluster, setActiveCluster] = useState(null);
  const [contStep, setContStep] = useState(0);
  const [contData, setContData] = useState({ state:"", agency:"", contacts:"", years:"", failures:[], secondFailure:null, secondType:"", decade:"" });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [sub, contStep]);

  const clusters = detectClusters(reports);
  const [stateFilter, setStateFilter] = useState("all");
  const filtered = stateFilter === "all" ? reports : reports.filter(r => r.state === stateFilter);
  const statesWithData = [...new Set(reports.map(r => r.state))].sort();

  const totalReports = filtered.length;
  const avgContacts = totalReports > 0 ? Math.round(filtered.reduce((s,r)=>s+r.contacts,0)/totalReports) : 0;
  const continuedPct = totalReports > 0 ? Math.round(filtered.filter(r=>r.failures.includes("continued_after_contact")).length/totalReports*100) : 0;
  const multiPct = totalReports > 0 ? Math.round(filtered.filter(r=>r.secondFailure).length/totalReports*100) : 0;

  const failureCounts = {};
  filtered.forEach(r => r.failures.forEach(f => failureCounts[f]=(failureCounts[f]||0)+1));
  const failureData = Object.entries(failureCounts).map(([id,count])=>({
    label: FAILURE_TYPES.find(f=>f.id===id)?.label||id, pct: Math.round(count/totalReports*100)
  })).sort((a,b)=>b.pct-a.pct).slice(0,6);

  const toggleFailure = id => setContData(p => ({...p, failures: p.failures.includes(id)?p.failures.filter(f=>f!==id):[...p.failures,id]}));

  const submitContribution = async () => {
    const entry = { state:contData.state, agency:contData.agency, contacts:parseInt(contData.contacts), years:parseInt(contData.years), failures:contData.failures, secondFailure:contData.secondFailure, secondType:contData.secondType, decade:contData.decade };
    const updated = [...reports, entry];
    setReports(updated);
    try { await storage.set("gyr-pattern-db", JSON.stringify(updated), true); } catch(e){}
    setSubmitted(true);
    setSub("dashboard");
    setContStep(0);
    setContData({ state:"", agency:"", contacts:"", years:"", failures:[], secondFailure:null, secondType:"", decade:"" });
  };

  // Pattern detail view
  if (activeCluster) {
    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"24px 20px 40px" }}>
        <button onClick={() => setActiveCluster(null)} style={{ padding:"6px 0", border:"none", backgroundColor:"transparent", cursor:"pointer", fontSize:14, color:"#6B7280", fontFamily:fonts, marginBottom:16 }}>← Back</button>
        <h2 style={sectionTitle}>{activeCluster.state} — {activeCluster.agency}</h2>
        <p style={{ fontSize:14, color:"#6B7280", lineHeight:1.6, marginBottom:24 }}>
          {activeCluster.count} people have reported similar experiences. This space is for those who share this pattern.
        </p>
        {[
          { level:1, title:"Just here to see the data", desc:"View patterns. Know you're not alone. No interaction required.", color:"#6B7280", bg:"#F9FAFB", border:"#E5E7EB" },
          { level:2, title:"Connect with others", desc:"Anonymous, moderated group. Share what you've learned. Find support.", color:"#2563EB", bg:"#EFF6FF", border:"#BFDBFE" },
          { level:3, title:"Take action", desc:"Connect with attorneys, journalists, or advocacy orgs. Your identity stays private unless you choose otherwise.", color:"#059669", bg:"#F0FDF4", border:"#A7F3D0" },
        ].map(l => (
          <button key={l.level} style={{
            width:"100%", padding:"14px 16px", borderRadius:10, border:`1.5px solid ${l.border}`,
            backgroundColor:l.bg, cursor:"pointer", textAlign:"left", marginBottom:10,
          }}>
            <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:l.color, marginBottom:4 }}>Level {l.level}: {l.title}</div>
            <div style={{ fontFamily:fonts, fontSize:13, color:"#6B7280", lineHeight:1.6 }}>{l.desc}</div>
          </button>
        ))}
        {activeCluster.state === "Connecticut" && (
          <div style={{ ...cardSt, marginTop:16 }}>
            <div style={labelSt}>Resources for Connecticut</div>
            {[
              { name:"Baber Law", desc:"Handling the $30M DCF negligence case. Contingency — no fee unless they win." },
              { name:"CT Office of the Child Advocate", desc:"Independent office that investigates DCF failures. Can review your case." },
              { name:"DCF Records Request", desc:"Under CGS § 17a-28, you have the right to your own records. Ask for each type by name." },
            ].map((r,i) => (
              <div key={i} style={{ padding:"10px 12px", borderRadius:8, backgroundColor:"#F8FAFC", border:"1px solid #E2E8F0", marginBottom:8 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#1a1a1a", marginBottom:2 }}>{r.name}</div>
                <div style={{ fontSize:12, color:"#6B7280", lineHeight:1.5 }}>{r.desc}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Contribute flow
  if (sub === "contribute") {
    const canProceed = contStep===0 ? contData.state&&contData.agency : contStep===1 ? contData.contacts&&contData.years : contStep===2 ? contData.failures.length>0 : contStep===3 ? contData.secondFailure!==null&&contData.decade : true;
    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"24px 20px 40px" }}>
        <button onClick={() => { setSub("dashboard"); setContStep(0); }} style={{ padding:"6px 0", border:"none", backgroundColor:"transparent", cursor:"pointer", fontSize:14, color:"#6B7280", fontFamily:fonts, marginBottom:16 }}>← Back</button>
        {/* Progress */}
        <div style={{ display:"flex", gap:4, marginBottom:24 }}>
          {[0,1,2,3,4].map(s => <div key={s} style={{ flex:1, height:3, borderRadius:2, backgroundColor: s<=contStep?"#2563EB":"#E5E7EB" }} />)}
        </div>
        {contStep===0 && (<div>
          <h3 style={sectionTitle}>Where did this happen?</h3>
          <p style={{ fontSize:13, color:"#9CA3AF", marginBottom:20, lineHeight:1.6 }}>No names. No identifying details.</p>
          <div style={{ marginBottom:14 }}><label style={labelSt}>State</label><select value={contData.state} onChange={e=>setContData(p=>({...p,state:e.target.value}))} style={inputSt}><option value="">Select...</option>{US_STATES.map(s=><option key={s} value={s}>{s}</option>)}</select></div>
          <div><label style={labelSt}>Primary agency</label><select value={contData.agency} onChange={e=>setContData(p=>({...p,agency:e.target.value}))} style={inputSt}><option value="">Select...</option>{AGENCY_TYPES.map(a=><option key={a} value={a}>{a}</option>)}</select></div>
        </div>)}
        {contStep===1 && (<div>
          <h3 style={sectionTitle}>How many chances did they have?</h3>
          <p style={{ fontSize:13, color:"#9CA3AF", marginBottom:20, lineHeight:1.6 }}>Approximate numbers are fine.</p>
          <div style={{ marginBottom:14 }}><label style={labelSt}>Times contacted</label><select value={contData.contacts} onChange={e=>setContData(p=>({...p,contacts:e.target.value}))} style={inputSt}><option value="">Select...</option><option value="1">1</option><option value="3">2-3</option><option value="5">4-6</option><option value="8">7-10</option><option value="15">11-20</option><option value="25">20+</option></select></div>
          <div><label style={labelSt}>Over how many years</label><select value={contData.years} onChange={e=>setContData(p=>({...p,years:e.target.value}))} style={inputSt}><option value="">Select...</option><option value="1">Less than 1</option><option value="2">1-2</option><option value="4">3-5</option><option value="7">6-10</option><option value="12">10+</option></select></div>
        </div>)}
        {contStep===2 && (<div>
          <h3 style={sectionTitle}>What did the system fail to do?</h3>
          <p style={{ fontSize:13, color:"#9CA3AF", marginBottom:16, lineHeight:1.6 }}>Select all that apply.</p>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {FAILURE_TYPES.map(f => (
              <button key={f.id} onClick={() => toggleFailure(f.id)} style={{
                padding:"11px 14px", borderRadius:8, border:`1.5px solid ${contData.failures.includes(f.id)?"#2563EB":"#E5E7EB"}`,
                backgroundColor: contData.failures.includes(f.id)?"#EFF6FF":"#fff", cursor:"pointer", textAlign:"left",
                fontFamily:fonts, fontSize:13, color: contData.failures.includes(f.id)?"#1E40AF":"#374151",
                fontWeight: contData.failures.includes(f.id)?600:400,
              }}>
                {contData.failures.includes(f.id)?"✓ ":""}{f.label}
              </button>
            ))}
          </div>
          <div style={{ marginTop:12, padding:"10px 12px", borderRadius:8, backgroundColor:"#FFFBEB", border:"1px solid #FDE68A", fontSize:12, color:"#92400E", lineHeight:1.6 }}>
            This can be a lot to see laid out. You can stop anytime. If you need support: <strong>988</strong> (call or text).
          </div>
        </div>)}
        {contStep===3 && (<div>
          <h3 style={sectionTitle}>A couple more questions</h3>
          <div style={{ marginBottom:16 }}>
            <label style={labelSt}>Was there a second institution that also failed?</label>
            <div style={{ display:"flex", gap:8 }}>
              {[{v:true,l:"Yes"},{v:false,l:"No"}].map(o => (
                <button key={String(o.v)} onClick={() => setContData(p=>({...p,secondFailure:o.v}))} style={{
                  flex:1, padding:"10px", borderRadius:8, border:`1.5px solid ${contData.secondFailure===o.v?"#2563EB":"#E5E7EB"}`,
                  backgroundColor: contData.secondFailure===o.v?"#EFF6FF":"#fff", color: contData.secondFailure===o.v?"#1E40AF":"#374151",
                  fontFamily:fonts, fontSize:14, fontWeight: contData.secondFailure===o.v?600:400, cursor:"pointer",
                }}>{o.l}</button>
              ))}
            </div>
          </div>
          {contData.secondFailure && <div style={{ marginBottom:16 }}><label style={labelSt}>What type?</label><select value={contData.secondType} onChange={e=>setContData(p=>({...p,secondType:e.target.value}))} style={inputSt}><option value="">Select...</option>{AGENCY_TYPES.map(a=><option key={a} value={a}>{a}</option>)}</select></div>}
          <div><label style={labelSt}>Approximate decade</label><select value={contData.decade} onChange={e=>setContData(p=>({...p,decade:e.target.value}))} style={inputSt}><option value="">Select...</option>{["2020s","2010s","2000s","1990s","1980s","1970s or earlier"].map(d=><option key={d} value={d}>{d}</option>)}</select></div>
        </div>)}
        {contStep===4 && (<div>
          <h3 style={sectionTitle}>Review what you're sharing</h3>
          <div style={{ padding:14, borderRadius:10, backgroundColor:"#F8FAFC", border:"1px solid #E2E8F0", fontSize:13, color:"#374151", lineHeight:2, marginBottom:14 }}>
            <div><strong>State:</strong> {contData.state}</div>
            <div><strong>Agency:</strong> {contData.agency}</div>
            <div><strong>Contacts:</strong> ~{contData.contacts}</div>
            <div><strong>Years:</strong> ~{contData.years}</div>
            <div><strong>Decade:</strong> {contData.decade}</div>
            <div><strong>Failures:</strong> {contData.failures.map(fid => FAILURE_TYPES.find(f=>f.id===fid)?.label).join("; ")}</div>
            {contData.secondFailure && <div><strong>Second failure:</strong> {contData.secondType}</div>}
          </div>
          <div style={{ padding:10, borderRadius:8, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", fontSize:12, color:"#065F46", lineHeight:1.6 }}>
            Anonymous. Cannot be traced to you. Combined with other contributions to reveal patterns.
          </div>
        </div>)}
        <div style={{ display:"flex", gap:8, marginTop:20 }}>
          {contStep>0 && <button onClick={() => setContStep(s=>s-1)} style={{ padding:"12px 18px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#374151", fontFamily:fonts, fontSize:14, fontWeight:500, cursor:"pointer" }}>Back</button>}
          <button onClick={() => contStep===4?submitContribution():setContStep(s=>s+1)} disabled={!canProceed} style={{
            flex:1, padding:"12px", borderRadius:8, border:"none",
            backgroundColor: canProceed?(contStep===4?"#059669":"#1a1a1a"):"#D1D5DB",
            color:"#fff", fontFamily:fonts, fontSize:14, fontWeight:600, cursor: canProceed?"pointer":"default",
          }}>
            {contStep===4?"Save to my device":"Continue"}
          </button>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"24px 20px 40px" }}>
      <h1 style={sectionTitle}>The Pattern</h1>
      <p style={{ fontSize:13, color:"#6B7280", lineHeight:1.7, marginBottom:12 }}>
        A tool to map the failures you've experienced. Add each agency contact, what they did or didn't do, and what happened next. Over time, the page draws a picture of what was done to you — useful for therapy, attorney consultations, or just for yourself.
      </p>

      <div style={{ padding:12, borderRadius:10, backgroundColor:"#FFFBEB", border:"1px solid #FDE68A", marginBottom:12, fontSize:12, color:"#92400E", lineHeight:1.6 }}>
        <strong>This is your private workspace.</strong> Everything you enter here stays in this browser, on this device. Nothing is sent anywhere. Other users do not see your entries, and you do not see theirs. We are not a centralized database — yet. If you want to support a real cross-survivor pattern dataset in the future, let us know.
      </div>

      {submitted && (
        <div style={{ padding:14, borderRadius:10, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", marginTop:12, marginBottom:4, fontSize:13, color:"#065F46", lineHeight:1.6 }}>
          <strong>Saved.</strong> This entry is now part of your private record on this device.
        </div>
      )}

      <button onClick={() => setSub("contribute")} style={{
        width:"100%", padding:"12px", borderRadius:8, border:"none", backgroundColor:"#1a1a1a",
        color:"#fff", fontFamily:fonts, fontSize:14, fontWeight:600, cursor:"pointer", margin:"16px 0",
      }}>
        {reports.length === 0 ? "Add your first entry" : "+ Add another entry"}
      </button>

      {reports.length === 0 && (
        <div style={{ ...cardSt, backgroundColor:"#F8FAFC", textAlign:"center", padding:24 }}>
          <div style={{ fontFamily:serifFonts, fontSize:18, fontWeight:600, color:"#6B7280", marginBottom:10 }}>Nothing here yet</div>
          <p style={{ fontSize:13, color:"#9CA3AF", lineHeight:1.7, maxWidth:400, margin:"0 auto" }}>
            Add entries one at a time — each agency contact, each failure, each year. As you add them, this page will draw your timeline, your most common failures, and any patterns that show up. Stored only on this device.
          </p>
        </div>
      )}

      {reports.length > 0 && (<div>
      {/* Stats */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        {[
          { n:totalReports, l:"reports", c:"#1a1a1a" },
          { n:avgContacts, l:"avg contacts", c:"#1a1a1a" },
          { n:`${continuedPct}%`, l:"abuse continued", c:"#DC2626" },
          { n:`${multiPct}%`, l:"multi-failure", c:"#7C3AED" },
        ].map((s,i) => (
          <div key={i} style={{ flex:1, minWidth:72, padding:"12px 8px", borderRadius:10, backgroundColor:"#fff", border:"1px solid #E5E7EB", textAlign:"center" }}>
            <div style={{ fontFamily:serifFonts, fontSize:20, fontWeight:700, color:s.c }}>{s.n}</div>
            <div style={{ fontFamily:fonts, fontSize:9, color:"#9CA3AF", marginTop:3, lineHeight:1.3 }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <select value={stateFilter} onChange={e=>setStateFilter(e.target.value)} style={{ padding:"8px 12px", borderRadius:8, border:"1px solid #D1D5DB", fontFamily:fonts, fontSize:12, color:"#374151", backgroundColor:"#fff", marginBottom:16 }}>
        <option value="all">All states</option>
        {statesWithData.map(s=><option key={s} value={s}>{s} ({reports.filter(r=>r.state===s).length})</option>)}
      </select>

      {/* Failures */}
      <div style={cardSt}>
        <div style={labelSt}>Most common failures</div>
        {failureData.map((f,i) => (
          <div key={i} style={{ marginBottom:10 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3, fontSize:11, color:"#6B7280" }}>
              <span>{f.label.length>35?f.label.substring(0,35)+"…":f.label}</span>
              <span style={{ fontWeight:600, color:"#374151" }}>{f.pct}%</span>
            </div>
            <div style={{ height:4, borderRadius:2, backgroundColor:"#F3F4F6" }}>
              <div style={{ height:"100%", width:`${f.pct}%`, backgroundColor:CHART_COLORS[i], borderRadius:2 }} />
            </div>
          </div>
        ))}
      </div>

      {/* Clusters */}
      <div style={{ ...labelSt, marginBottom:8, marginTop:8 }}>Recurring patterns in your entries</div>
      <p style={{ fontSize:12, color:"#9CA3AF", lineHeight:1.5, marginBottom:14 }}>
        When the same agency or failure shows up across multiple of your entries, it surfaces here. These are repetitions in your own record — useful evidence when explaining what happened to a therapist or attorney.
      </p>
      {(stateFilter==="all"?clusters:clusters.filter(c=>c.state===stateFilter)).map((c,i) => (
        <div key={i} style={{ ...cardSt, borderColor: c.count>=5?"#BFDBFE":"#E5E7EB" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <div>
              <div style={{ fontFamily:serifFonts, fontSize:16, fontWeight:700, color:"#1a1a1a" }}>{c.state}</div>
              <div style={{ fontSize:11, color:"#6B7280" }}>{c.agency}</div>
            </div>
            <div style={{ fontFamily:serifFonts, fontSize:22, fontWeight:700, color: c.count>=5?"#2563EB":"#6B7280" }}>{c.count}</div>
          </div>
          <div style={{ display:"flex", gap:12, fontSize:11, color:"#6B7280", marginBottom:8 }}>
            <span><strong style={{ color:"#374151", fontSize:14 }}>{c.avgContacts}</strong> avg contacts</span>
            <span><strong style={{ color:"#374151", fontSize:14 }}>{c.avgYears}</strong> avg years</span>
            {c.continuedPct>0 && <span><strong style={{ color:"#DC2626", fontSize:14 }}>{c.continuedPct}%</strong> continued</span>}
          </div>
          {c.topFailures.map((f,j) => (
            <div key={j} style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", borderTop: j===0?"1px solid #F3F4F6":"none", fontSize:11, color:"#6B7280" }}>
              <span>{f.label}</span>
              <span style={{ fontWeight:600, color: f.pct>=70?"#DC2626":f.pct>=40?"#D97706":"#6B7280", backgroundColor: f.pct>=70?"#FEF2F2":f.pct>=40?"#FFFBEB":"#F9FAFB", padding:"1px 6px", borderRadius:8, fontSize:10 }}>{f.pct}%</span>
            </div>
          ))}
          {c.count >= 5 && (
            <button onClick={() => setActiveCluster(c)} style={{
              width:"100%", padding:"9px", borderRadius:8, border:"1px solid #BFDBFE",
              backgroundColor:"#EFF6FF", color:"#1E40AF", fontFamily:fonts, fontSize:12,
              fontWeight:600, cursor:"pointer", marginTop:10,
            }}>
              You logged this {c.count} times — view details →
            </button>
          )}
        </div>
      ))}

      <div style={{ marginTop:16, padding:10, borderRadius:8, backgroundColor:"#F8FAFC", border:"1px solid #E2E8F0", fontSize:10, color:"#94A3B8", lineHeight:1.6 }}>
        These numbers reflect only your own entries on this device. They are not independently verified and they are not aggregated with any other person's data.
      </div>
      </div>)}
    </div>
  );
}

/* ═══════════════════════════════════════════
   SAFETY CARD — Privacy & Data Clearing
   ═══════════════════════════════════════════ */

function SafetyCard() {
  const [confirmClear, setConfirmClear] = useState(false);
  const [cleared, setCleared] = useState(false);

  const clearAllData = async () => {
    try {
      await storage.delete("gyr-timeline");
      await storage.delete("gyr-stories");
      await storage.delete("gyr-case-progress");
    } catch(e) {}
    setCleared(true);
    setConfirmClear(false);
  };

  return (
    <div style={{ ...cardSt, backgroundColor:"#FEF2F2", borderColor:"#FECACA", marginTop:16 }}>
      <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#991B1B", marginBottom:8 }}>Your safety matters</div>
      <div style={{ fontSize:13, color:"#991B1B", lineHeight:1.8 }}>
        <p style={{margin:"0 0 8px"}}>Some tools in this app save data on your device — your timeline, journal entries, and case progress. This data is stored only on YOUR phone or computer. We never see it and it's never sent to any server.</p>
        <p style={{margin:"0 0 8px"}}><strong>If someone might look at your phone or computer</strong> — a partner, parent, foster parent, or anyone — you can delete everything this app has saved with one tap. The letters you generate, the searches you run, and everything else on this site disappears when you close the page.</p>
        <p style={{margin:"0 0 8px"}}>You can also use your browser's private/incognito mode — nothing will be saved at all.</p>
      </div>
      {cleared ? (
        <div style={{ padding:10, borderRadius:8, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", fontSize:13, color:"#065F46", fontWeight:600, textAlign:"center" }}>
          All saved data has been deleted.
        </div>
      ) : confirmClear ? (
        <div>
          <p style={{ fontSize:12, color:"#991B1B", marginBottom:8 }}>This will permanently delete your timeline, journal entries, and case progress. This cannot be undone.</p>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={clearAllData} style={{ flex:1, padding:"10px", borderRadius:8, border:"none", backgroundColor:"#991B1B", color:"#fff", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer" }}>
              Yes — delete everything
            </button>
            <button onClick={()=>setConfirmClear(false)} style={{ flex:1, padding:"10px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#374151", fontFamily:fonts, fontSize:13, cursor:"pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={()=>setConfirmClear(true)} style={{
          width:"100%", padding:"10px", borderRadius:8, border:"1px solid #FECACA",
          backgroundColor:"#fff", color:"#991B1B", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer",
        }}>
          Delete all saved data from this device
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   FEEDBACK FORM
   ═══════════════════════════════════════════ */

const FEEDBACK_ISSUE_TYPES = [
  { v:"wrong_data",   l:"Wrong legal info" },
  { v:"wrong_agency", l:"Wrong agency/address" },
  { v:"bug",          l:"Something broken" },
  { v:"suggestion",   l:"Suggestion" },
  { v:"other",        l:"Other" },
];

function FeedbackForm() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type:"", page:"", details:"", state:"", email:"" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!form.type || !form.details || sending) return;
    setSending(true);
    setError("");
    const label = FEEDBACK_ISSUE_TYPES.find(o => o.v === form.type)?.l || form.type;
    try {
      const res = await fetch("https://formspree.io/f/mvzdzvjn", {
        method: "POST",
        headers: { "Accept": "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          issue_type: label,
          page_or_state: form.page,
          details: form.details,
          email: form.email,
          _subject: `getmyrecords feedback: ${label}`,
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSent(true);
      setForm({ type:"", page:"", details:"", state:"", email:"" });
    } catch(e) {
      setError("Couldn't send. Check your connection and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ ...cardSt, marginTop:10 }}>
      <button onClick={()=>{setOpen(!open);setSent(false);}} style={{
        width:"100%", border:"none", backgroundColor:"transparent", cursor:"pointer",
        textAlign:"left", padding:0, display:"flex", justifyContent:"space-between", alignItems:"center",
      }}>
        <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a" }}>Found a problem?</div>
        <span style={{ fontSize:16, color:"#9CA3AF", transform:open?"rotate(180deg)":"none", transition:"transform 0.2s" }}>▾</span>
      </button>

      {!open && (
        <div style={{ fontSize:12, color:"#9CA3AF", marginTop:4 }}>
          Report incorrect data, bugs, or suggestions. Your correction could protect someone.
        </div>
      )}

      {open && (<div style={{ marginTop:12 }}>
        {sent ? (
          <div style={{ padding:12, borderRadius:8, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", fontSize:13, color:"#065F46", textAlign:"center" }}>
            Thank you. We'll review this and fix it.
          </div>
        ) : (<div>
          <div style={{ marginBottom:10 }}>
            <label style={labelSt}>What kind of issue?</label>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {FEEDBACK_ISSUE_TYPES.map(o => (
                <button key={o.v} onClick={()=>setForm(p=>({...p,type:o.v}))} style={{
                  padding:"6px 12px", borderRadius:16, fontSize:12, fontFamily:fonts, cursor:"pointer",
                  border: form.type===o.v ? "1.5px solid #1a1a1a" : "1px solid #E5E7EB",
                  backgroundColor: form.type===o.v ? "#1a1a1a" : "#fff",
                  color: form.type===o.v ? "#fff" : "#6B7280",
                }}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom:10 }}>
            <label style={labelSt}>Which page or state?</label>
            <input value={form.page} onChange={e=>setForm(p=>({...p,page:e.target.value}))} placeholder="e.g., Connecticut SOL info, Police FOIA generator" style={inputSt} />
          </div>

          <div style={{ marginBottom:10 }}>
            <label style={labelSt}>What's wrong or what should change?</label>
            <textarea value={form.details} onChange={e=>setForm(p=>({...p,details:e.target.value}))} placeholder="Be as specific as you can — include what it currently says vs. what it should say" rows={3} style={{...inputSt, resize:"vertical"}} />
          </div>

          <div style={{ marginBottom:12 }}>
            <label style={labelSt}>Your email (optional — only if you want a response)</label>
            <input value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="Optional" style={inputSt} />
          </div>

          <button onClick={submit} disabled={!form.type||!form.details||sending} style={{
            width:"100%", padding:"11px", borderRadius:8, border:"none",
            backgroundColor: (form.type&&form.details&&!sending) ? "#1a1a1a" : "#D1D5DB",
            color:"#fff", fontFamily:fonts, fontSize:13, fontWeight:600,
            cursor: (form.type&&form.details&&!sending) ? "pointer" : "default",
          }}>
            {sending ? "Sending…" : "Submit"}
          </button>
          {error && (
            <div style={{ marginTop:8, fontSize:12, color:"#B91C1C", textAlign:"center" }}>{error}</div>
          )}
        </div>)}
      </div>)}
    </div>
  );
}

/* ═══════════════════════════════════════════
   ABOUT PAGE
   ═══════════════════════════════════════════ */

function PrivacyPage({ setPage }) {
  return (
    <div style={{ maxWidth:680, margin:"0 auto", padding:"28px 20px 40px" }}>
      <h1 style={sectionTitle}>Privacy</h1>

      <div style={{ fontSize:13, color:"#9CA3AF", marginBottom:20 }}>
        Last reviewed: April 2026
      </div>

      <div style={{ fontSize:15, color:"#374151", lineHeight:1.8, marginBottom:24 }}>
        This page tells you, in plain language, exactly what data flows where when you use getmyrecords.org. We wrote it for survivors who need to know — not for lawyers.
      </div>

      <div style={{ ...cardSt, marginBottom:16 }}>
        <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>The short version</div>
        <ul style={{ margin:"0 0 0 18px", padding:0, fontSize:13, color:"#374151", lineHeight:1.8 }}>
          <li>We do not run a backend. There is no database. There are no user accounts. There are no logs.</li>
          <li>We do not use Google Analytics, Meta pixel, or any other third-party tracker. We do not set cookies.</li>
          <li>Anything you save (saved analyses, your case progress, your "Pattern" entries) stays in your browser, on your device. We never see it.</li>
          <li>The AI analyzer sends your files directly to Anthropic with your own API key. We never see your files or your analysis. (More on what Anthropic does below.)</li>
          <li>The "Found a problem?" feedback form sends your message to a relay service called Formspree, which forwards it to us. (More below.)</li>
          <li>The Quick Exit button in the top-right replaces the page with weather.com. The Esc key does the same. The visible content is wiped instantly, even before the new page finishes loading.</li>
        </ul>
      </div>

      <div style={{ ...cardSt, marginBottom:16 }}>
        <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>What stays in your browser</div>
        <p style={{ margin:"0 0 8px", fontSize:13, color:"#374151", lineHeight:1.7 }}>
          The site uses your browser's built-in storage (called <em>localStorage</em>) to remember things between visits. This stays on your device — it does not get sent to any server.
        </p>
        <p style={{ margin:"0 0 6px", fontSize:13, color:"#374151", lineHeight:1.7 }}>What's stored:</p>
        <ul style={{ margin:"0 0 0 18px", padding:0, fontSize:13, color:"#374151", lineHeight:1.7 }}>
          <li>Your "Build My Case" checklist progress</li>
          <li>Your "Pattern" entries (which agencies, what failures, what year)</li>
          <li>Any AI analyses you choose to save with the "Save to my device" button</li>
          <li>Your reading-mode preference (normal or larger text)</li>
        </ul>
        <p style={{ margin:"8px 0 0", fontSize:12, color:"#92400E", lineHeight:1.6 }}>
          <strong>Important:</strong> anyone with access to this device — a family member, an abuser, a roommate — can open this browser and see everything stored here. If you are not on a private device, do not save anything. Use the "Clear all data" option in About to wipe everything when you're done.
        </p>
      </div>

      <div style={{ ...cardSt, marginBottom:16 }}>
        <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>What Anthropic sees (the AI analyzer)</div>
        <p style={{ margin:"0 0 8px", fontSize:13, color:"#374151", lineHeight:1.7 }}>
          When you use the "Analyze My Records" tool, your files are sent directly from your browser to Anthropic — the company that makes the Claude AI model. We do not see, store, or proxy any of this. Your files never touch our servers.
        </p>
        <p style={{ margin:"0 0 6px", fontSize:13, color:"#374151", lineHeight:1.7 }}>What Anthropic has publicly committed to:</p>
        <ul style={{ margin:"0 0 8px 18px", padding:0, fontSize:13, color:"#374151", lineHeight:1.7 }}>
          <li>They <strong>do not train</strong> their AI on data you send through the API.</li>
          <li>They store API requests for <strong>up to 30 days</strong> for safety/abuse monitoring, then delete them.</li>
          <li>They are <strong>SOC 2 Type II</strong> certified.</li>
          <li>They are based in the United States and subject to US legal process.</li>
        </ul>
        <p style={{ margin:"0 0 8px", fontSize:13, color:"#374151", lineHeight:1.7 }}>
          What Anthropic <em>can</em> do: technically read what you send. The data is not encrypted in a way that prevents this. A subpoena could compel them to disclose data still in the 30-day window. They say they push back and notify users when legally allowed.
        </p>
        <p style={{ margin:"0", fontSize:12, color:"#374151", lineHeight:1.6 }}>
          Read their full privacy policy at <strong>anthropic.com/legal/privacy</strong>. Before using the analyzer with the most sensitive files, the warning on that page lists situations where you should not use any cloud AI tool — read it.
        </p>
      </div>

      <div style={{ ...cardSt, marginBottom:16 }}>
        <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>What Formspree sees (the feedback form)</div>
        <p style={{ margin:"0 0 8px", fontSize:13, color:"#374151", lineHeight:1.7 }}>
          The "Found a problem?" form at the bottom of pages uses a relay service called Formspree to forward your message to us. Formspree sees what you type into that form (issue type, page, details, optionally your email if you provide one) and forwards it to our inbox. They do not see anything else about your visit.
        </p>
        <p style={{ margin:"0", fontSize:13, color:"#374151", lineHeight:1.7 }}>
          We do not require an email. Leave it blank if you don't want a reply. The form is not a way to reach a human urgently — for crisis support, use 988 (call or text) or RAINN at 1-800-656-4673.
        </p>
      </div>

      <div style={{ ...cardSt, marginBottom:16 }}>
        <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>Quick Exit and your browser history</div>
        <p style={{ margin:"0", fontSize:13, color:"#374151", lineHeight:1.7 }}>
          The red "Quick exit" button (and the Esc key) replaces the page with weather.com. The current page is wiped from view immediately, even before the new page finishes loading. We chose weather.com on purpose: unlike google.com, it does not show personalized search history, account avatars, or anything else that could give away who you are. This hides what's currently on your screen, but it <strong>does not erase your browser history</strong>. If someone else uses your device and looks at your history or open tabs, they may still see that you visited getmyrecords.org. To prevent this, use a private browsing window (Incognito in Chrome, Private in Safari/Firefox) — and clear your history when you're done.
        </p>
      </div>

      <div style={{ ...cardSt, marginBottom:16 }}>
        <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>What we will never do</div>
        <ul style={{ margin:"0 0 0 18px", padding:0, fontSize:13, color:"#374151", lineHeight:1.7 }}>
          <li>Sell or share data — there is no data to sell.</li>
          <li>Add tracking, analytics, or third-party advertising.</li>
          <li>Run a database of survivors without telling you, in plain language, exactly how it works.</li>
          <li>Surprise you. If anything on this page changes, we'll update it and bump the date at the top.</li>
        </ul>
      </div>

      <button onClick={()=>setPage("about")} style={{
        width:"100%", padding:"12px", borderRadius:10, border:"1px solid #D1D5DB",
        backgroundColor:"#fff", color:"#374151",
        fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer",
      }}>← Back to About</button>
    </div>
  );
}

function AboutPage({ setPage }) {
  const [showCases, setShowCases] = useState(false);
  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
      <h1 style={sectionTitle}>About</h1>

      <div style={{ fontSize:15, color:"#374151", lineHeight:1.8, marginBottom:10 }}>
        getmyrecords.org is free. No ads. No tracking. No sign-up. No one collecting your information.
      </div>
      <div style={{ fontSize:15, color:"#374151", lineHeight:1.8, marginBottom:10 }}>
        This site exists because the systems that were supposed to protect children — CPS, DCF, foster care, courts, youth organizations — failed millions of people. And the tools to deal with that have been hidden behind legal jargon, confusing government websites, and expensive lawyers.
      </div>
      <div style={{ fontSize:15, color:"#374151", lineHeight:1.8, marginBottom:24 }}>
        We built this so you don't need a lawyer to request your own records, don't need a college degree to understand what's in them, and don't need to be alone in figuring out what to do next.
      </div>

      <div style={{ padding:14, borderRadius:10, backgroundColor:"#FFFBEB", border:"1px solid #FDE68A", marginBottom:24, fontSize:13, color:"#92400E", lineHeight:1.7 }}>
        <strong>This is a new project</strong> and we're still checking everything. The tools work, but the legal information — especially filing deadlines and state-specific data — should be verified with an attorney before you make decisions based on it. If you spot something that looks wrong, let us know.
      </div>

      <div style={cardSt}>
        <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:12 }}>The scale of this</div>
        {[
          ["Survivors of childhood sexual abuse in the US", "42 million"],
          ["Girls who experience it before age 18", "1 in 4"],
          ["Boys who experience it before age 18", "1 in 13"],
          ["Boy Scouts sexual abuse claims filed", "82,000+"],
          ["Catholic Church abuse claims in the US", "20,000+"],
          ["Lifetime economic cost per survivor (CDC)", "$9.3 billion"],
        ].map(([label, stat], i) => (
          <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", padding:"8px 0", borderTop: i > 0 ? "1px solid #F3F4F6" : "none" }}>
            <span style={{ fontSize:13, color:"#6B7280", flex:1, paddingRight:12 }}>{label}</span>
            <span style={{ fontFamily:serifFonts, fontSize:16, fontWeight:700, color:"#1a1a1a", whiteSpace:"nowrap" }}>{stat}</span>
          </div>
        ))}
        <div style={{ fontSize:10, color:"#9CA3AF", marginTop:8 }}>Sources: CDC, BSA bankruptcy proceedings, John Jay Report, USCCB audits</div>
      </div>

      <button onClick={()=>setShowCases(!showCases)} style={{
        width:"100%", padding:"14px 16px", borderRadius:10, border:"1px solid #E5E7EB",
        backgroundColor:"#fff", cursor:"pointer", textAlign:"left", marginTop:14, marginBottom:showCases?0:14,
        display:"flex", justifyContent:"space-between", alignItems:"center",
      }}>
        <span style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a" }}>Cases that changed the law</span>
        <span style={{ fontSize:16, color:"#9CA3AF", transform:showCases?"rotate(180deg)":"none", transition:"transform 0.2s" }}>▾</span>
      </button>

      {showCases && (<div>
        <div style={{ padding:12, borderRadius:"0 0 10px 10px", backgroundColor:"#FFFBEB", border:"1px solid #FDE68A", borderTop:"none", marginBottom:14, fontSize:12, color:"#92400E", lineHeight:1.6 }}>
          <strong>Content warning.</strong> The cases below describe child sexual abuse, institutional cover-ups, and system failures. The descriptions are factual and not graphic, but the subject matter is heavy. Take care of yourself. You can close this section at any time. If you need support: call or text <strong>988</strong>.
        </div>

        {[
          { title:"Boy Scouts of America — 82,000 claims", meta:"$2.46 billion settlement · Bankruptcy filed 2020 · Payments began 2026", body:"The largest child sexual abuse case in US history. Over 82,000 men filed claims alleging abuse by scout leaders spanning decades. The BSA's internal \"Perversion Files\" — released by court order in 2012 — showed the organization knew about abusers and failed to report them to police. The bankruptcy settlement created a trust fund paying survivors between $3,500 and $2.7 million depending on the severity and evidence. The case drove lookback window legislation in multiple states." },
          { title:"Catholic Church — decades of cover-ups", meta:"$4+ billion in settlements nationwide · 20,000+ claims", body:"Beginning with the Boston Globe's 2002 investigation (the \"Spotlight\" report), survivors revealed that the Catholic Church systematically transferred abusive priests between parishes instead of removing them — allowing them to abuse new victims. Dioceses across the country have filed bankruptcy to manage settlement costs. The Church's internal documents, obtained through litigation, showed decades of institutional knowledge and cover-up. These cases were the primary driver behind lookback window laws in New York, New Jersey, California, and many other states." },
          { title:"Larry Nassar / USA Gymnastics — 500+ victims", meta:"$380M from USA Gymnastics · $500M from Michigan State · FBI failures documented", body:"Team doctor Larry Nassar sexually abused over 500 gymnasts over two decades. USA Gymnastics, Michigan State University, and the FBI all had reports and failed to act. An inspector general report found that FBI agents made false statements and failed to investigate for over a year after being notified. Survivors' testimony to Congress in 2021 drove federal legislation and exposed how institutions protect their reputation over children." },
          { title:"Foster care system failures — nationwide", meta:"Class action lawsuits in 30+ states · Federal oversight in multiple states", body:"Child welfare agencies in over 30 states have faced class action lawsuits for failing to protect children in their care. In Connecticut, a woman filed a $30 million claim against DCF after the agency investigated her family 27 times over 12 years and still approved the man who was raping her as her legal guardian. In Texas, a federal judge found that foster children \"were more likely to be abused in the state's care than in the homes they were removed from.\" In many states, federal courts have placed child welfare systems under court supervision due to systemic failures." },
          { title:"Jeffrey Epstein — institutional complicity", meta:"Victims fund paid $150M+ · \"Virginia's Law\" introduced in Senate 2026", body:"The Epstein case exposed how wealth and institutional connections can shield abusers for decades. Despite a 2008 plea deal that many called a miscarriage of justice, survivors continued to fight — leading to his 2019 arrest, federal prosecution, and a victims' compensation fund. In 2026, Senator Schumer introduced \"Virginia's Law\" — named after survivor Virginia Giuffre — to eliminate the federal civil statute of limitations for sexual abuse and trafficking. The case fundamentally changed public understanding of how systems enable abuse." },
        ].map((c, i) => (
          <div key={i} style={{ ...cardSt, marginBottom:10 }}>
            <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:4 }}>{c.title}</div>
            <div style={{ fontSize:11, color:"#9CA3AF", marginBottom:8 }}>{c.meta}</div>
            <div style={{ fontSize:13, color:"#374151", lineHeight:1.7 }}>{c.body}</div>
          </div>
        ))}

        <div style={{ padding:14, borderRadius:10, backgroundColor:"#F5F3FF", border:"1px solid #DDD6FE", marginBottom:14, fontSize:13, color:"#5B21B6", lineHeight:1.7 }}>
          <strong>What these cases have in common:</strong> In every one, the institutions knew. They had reports, they had evidence, they had warnings — and they chose to protect themselves instead of the children in their care. The records that proved it existed the whole time. Getting those records was the first step in every case.
        </div>
      </div>)}

      <div style={{ ...cardSt, marginTop:10 }}>
        <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:6 }}>What we don't do</div>
        <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.7 }}>
          We don't collect personal information. We don't track users. We don't serve ads. We don't sell data. We don't store identifying information on any server. We don't replace attorneys, therapists, or crisis services. We don't pressure anyone to take action before they're ready.
        </div>
      </div>

      <div style={{ ...cardSt, marginTop:10 }}>
        <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:6 }}>What we do</div>
        <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.7 }}>
          We give people tools to understand what happened to them, document it, request the records that prove it, know their legal rights, see that they're not alone, and connect with others who share their experience — all for free, on their own terms, at their own pace.
        </div>
      </div>

      <div style={{ ...cardSt, marginTop:10 }}>
        <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#1a1a1a", marginBottom:6 }}>Open source</div>
        <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.7 }}>
          The code is open. The data is anonymized. Anyone can audit it. Anyone can contribute. No one owns it. If this project disappeared tomorrow, every tool in it could be rebuilt from the public source code.
        </div>
      </div>

      <button onClick={()=>setPage("privacy")} style={{
        width:"100%", marginTop:10, padding:"12px", borderRadius:10,
        border:"1px solid #E5E7EB", backgroundColor:"#fff",
        color:"#374151", fontFamily:fonts, fontSize:13, fontWeight:600,
        cursor:"pointer", textAlign:"left",
      }}>
        Privacy details — what gets sent where →
      </button>

      <FeedbackForm />

      <div style={{ marginTop:16, padding:16, borderRadius:10, border:"1px solid #E5E7EB", backgroundColor:"#fff", textAlign:"center" }}>
        <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.7, marginBottom:10 }}>
          This project is free and always will be. No ads, no sponsors, no data sales. If it helped you and you're in a position to, you can help keep it running.
        </div>
        <a href="https://ko-fi.com/nickashe" target="_blank" rel="noopener noreferrer" style={{
          display:"inline-block", padding:"10px 20px", borderRadius:8, border:"1px solid #E5E7EB",
          backgroundColor:"#FAFAF9", color:"#374151", fontFamily:fonts, fontSize:13, fontWeight:600,
          textDecoration:"none", cursor:"pointer",
        }}>
          Support this project →
        </a>
      </div>

      <SafetyCard />
    </div>
  );
}

/* ═══════════════════════════════════════════
   HIDE YOURSELF — Privacy & Identity Protection
   Update HIDE_DATA_LAST_REVIEWED whenever you re-check opt-out URLs.
   ═══════════════════════════════════════════ */

const HIDE_DATA_LAST_REVIEWED = "April 2026";

const HIDE_TIER_1 = [
  {
    id: "acp",
    title: "Apply for your state's Address Confidentiality Program",
    why: "An ACP gives you a substitute mailing address that hides your real one from public records, voter rolls, court filings, and the DMV. Free in every state. Most require a confirmed history of domestic violence, sexual assault, or stalking — your local DV agency can usually help you apply same-day.",
    url: "https://www.womenslaw.org/about-abuse/safety-tips/safety-while-using-internet-and-technology",
    timeEst: "Hours to days",
    requiresId: true,
    recurring: false,
    note: "Application paths vary by state — search '[your state] address confidentiality program' or call your local DV agency.",
  },
  {
    id: "freeze-equifax",
    title: "Freeze your credit at Equifax",
    why: "A credit freeze prevents anyone — including an abuser — from opening new accounts in your name. Free, doesn't affect your credit score, and you can lift it temporarily if you need credit yourself.",
    url: "https://www.equifax.com/personal/credit-report-services/credit-freeze/",
    timeEst: "5-10 min",
    requiresId: true,
    recurring: false,
  },
  {
    id: "freeze-experian",
    title: "Freeze your credit at Experian",
    why: "Same as above — must be done at all three bureaus to be effective.",
    url: "https://www.experian.com/freeze/center.html",
    timeEst: "5-10 min",
    requiresId: true,
    recurring: false,
  },
  {
    id: "freeze-transunion",
    title: "Freeze your credit at TransUnion",
    why: "The third bureau. All three need to be frozen to fully block new accounts.",
    url: "https://www.transunion.com/credit-freeze",
    timeEst: "5-10 min",
    requiresId: true,
    recurring: false,
  },
  {
    id: "truepeoplesearch",
    title: "Remove yourself from TruePeopleSearch",
    why: "TruePeopleSearch is the most-Googled free people-search site. It typically lists your current address, phone number, and relatives. Often the first place an abuser looks.",
    url: "https://www.truepeoplesearch.com/removal",
    timeEst: "1-2 min",
    requiresId: false,
    recurring: true,
  },
  {
    id: "fastpeoplesearch",
    title: "Remove yourself from FastPeopleSearch",
    why: "Same data as TruePeopleSearch under a different brand. Easy opt-out, no ID required.",
    url: "https://www.fastpeoplesearch.com/removal",
    timeEst: "1-2 min",
    requiresId: false,
    recurring: true,
  },
  {
    id: "whitepages",
    title: "Suppress your Whitepages listing",
    why: "Whitepages has been around for decades and is heavily indexed by Google. They have a free suppression form (don't pay for premium).",
    url: "https://www.whitepages.com/suppression_requests",
    timeEst: "2-3 min",
    requiresId: false,
    recurring: true,
  },
  {
    id: "google-voice",
    title: "Set up a free Google Voice number",
    why: "Stop giving out your real phone number. A Google Voice number forwards calls and texts to your real phone but is what you give to schools, doctors, websites, and anyone you don't fully trust.",
    url: "https://voice.google.com",
    timeEst: "5 min",
    requiresId: false,
    recurring: false,
    note: "Requires a Google account. Hushed and TextNow are alternatives if you don't want to give Google your number.",
  },
];

// URL updates cross-referenced against privacyguides.org/en/data-broker-removals
// (community-maintained; their PR #3078 caught a PeekYou URL rot in July 2025).
const HIDE_TIER_2 = [
  { id: "spokeo",        title: "Spokeo opt-out",                    url: "https://www.spokeo.com/optout",                                       timeEst: "3-5 min",  requiresId: false, recurring: true,  why: "Aggregates from many sources. Often shows email addresses and social profiles. Search yourself at spokeo.com/search first to confirm you're listed." },
  { id: "beenverified",  title: "BeenVerified opt-out",              url: "https://www.beenverified.com/app/optout/search",                      timeEst: "3-5 min",  requiresId: false, recurring: true,  why: "Background-check site. Often picked up by Google search results." },
  { id: "mylife",        title: "MyLife opt-out",                    url: "https://www.mylife.com/privacyrequest",                               timeEst: "10+ min",  requiresId: true,  recurring: true,  why: "Notoriously aggressive. May take weeks. Worth the effort — they fabricate \"reputation scores\" that can be embarrassing." },
  { id: "radaris",       title: "Radaris opt-out",                   url: "https://radaris.com/control-privacy",                                 timeEst: "5 min",    requiresId: false, recurring: true,  why: "Aggregates public records and social media. Persistent — comes back if you don't re-check." },
  { id: "intelius",      title: "Intelius / PeopleConnect suppression (covers Intelius, ZabaSearch, US Search, Public Records 360, Instant Checkmate, TruthFinder)", url: "https://suppression.peopleconnect.us/login", timeEst: "5-10 min", requiresId: true, recurring: true, why: "PeopleConnect owns Intelius, ZabaSearch, US Search, Public Records 360, Instant Checkmate, and TruthFinder. One central suppression portal covers all of them. Flagged as \"high priority\" by Privacy Guides because automated removal services don't handle it." },
  { id: "thatsthem",     title: "ThatsThem opt-out",                 url: "https://thatsthem.com/optout",                                        timeEst: "3 min",    requiresId: false, recurring: true,  why: "Includes email and IP address data. Free opt-out form." },
  { id: "pipl",          title: "Pipl removal request",              url: "https://pipl.com/personal-information-removal-request",               timeEst: "5 min",    requiresId: false, recurring: true,  why: "Used by investigators and recruiters. Removal works but is manual." },
  { id: "familytreenow", title: "FamilyTreeNow opt-out",             url: "https://www.familytreenow.com/optout",                                timeEst: "3 min",    requiresId: false, recurring: true,  why: "Maps your relatives — particularly dangerous because abusers can use it to find you through family members." },
  { id: "peekyou",       title: "PeekYou — look up via CA Data Broker Registry",                     url: "https://oag.ca.gov/data-brokers",                                    timeEst: "15 min",   requiresId: false, recurring: true,  why: "PeekYou has intentionally removed their opt-out page and their contact form. They cannot be opted out via a normal form anymore. The legitimate path: California requires data brokers to register with the state Attorney General and provide working contact info — search the CA registry for \"PeekYou\" to find their current required deletion contact. Email them a CCPA / state-privacy-law deletion request. If they don't respond within 45 days, file a complaint with your state AG and the California AG (oag.ca.gov/contact/consumer-complaint-against-business-or-company)." },
  { id: "advbackground", title: "Advanced Background Checks opt-out", url: "https://www.advancedbackgroundchecks.com/removal",                   timeEst: "3 min",    requiresId: false, recurring: true,  why: "Background-check aggregator. Search yourself at advancedbackgroundchecks.com first to get your record URL, then use the removal form." },
  { id: "checkpeople",   title: "CheckPeople opt-out",               url: "https://checkpeople.com/do-not-sell-info",                            timeEst: "3 min",    requiresId: false, recurring: true,  why: "Select the \"Remove Record\" option on the form." },
  { id: "clustrmaps",    title: "ClustrMaps opt-out",                url: "https://clustrmaps.com/bl/opt-out",                                   timeEst: "3 min",    requiresId: false, recurring: true,  why: "Maps your address history visually on a map — particularly dangerous for stalking. Search clustrmaps.com first to confirm you're listed." },
  { id: "infotracer",    title: "InfoTracer opt-out",                url: "https://infotracer.com/optout/",                                      timeEst: "5 min",    requiresId: false, recurring: true,  why: "Search yourself at infotracer.com first to find your record, then submit the opt-out form." },
  { id: "publicdatausa", title: "PublicDataUSA opt-out",             url: "https://publicdatausa.com/remove.php",                                timeEst: "3 min",    requiresId: false, recurring: true,  why: "Search publicdatausa.com first to confirm your listing." },
  { id: "usphonebook",   title: "USPhonebook opt-out",               url: "https://www.usphonebook.com/opt-out",                                 timeEst: "3 min",    requiresId: false, recurring: true,  why: "Reverse phone lookup and address aggregator. Combined search-and-opt-out on the same page." },
];

const REMOVAL_SERVICES = [
  {
    id: "optery-free",
    name: "Optery — free tier",
    price: "$0",
    coverage: "Exposure scan across 100+ brokers + DIY removal guides",
    bestFor: "The right starting point for almost everyone. See exactly where you appear with no payment required, then decide if you want to do removals yourself or upgrade.",
    url: "https://www.optery.com/",
    badge: "Start here",
  },
  {
    id: "optery-paid",
    name: "Optery — paid",
    price: "~$4–25 / month",
    coverage: "Continuous automated removal across 100+ brokers, transparent dashboard",
    bestFor: "Automation with the most visible reporting. You can see what's been removed and what's still pending.",
    url: "https://www.optery.com/pricing/",
  },
  {
    id: "easyoptouts",
    name: "EasyOptOuts",
    price: "~$20 / year",
    coverage: "One-time batch across ~30 easy brokers",
    bestFor: "Cheapest serious option. Best for a baseline annual cleanup, not continuous monitoring.",
    url: "https://easyoptouts.com/",
  },
  {
    id: "deleteme",
    name: "DeleteMe (Abine)",
    price: "~$129 / year",
    coverage: "Continuous removal across ~30+ brokers, quarterly cycles",
    bestFor: "The oldest and most established service. Best track record, conservative scope.",
    url: "https://joindeleteme.com/",
  },
  {
    id: "incogni",
    name: "Incogni (Surfshark)",
    price: "~$77–99 / year",
    coverage: "Continuous removal, claims 180+ brokers",
    bestFor: "Largest claimed coverage. Aggressive marketing but a legitimate company (owned by Surfshark / Nord Security).",
    url: "https://incogni.com/",
  },
  {
    id: "privacyduck",
    name: "PrivacyDuck",
    price: "~$500+ / year",
    coverage: "Concierge / white-glove service with personal account manager",
    bestFor: "Higher-stakes situations where you need a human handling escalations and edge cases.",
    url: "https://privacyduck.com/",
  },
];

const HIDE_TIER_3 = [
  { id: "lexisnexis", title: "LexisNexis Consumer Disclosure / opt-out", url: "https://consumer.risk.lexisnexis.com/request",               timeEst: "30+ min",  requiresId: true, recurring: false, why: "The largest commercial data wholesaler. Used by insurance companies, landlords, and law enforcement. Removal is multi-step, partial, and slow — but worth doing." },
  { id: "acxiom",     title: "Acxiom opt-out",                            url: "https://isapps.acxiom.com/optout/optout.aspx",               timeEst: "10 min",   requiresId: false, recurring: true, why: "A major B2B data broker. Sells to advertisers and other brokers. Opt-out applies only to their marketing data." },
  { id: "google-removal", title: "Google personal-information removal request", url: "https://support.google.com/websearch/answer/9673730", timeEst: "varies",   requiresId: false, recurring: true, why: "Google has a process to remove specific personal info (home address, phone, email, ID numbers) from search results. Doesn't remove the source page, just the Google listing." },
  { id: "wayback",    title: "Wayback Machine removal",                   url: "https://help.archive.org/help/how-do-i-request-to-remove-something-from-archive-org/", timeEst: "varies", requiresId: false, recurring: false, why: "If old versions of web pages with your info are archived on archive.org, you can email info@archive.org to request removal." },
];

function RemovalServicesCard() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      marginBottom:20, borderRadius:10, border:"1px solid #DDD6FE",
      backgroundColor:"#F5F3FF", overflow:"hidden",
    }}>
      <button onClick={()=>setOpen(o=>!o)} style={{
        width:"100%", padding:"14px", border:"none", backgroundColor:"transparent",
        textAlign:"left", cursor:"pointer",
        display:"flex", justifyContent:"space-between", alignItems:"center", gap:8,
      }}>
        <div>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#5B21B6", marginBottom:2 }}>
            Want this done for you? Compare automated removal services
          </div>
          <div style={{ fontSize:12, color:"#7C3AED" }}>
            Free and paid tools that handle the manual checklist below for you. Optery's free tier is a good starting point.
          </div>
        </div>
        <span style={{ fontSize:14, color:"#7C3AED", transform: open ? "rotate(180deg)" : "none", transition:"transform 0.15s" }}>▾</span>
      </button>

      {open && (
        <div style={{ padding:"0 14px 14px" }}>
          <div style={{
            padding:"10px 12px", borderRadius:8, backgroundColor:"#FEF2F2",
            border:"1px solid #FECACA", color:"#991B1B", fontSize:12, lineHeight:1.6, marginBottom:12,
          }}>
            <strong>Trust tradeoff:</strong> to remove you from data brokers, these services need your full name, addresses, phone numbers, email aliases, and sometimes DOB. Stick with established companies. Be cautious of newer "removal services" you find via SEO articles or AI-generated recommendations.
          </div>

          {REMOVAL_SERVICES.map(svc => (
            <div key={svc.id} style={{
              padding:"12px", borderRadius:8, border: svc.badge ? "1.5px solid #7C3AED" : "1px solid #E5E7EB",
              backgroundColor:"#fff", marginBottom:8,
            }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, marginBottom:4 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                  <span style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1a1a1a" }}>{svc.name}</span>
                  {svc.badge && (
                    <span style={{
                      fontSize:9, fontWeight:700, padding:"2px 6px", borderRadius:4,
                      backgroundColor:"#7C3AED", color:"#fff",
                      textTransform:"uppercase", letterSpacing:0.5,
                    }}>{svc.badge}</span>
                  )}
                </div>
                <a href={svc.url} target="_blank" rel="noopener noreferrer" style={{
                  flex:"0 0 auto", padding:"5px 10px", borderRadius:6,
                  border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#374151",
                  fontFamily:fonts, fontSize:11, fontWeight:600, textDecoration:"none",
                  whiteSpace:"nowrap",
                }}>Open ↗</a>
              </div>
              <div style={{ fontSize:11, fontWeight:600, color:"#5B21B6", marginBottom:4 }}>{svc.price}</div>
              <div style={{ fontSize:12, color:"#374151", marginBottom:4 }}><strong>Covers:</strong> {svc.coverage}</div>
              <div style={{ fontSize:12, color:"#6B7280", lineHeight:1.5 }}>{svc.bestFor}</div>
            </div>
          ))}

          <div style={{ fontSize:11, color:"#6B7280", lineHeight:1.5, marginTop:8 }}>
            <strong>What none of these can do:</strong> remove government records (court records, voter rolls, property records, professional licenses) or handle brokers who deliberately break their opt-outs (like PeekYou). For those, you still need the manual steps below — the Address Confidentiality Program, CCPA requests, and state AG complaints.
          </div>
        </div>
      )}
    </div>
  );
}

function HideTierItem({ item, index }) {
  return (
    <div style={{
      padding:"14px", borderRadius:10, border:"1px solid #E5E7EB",
      backgroundColor:"#fff", marginBottom:10,
    }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10, marginBottom:6 }}>
        <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1a1a1a", flex:1 }}>
          <span style={{ color:"#9CA3AF", marginRight:6 }}>{index + 1}.</span>
          {item.title}
        </div>
        <a href={item.url} target="_blank" rel="noopener noreferrer" style={{
          flex:"0 0 auto", padding:"6px 12px", borderRadius:6,
          border:"1px solid #1a1a1a", backgroundColor:"#1a1a1a", color:"#fff",
          fontFamily:fonts, fontSize:11, fontWeight:600, textDecoration:"none",
          whiteSpace:"nowrap",
        }}>Open ↗</a>
      </div>
      <div style={{ fontSize:12, color:"#6B7280", lineHeight:1.6, marginBottom:8 }}>{item.why}</div>
      {item.note && (
        <div style={{ fontSize:11, color:"#92400E", marginBottom:8, fontStyle:"italic" }}>{item.note}</div>
      )}
      <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
        <span style={{ fontSize:10, fontWeight:600, padding:"2px 6px", borderRadius:4, backgroundColor:"#F3F4F6", color:"#6B7280" }}>
          ⏱ {item.timeEst}
        </span>
        {item.requiresId && (
          <span style={{ fontSize:10, fontWeight:600, padding:"2px 6px", borderRadius:4, backgroundColor:"#FEF3C7", color:"#92400E", border:"1px solid #FDE68A" }}>
            ID required
          </span>
        )}
        {item.recurring && (
          <span style={{ fontSize:10, fontWeight:600, padding:"2px 6px", borderRadius:4, backgroundColor:"#EFF6FF", color:"#1E40AF", border:"1px solid #BFDBFE" }}>
            Re-check quarterly
          </span>
        )}
      </div>
    </div>
  );
}

function HideYourselfPage({ setPage }) {
  const [tier, setTier] = useState(1);
  const [checked, setChecked] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await storage.get("gyr-hide-checks");
        if (r?.value) setChecked(JSON.parse(r.value));
      } catch (e) {}
      setLoaded(true);
    })();
  }, []);

  const toggleCheck = async (id) => {
    const next = { ...checked, [id]: !checked[id] };
    setChecked(next);
    try { await storage.set("gyr-hide-checks", JSON.stringify(next)); } catch (e) {}
  };

  const tiers = [
    { n: 1, label: "Urgent (24 hours)", color:"#DC2626", bg:"#FEF2F2", items: HIDE_TIER_1 },
    { n: 2, label: "Within a week",     color:"#D97706", bg:"#FFFBEB", items: HIDE_TIER_2 },
    { n: 3, label: "Long-term",         color:"#2563EB", bg:"#EFF6FF", items: HIDE_TIER_3 },
  ];
  const current = tiers.find(t => t.n === tier);

  if (!loaded) return <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px", fontFamily:fonts, color:"#9CA3AF" }}>Loading...</div>;

  return (
    <div style={{ maxWidth:680, margin:"0 auto", padding:"28px 20px 40px" }}>
      <h1 style={sectionTitle}>Hide Yourself</h1>
      <p style={{ fontSize:14, color:"#6B7280", lineHeight:1.7, marginBottom:16 }}>
        A guided checklist for removing your name, address, and contact info from the public records and people-search sites that abusers use to find people. Free. Does not require any specialized tools.
      </p>

      <div style={{ padding:14, borderRadius:10, backgroundColor:"#FEF2F2", border:"1px solid #FECACA", marginBottom:16, fontSize:13, color:"#991B1B", lineHeight:1.6 }}>
        <strong>Read this first.</strong> This is a free DIY guide for survivors. It is <em>not</em> a substitute for professional safety planning if your life is in immediate danger. If you are being actively pursued by someone with significant resources (a stalker, an organized abuser, a state actor), you also need a professional and probably a lawyer. Call the <strong>National Domestic Violence Hotline at 1-800-799-7233</strong> for help building a safety plan with a real person.
      </div>

      <RemovalServicesCard />

      <div style={{ padding:12, borderRadius:10, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", marginBottom:20, fontSize:12, color:"#065F46", lineHeight:1.6 }}>
        Your checklist progress is saved on this device only. Nothing is uploaded. You can close this page and come back — your checks will still be here. Anyone with access to this device can also see them, so use the "Clear all data" option in About if you need to wipe it.
      </div>

      <div style={{ display:"flex", gap:6, marginBottom:20 }}>
        {tiers.map(t => (
          <button key={t.n} onClick={()=>setTier(t.n)} style={{
            flex:1, padding:"10px 6px", borderRadius:8,
            border:`1.5px solid ${tier===t.n?t.color:"#E5E7EB"}`,
            backgroundColor: tier===t.n ? t.bg : "#fff",
            color: tier===t.n ? t.color : "#6B7280",
            fontFamily:fonts, fontSize:11, fontWeight:700, cursor:"pointer",
            display:"flex", flexDirection:"column", alignItems:"center", gap:2,
          }}>
            <span style={{ fontSize:14 }}>Tier {t.n}</span>
            <span style={{ fontSize:10, fontWeight:500 }}>{t.label}</span>
          </button>
        ))}
      </div>

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:10 }}>
        <div style={{ fontFamily:fonts, fontSize:12, fontWeight:700, color:current.color, textTransform:"uppercase", letterSpacing:0.5 }}>
          {current.label}
        </div>
        <div style={{ fontSize:11, color:"#9CA3AF" }}>
          {current.items.filter(i => checked[i.id]).length} / {current.items.length} done
        </div>
      </div>

      {current.items.map((item, i) => (
        <div key={item.id} style={{ position:"relative" }}>
          <input
            type="checkbox"
            checked={!!checked[item.id]}
            onChange={()=>toggleCheck(item.id)}
            id={`hide-${item.id}`}
            style={{
              position:"absolute", top:14, left:-26, width:18, height:18, cursor:"pointer",
            }}
          />
          <HideTierItem item={item} index={i} />
        </div>
      ))}

      <div style={{ marginTop:24, padding:14, borderRadius:10, backgroundColor:"#F8FAFC", border:"1px solid #E2E8F0", fontSize:12, color:"#475569", lineHeight:1.6 }}>
        <strong>A few things to know:</strong>
        <ul style={{ margin:"6px 0 0 18px", padding:0 }}>
          <li>Opt-out URLs change. We last reviewed these in <strong>{HIDE_DATA_LAST_REVIEWED}</strong>. If a link is broken, search "[site name] opt out".</li>
          <li>Some opt-outs require uploading a photo ID, which is a privacy concern in itself. Those are flagged with an "ID required" badge above. You can usually black out everything except your name and address before uploading.</li>
          <li>Most people-search sites want you to find your own record first — search the site for your name, copy the URL of your listing, and paste it into the opt-out form.</li>
          <li>Many of these re-add you within months. Items marked "Re-check quarterly" need to be re-done — set a calendar reminder.</li>
          <li>Removing yourself from one site does not remove you from the others. They source data independently.</li>
          <li>If you'd rather have this done for you, see the <strong>"Want this done for you?"</strong> card at the top of this page for a comparison of free and paid removal services.</li>
          <li><strong>When a broker's opt-out is broken on purpose</strong> (PeekYou is the worst example, but they're not the only one): California's Data Broker Registry at <strong>oag.ca.gov/data-brokers</strong> requires every registered broker to keep working contact info on file. Search it for the broker's name to find a deletion contact they are legally required to honor. If they ignore you for 45 days, file a complaint with your state Attorney General and the California AG.</li>
        </ul>
      </div>

      <div style={{ marginTop:16, padding:14, borderRadius:10, backgroundColor:"#fff", border:"1px solid #E5E7EB", fontSize:12, color:"#374151", lineHeight:1.7 }}>
        <strong>Further reading</strong>
        <ul style={{ margin:"8px 0 0 18px", padding:0 }}>
          <li>
            <a href="https://www.techsafety.org/resources-survivors" target="_blank" rel="noopener noreferrer" style={{ color:"#1E40AF", textDecoration:"underline" }}>NNEDV Safety Net — Tech Safety for Survivors</a>
            {" "}— the most authoritative survivor-specific resource. Written by and for DV/SA advocates.
          </li>
          <li>
            <a href="https://www.privacyguides.org/en/data-broker-removals/" target="_blank" rel="noopener noreferrer" style={{ color:"#1E40AF", textDecoration:"underline" }}>Privacy Guides — Data Broker Removals</a>
            {" "}— community-maintained opt-out list. We cross-reference URLs against theirs when we review this page.
          </li>
          <li>
            <a href="https://www.privacyguides.org/" target="_blank" rel="noopener noreferrer" style={{ color:"#1E40AF", textDecoration:"underline" }}>Privacy Guides — general privacy tools</a>
            {" "}— vetted recommendations for browsers, VPNs, email, password managers. Useful if you also want to harden your day-to-day devices.
          </li>
          <li>
            <a href="https://inteltechniques.com/workbook.html" target="_blank" rel="noopener noreferrer" style={{ color:"#1E40AF", textDecoration:"underline" }}>Inteltechniques Personal Data Removal Workbook</a>
            {" "}— free, comprehensive, but written in OSINT-investigator language. Dense reading.
          </li>
          <li>
            <strong>National Domestic Violence Hotline</strong> — 1-800-799-7233 (call or text START to 88788). For human safety planning, not DIY.
          </li>
        </ul>
      </div>

      <button onClick={()=>setPage("contribute")} style={{
        width:"100%", marginTop:16, padding:"12px", borderRadius:10, border:"1px solid #BFDBFE",
        backgroundColor:"#EFF6FF", color:"#1E40AF",
        fontFamily:fonts, fontSize:12, fontWeight:600, cursor:"pointer",
      }}>Found a broken link or a site we're missing? Help us fix it →</button>

      <button onClick={()=>setPage("home")} style={{
        width:"100%", marginTop:8, padding:"12px", borderRadius:10, border:"1px solid #D1D5DB",
        backgroundColor:"#fff", color:"#374151",
        fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer",
      }}>← Back to home</button>
    </div>
  );
}

/* ═══════════════════════════════════════════
   CRISIS PAGE
   ═══════════════════════════════════════════ */

function CrisisPage() {
  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
      <h1 style={{ ...sectionTitle, color:"#991B1B" }}>You deserve to be safe.</h1>
      <div style={{ ...bodyText, marginBottom:24 }}>
        <p><strong>If you're in immediate danger, call 911.</strong> You don't need permission. You don't need to explain everything.</p>
      </div>
      {[
        { name:"Childhelp National Abuse Hotline", contact:"1-800-422-4453", desc:"24/7. For kids, teens, and adults. They listen." },
        { name:"RAINN Sexual Assault Hotline", contact:"1-800-656-4673", desc:"24/7. Confidential. Chat at rainn.org" },
        { name:"988 Suicide & Crisis Lifeline", contact:"Call or text 988", desc:"24/7. For anyone in crisis." },
        { name:"Crisis Text Line", contact:"Text HOME to 741741", desc:"24/7. If you'd rather text than talk." },
      ].map((r,i) => (
        <div key={i} style={{ ...cardSt }}>
          <div style={{ fontSize:15, fontWeight:700, color:"#1a1a1a", marginBottom:4 }}>{r.name}</div>
          <div style={{ fontSize:18, fontWeight:700, color:"#2563EB", marginBottom:4, letterSpacing:"0.01em" }}>{r.contact}</div>
          <div style={{ fontSize:13, color:"#6B7280" }}>{r.desc}</div>
        </div>
      ))}
      <div style={{ ...cardSt, backgroundColor:"#FEF2F2", borderColor:"#FECACA" }}>
        <div style={{ fontSize:14, fontWeight:700, color:"#991B1B", marginBottom:8 }}>If you're a kid or teen reading this</div>
        <p style={{ fontSize:14, color:"#991B1B", lineHeight:1.7, margin:0 }}>
          What's happening to you is not your fault. It doesn't matter what anyone told you. There are people whose entire job is to help you. Call any number above. You don't have to give your name. You can hang up whenever you want. But please call.
        </p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   WRITE YOUR STORY
   ═══════════════════════════════════════════ */

function StoryPage() {
  const [story, setStory] = useState({ title:"", body:"", mood:"", private:true });
  const [saved, setSaved] = useState(false);
  const [entries, setEntries] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await storage.get("gyr-stories");
        if (r?.value) setEntries(JSON.parse(r.value));
      } catch(e) {}
      setLoaded(true);
    })();
  }, []);

  const saveStory = async () => {
    if (!story.body.trim()) return;
    const entry = {
      id: Date.now().toString(36) + Math.random().toString(36).substr(2,5),
      title: story.title || "Untitled",
      body: story.body,
      mood: story.mood,
      private: story.private,
      date: new Date().toISOString(),
    };
    const updated = [entry, ...entries];
    setEntries(updated);
    try { await storage.set("gyr-stories", JSON.stringify(updated)); } catch(e){}
    setStory({ title:"", body:"", mood:"", private:true });
    setSaved(true);
    setTimeout(()=>setSaved(false), 3000);
  };

  const deleteEntry = async (id) => {
    const updated = entries.filter(e=>e.id!==id);
    setEntries(updated);
    try { await storage.set("gyr-stories", JSON.stringify(updated)); } catch(e){}
    setViewing(null);
  };

  const exportEntry = (entry) => {
    const text = `${entry.title}\n${new Date(entry.date).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}\n\n${entry.body}`;
    const b = new Blob([text], {type:"text/plain"});
    const u = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = u;
    a.download = `${entry.title.replace(/[^a-zA-Z0-9]/g,"-")}.txt`;
    a.click();
    URL.revokeObjectURL(u);
  };

  const exportAll = () => {
    const text = entries.map(e => `${"=".repeat(60)}\n${e.title}\n${new Date(e.date).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}\n${"=".repeat(60)}\n\n${e.body}\n`).join("\n\n");
    const b = new Blob([text], {type:"text/plain"});
    const u = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = u;
    a.download = "my-story.txt";
    a.click();
    URL.revokeObjectURL(u);
  };

  if (!loaded) return <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px", fontFamily:fonts, color:"#9CA3AF" }}>Loading...</div>;

  // Viewing a single entry
  if (viewing) {
    const entry = entries.find(e=>e.id===viewing);
    if (!entry) return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
        <p style={{ fontFamily:fonts, color:"#9CA3AF" }}>Entry not found.</p>
        <button onClick={()=>setViewing(null)} style={{ padding:"8px 16px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#6B7280", fontFamily:fonts, fontSize:13, cursor:"pointer" }}>← Back</button>
      </div>
    );
    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
        <button onClick={()=>setViewing(null)} style={{ marginBottom:16, padding:"8px 16px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#6B7280", fontFamily:fonts, fontSize:13, cursor:"pointer" }}>← Back</button>
        <h1 style={{ fontFamily:serifFonts, fontSize:22, fontWeight:700, color:"#1a1a1a", marginBottom:4 }}>{entry.title}</h1>
        <div style={{ fontSize:12, color:"#9CA3AF", marginBottom:20 }}>{new Date(entry.date).toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}{entry.mood ? ` · ${entry.mood}` : ""}</div>
        <div style={{ fontSize:14, color:"#374151", lineHeight:1.9, whiteSpace:"pre-wrap", marginBottom:24 }}>{entry.body}</div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={()=>exportEntry(entry)} style={{ flex:1, padding:"10px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#374151", fontFamily:fonts, fontSize:13, cursor:"pointer" }}>Download</button>
          <button onClick={()=>{if(window.confirm("Delete this entry? This can't be undone."))deleteEntry(entry.id);}} style={{ padding:"10px 16px", borderRadius:8, border:"1px solid #FECACA", backgroundColor:"#FEF2F2", color:"#991B1B", fontFamily:fonts, fontSize:13, cursor:"pointer" }}>Delete</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
      <h1 style={sectionTitle}>Write Your Story</h1>
      <p style={{ fontSize:14, color:"#6B7280", lineHeight:1.7, marginBottom:20 }}>
        Your records tell the system's version. This is where you write yours. The context they left out, the things they got wrong, what it was actually like. Private by default. Yours to keep.
      </p>

      <div style={{ padding:14, borderRadius:10, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", marginBottom:20, fontSize:12, color:"#065F46", lineHeight:1.6 }}>
        Everything you write here is saved on your device only. It's not uploaded, not shared, not visible to anyone but you. You can export it as a file to keep, share with a therapist, or bring to a lawyer.
      </div>

      <div style={{ marginBottom:12 }}>
        <label style={labelSt}>Title (optional)</label>
        <input value={story.title} onChange={e=>setStory(p=>({...p,title:e.target.value}))} placeholder="e.g., What I remember about 2004, or The first time I read my file" style={inputSt} />
      </div>

      <div style={{ marginBottom:12 }}>
        <label style={labelSt}>How are you feeling right now? (optional)</label>
        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
          {["Angry","Sad","Numb","Relieved","Confused","Determined","Scared","Hopeful","Overwhelmed"].map(m => (
            <button key={m} onClick={()=>setStory(p=>({...p,mood:p.mood===m?"":m}))} style={{
              padding:"6px 12px", borderRadius:20, fontSize:12, fontFamily:fonts, cursor:"pointer",
              border:`1px solid ${story.mood===m?"#7C3AED":"#E5E7EB"}`,
              backgroundColor:story.mood===m?"#F5F3FF":"#fff",
              color:story.mood===m?"#7C3AED":"#6B7280",
            }}>{m}</button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom:16 }}>
        <label style={labelSt}>Write</label>
        <textarea value={story.body} onChange={e=>setStory(p=>({...p,body:e.target.value}))} placeholder="Write whatever you need to write. There are no rules here. You can respond to what you read in your records, correct what the caseworker got wrong, describe what was really happening, or just say what you need to say. This space is yours." rows={10} style={{...inputSt, resize:"vertical", lineHeight:1.8, fontSize:14}} />
      </div>

      {saved && <div style={{ padding:10, borderRadius:8, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", fontSize:13, color:"#065F46", marginBottom:12, textAlign:"center" }}>Saved.</div>}

      <button onClick={saveStory} disabled={!story.body.trim()} style={{
        width:"100%", padding:"14px", borderRadius:10, border:"none",
        backgroundColor:story.body.trim()?"#1a1a1a":"#D1D5DB",
        color:"#fff", fontFamily:fonts, fontSize:15, fontWeight:600,
        cursor:story.body.trim()?"pointer":"default",
      }}>
        Save Entry
      </button>

      {entries.length > 0 && (
        <div style={{ marginTop:32 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div style={{ fontFamily:fonts, fontSize:11, fontWeight:600, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:"0.08em" }}>
              Your entries ({entries.length})
            </div>
            {entries.length > 1 && (
              <button onClick={exportAll} style={{ padding:"4px 10px", borderRadius:6, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#6B7280", fontFamily:fonts, fontSize:11, cursor:"pointer" }}>
                Export all
              </button>
            )}
          </div>
          {entries.map(entry => (
            <button key={entry.id} onClick={()=>setViewing(entry.id)} style={{
              width:"100%", padding:"12px 14px", borderRadius:8, border:"1px solid #E5E7EB",
              backgroundColor:"#fff", cursor:"pointer", textAlign:"left", marginBottom:6,
            }}>
              <div style={{ fontFamily:fonts, fontSize:13, fontWeight:600, color:"#1a1a1a", marginBottom:2 }}>{entry.title}</div>
              <div style={{ fontSize:11, color:"#9CA3AF" }}>
                {new Date(entry.date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                {entry.mood ? ` · ${entry.mood}` : ""}
                {" · "}{entry.body.length > 80 ? entry.body.substring(0,80)+"..." : entry.body}
              </div>
            </button>
          ))}
        </div>
      )}

      <div style={{ ...cardSt, marginTop:24, backgroundColor:"#F5F3FF", borderColor:"#DDD6FE" }}>
        <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#5B21B6", marginBottom:6 }}>Prompts if you're not sure where to start</div>
        <div style={{ fontSize:12, color:"#5B21B6", lineHeight:1.8 }}>
          <p style={{margin:"0 0 4px"}}>• What do I remember that the records don't say?</p>
          <p style={{margin:"0 0 4px"}}>• What did the caseworker get wrong?</p>
          <p style={{margin:"0 0 4px"}}>• What was I feeling during that time that no one asked about?</p>
          <p style={{margin:"0 0 4px"}}>• What do I want people to understand about what happened?</p>
          <p style={{margin:"0 0 4px"}}>• What would I tell the child in those pages?</p>
          <p style={{margin:0}}>• What do I need to say that I've never been able to say?</p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   WHO ELSE? — Public Records Search Tool
   ═══════════════════════════════════════════ */

function SearchToolPage() {
  const [searchName, setSearchName] = useState("");
  const [altNames, setAltNames] = useState("");
  const [location, setLocation] = useState("");
  const [institution, setInstitution] = useState("");
  const [generated, setGenerated] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [aiResults, setAiResults] = useState(null);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiError, setAiError] = useState("");
  const [showAI, setShowAI] = useState(false);

  const names = [searchName, ...altNames.split(",").map(n=>n.trim())].filter(Boolean);
  const nameQueries = names.map(n => `"${n}"`);

  const buildLinks = () => {
    if (!searchName.trim()) return [];
    const primary = searchName.trim();
    const loc = location.trim();
    const inst = institution.trim();
    const links = [];

    // Judyrecords — best free state court search, but no deep-link URL — manual lookup required
    links.push({
      category: "Court Records",
      label: `Judyrecords — manually search "${primary}"`,
      url: "https://www.judyrecords.com",
      desc: `Best free index of US state court cases (760M+ records). Click through, then type "${primary}" into the search box on the homepage. Their site does not support pre-filled search links, so the search has to be done by hand.`,
      icon: "§",
      color: "#2563EB",
      manual: true,
    });

    // CourtListener — secondary, supports deep-linked search
    links.push({
      category: "Court Records",
      label: "CourtListener — federal + RECAP search",
      url: `https://www.courtlistener.com/?q=${encodeURIComponent(primary)}&type=r`,
      desc: "Free Federal court records (Free Law Project). Strongest coverage for federal dockets; some state courts via the RECAP project.",
      icon: "§",
      color: "#2563EB",
    });

    // Google court/arrest searches
    const googleBase = "https://www.google.com/search?q=";
    links.push({
      category: "Court Records",
      label: `Google — "${primary}" court records`,
      url: `${googleBase}${encodeURIComponent(`"${primary}" ${loc ? loc + " " : ""}court case OR arrest OR charged OR convicted`)}`,
      desc: "Google search with exact name match + legal keywords.",
      icon: "§",
      color: "#2563EB",
    });

    // Sex Offender Registry
    links.push({
      category: "Sex Offender Registry",
      label: "National Sex Offender Public Website (NSOPW)",
      url: `https://www.nsopw.gov/search-sex-offender-registries`,
      desc: "Searches all 50 state registries at once. Free. If they were convicted of a qualifying offense, they're listed with photo and offense details.",
      icon: "⚠",
      color: "#DC2626",
    });

    // Google sex offender search
    links.push({
      category: "Sex Offender Registry",
      label: `Google — "${primary}" sex offender`,
      url: `${googleBase}${encodeURIComponent(`"${primary}" ${loc ? loc + " " : ""}sex offender OR registry OR convicted`)}`,
      desc: "Sometimes news coverage or state registry pages show up in Google before the national database.",
      icon: "⚠",
      color: "#DC2626",
    });

    // News search
    links.push({
      category: "News Coverage",
      label: `Google News — "${primary}"`,
      url: `https://www.google.com/search?q=${encodeURIComponent(`"${primary}" ${loc ? loc + " " : ""}arrest OR abuse OR charged OR convicted`)}&tbm=nws`,
      desc: "Arrests and convictions are often covered by local newspapers.",
      icon: "★",
      color: "#0891B2",
    });

    // Newspaper archives
    links.push({
      category: "News Coverage",
      label: `Newspapers.com — historical archives`,
      url: `${googleBase}${encodeURIComponent(`"${primary}" site:newspapers.com`)}`,
      desc: "Historical newspaper archives. Older cases often show up here when they don't appear in modern search results.",
      icon: "★",
      color: "#0891B2",
    });

    // PACER
    links.push({
      category: "Federal Courts",
      label: "PACER — federal court records (registration required)",
      url: "https://pacer.uscourts.gov/register-account",
      desc: "Federal courts only — does NOT cover state child welfare, family court, or most criminal cases. Searching the case index is free; viewing documents costs $0.10/page (waived if you stay under $30/quarter). Registration requires your real name, address, and a credit card on file even if you're never charged. If you're hiding from an abuser, weigh that paper trail before signing up.",
      icon: "§",
      color: "#7C3AED",
    });

    // Civil lawsuits / other survivors
    links.push({
      category: "Civil Lawsuits by Other Survivors",
      label: `Judyrecords — manually search "${primary}"`,
      url: "https://www.judyrecords.com",
      desc: `Other survivors may have filed civil lawsuits. Click through, then type "${primary}" into the search box. Judyrecords does not support pre-filled search links, so this has to be done by hand.`,
      icon: "◉",
      color: "#7C3AED",
      manual: true,
    });

    // Institution search if provided
    if (inst) {
      links.push({
        category: "Institution Search",
        label: `Google — "${inst}" abuse OR lawsuit OR investigation`,
        url: `${googleBase}${encodeURIComponent(`"${inst}" abuse OR lawsuit OR investigation OR settlement`)}`,
        desc: `Search for other cases involving ${inst}. Institutions with patterns often face multiple lawsuits.`,
        icon: "◆",
        color: "#BE185D",
      });
      links.push({
        category: "Institution Search",
        label: `Judyrecords — manually search "${inst}"`,
        url: "https://www.judyrecords.com",
        desc: `Click through, then type "${inst}" into the search box on the homepage. Court cases naming the institution as a party.`,
        icon: "◆",
        color: "#BE185D",
        manual: true,
      });
    }

    // Alt names
    names.slice(1).forEach(alt => {
      links.push({
        category: "Alternate Names",
        label: `Judyrecords — manually search "${alt}"`,
        url: "https://www.judyrecords.com",
        desc: `Click through, then type "${alt}" into the search box. Alternate name/spelling.`,
        icon: "●",
        color: "#6B7280",
        manual: true,
      });
      links.push({
        category: "Alternate Names",
        label: `Google — "${alt}" arrest OR convicted`,
        url: `${googleBase}${encodeURIComponent(`"${alt}" ${loc ? loc + " " : ""}arrest OR convicted OR charged`)}`,
        desc: `Search alternate name/spelling in Google.`,
        icon: "●",
        color: "#6B7280",
      });
    });

    return links;
  };

  const runAISearch = async () => {
    if (!apiKey || !searchName.trim()) return;
    setAiProcessing(true);
    setAiError("");
    try {
      const prompt = `You are helping a survivor of childhood abuse research whether other people have filed legal cases against their abuser or the institution that enabled the abuse.

Search for public information about: ${searchName}
${altNames ? "Alternate names/spellings: " + altNames : ""}
${location ? "Location: " + location : ""}
${institution ? "Institution/organization: " + institution : ""}

Search for:
1. Criminal cases — arrests, charges, convictions for any offense, especially sexual abuse, assault, or child-related crimes
2. Civil lawsuits — cases filed against this person by other survivors
3. Sex offender registry status
4. News coverage of arrests, charges, or convictions
5. If an institution is named, search for lawsuits, investigations, or settlements involving that institution
6. Any patterns — multiple cases, multiple victims, institutional cover-ups

Be thorough. Search multiple variations of the name. Check news, court records, and registries.

Present findings clearly:
- What you found (with sources)
- What you didn't find (and what that means — absence of records doesn't mean absence of offense)
- Suggested next steps based on what you found
- If you found other survivors or cases, note the case numbers and courts so the person can look them up

Be direct but sensitive. You're talking to someone who was harmed by this person.`;

      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 4000,
          tools: [{ type: "web_search_20250305", name: "web_search" }],
          messages: [{ role: "user", content: prompt }],
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error?.message || `API error: ${response.status}`);
      }

      const data = await response.json();
      const text = data.content?.filter(c=>c.type==="text").map(c=>c.text).join("\n") || "No results returned.";
      setAiResults(text);
    } catch (e) {
      setAiError(e.message || "Something went wrong.");
    }
    setAiProcessing(false);
  };

  const links = generated ? buildLinks() : [];
  const categories = [...new Set(links.map(l=>l.category))];

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
      <h1 style={sectionTitle}>Who Else?</h1>
      <p style={{ fontSize:14, color:"#6B7280", lineHeight:1.7, marginBottom:20 }}>
        Search public records to find out if anyone else has filed against the person who harmed you — or the institution that let it happen. Finding other survivors changes everything.
      </p>

      <div style={{ padding:14, borderRadius:10, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", marginBottom:20, fontSize:12, color:"#065F46", lineHeight:1.6 }}>
        <strong>This tool generates search links.</strong> Nothing is sent anywhere. The searches happen in your own browser when you click the links. Your search terms stay on your device until you choose to click.
      </div>

      <div style={{ marginBottom:14 }}>
        <label style={labelSt}>Name of the person</label>
        <input value={searchName} onChange={e=>setSearchName(e.target.value)} placeholder="First and last name" style={inputSt} />
      </div>

      <div style={{ marginBottom:14 }}>
        <label style={labelSt}>Alternate names or spellings (optional)</label>
        <input value={altNames} onChange={e=>setAltNames(e.target.value)} placeholder="Maiden name, nickname, common misspelling — comma separated" style={inputSt} />
        <div style={{ fontSize:11, color:"#9CA3AF", marginTop:4 }}>Try middle names, Jr/Sr, and common misspellings. Court records often have errors.</div>
      </div>

      <div style={{ marginBottom:14 }}>
        <label style={labelSt}>City and state (optional but helps)</label>
        <input value={location} onChange={e=>setLocation(e.target.value)} placeholder="e.g., Hartford, CT" style={inputSt} />
      </div>

      <div style={{ marginBottom:20 }}>
        <label style={labelSt}>Institution or organization (optional)</label>
        <input value={institution} onChange={e=>setInstitution(e.target.value)} placeholder="e.g., Boy Scouts Troop 123, St. Mary's Church, Elm Street Group Home" style={inputSt} />
        <div style={{ fontSize:11, color:"#9CA3AF", marginTop:4 }}>Church, school, youth organization, group home, foster care agency — any entity that may have enabled the abuse.</div>
      </div>

      <button onClick={()=>setGenerated(true)} disabled={!searchName.trim()} style={{
        width:"100%", padding:"14px", borderRadius:10, border:"none",
        backgroundColor:searchName.trim()?"#1a1a1a":"#D1D5DB",
        color:"#fff", fontFamily:fonts, fontSize:15, fontWeight:600,
        cursor:searchName.trim()?"pointer":"default", marginBottom:8,
      }}>
        Generate Search Links
      </button>

      {generated && links.length > 0 && (
        <div style={{ marginTop:24 }}>
          <div style={{ fontSize:11, fontWeight:600, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:16 }}>
            {links.length} searches ready — click to open in a new tab
          </div>

          {categories.map(cat => (
            <div key={cat} style={{ marginBottom:20 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:8, paddingBottom:6, borderBottom:"1px solid #E5E7EB" }}>{cat}</div>
              {links.filter(l=>l.category===cat).map((link, i) => (
                <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" style={{
                  display:"block", padding:"12px 14px", borderRadius:8, border:"1px solid #E5E7EB",
                  backgroundColor:"#fff", textDecoration:"none", marginBottom:6, cursor:"pointer",
                }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:14, color:link.color }}>{link.icon}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:link.color, marginBottom:2, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                        {link.label}
                        {link.manual && (
                          <span style={{
                            fontSize:9, fontWeight:700, padding:"2px 6px", borderRadius:4,
                            backgroundColor:"#FEF3C7", color:"#92400E",
                            border:"1px solid #FDE68A", textTransform:"uppercase", letterSpacing:0.4,
                          }}>Manual search</span>
                        )}
                      </div>
                      <div style={{ fontSize:11, color:"#9CA3AF", lineHeight:1.5 }}>{link.desc}</div>
                    </div>
                    <span style={{ fontSize:14, color:"#D1D5DB" }}>↗</span>
                  </div>
                </a>
              ))}
            </div>
          ))}

          <div style={{ ...cardSt, backgroundColor:"#FFFBEB", borderColor:"#FDE68A", marginTop:16 }}>
            <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#92400E", marginBottom:6 }}>What to do with what you find</div>
            <div style={{ fontSize:12, color:"#92400E", lineHeight:1.7 }}>
              <p style={{margin:"0 0 6px"}}><strong>Found a court case?</strong> Write down the case number and court. You can request the full file from the clerk.</p>
              <p style={{margin:"0 0 6px"}}><strong>Found other survivors' lawsuits?</strong> Note the attorney names — these are lawyers who already know the case and may take yours.</p>
              <p style={{margin:"0 0 6px"}}><strong>Found a conviction?</strong> This is powerful corroboration for your own case, even if your specific abuse wasn't part of that case.</p>
              <p style={{margin:"0 0 6px"}}><strong>Found nothing?</strong> That doesn't mean nothing happened. Many abusers were never charged. Many cases were sealed. Many survivors never filed. Your experience is still valid.</p>
              <p style={{margin:0}}><strong>Found a pattern?</strong> Multiple cases against the same person or institution is exactly what attorneys and journalists need. Consider reaching out.</p>
            </div>
          </div>

          {/* AI-powered deep search */}
          <div style={{ ...cardSt, marginTop:16, backgroundColor:"#F0FDF4", borderColor:"#A7F3D0" }}>
            <div style={{ fontFamily:fonts, fontSize:14, fontWeight:700, color:"#065F46", marginBottom:8 }}>Want a deeper search?</div>
            <p style={{ fontSize:12, color:"#065F46", lineHeight:1.6, marginBottom:12 }}>
              Use AI to search the web, read results, and compile a report. Costs ~$0.50-1.00 per search. Requires an Anthropic API key.
            </p>
            {!showAI ? (
              <button onClick={()=>setShowAI(true)} style={{ padding:"10px 16px", borderRadius:8, border:"1px solid #A7F3D0", backgroundColor:"#fff", color:"#065F46", fontFamily:fonts, fontSize:12, fontWeight:600, cursor:"pointer" }}>
                Set up AI search →
              </button>
            ) : (
              <div>
                <div style={{marginBottom:10}}>
                  <label style={labelSt}>Anthropic API Key</label>
                  <input type="password" value={apiKey} onChange={e=>setApiKey(e.target.value)} placeholder="sk-ant-..." style={inputSt} />
                </div>
                {aiError && <div style={{ padding:10, borderRadius:8, backgroundColor:"#FEF2F2", border:"1px solid #FECACA", fontSize:12, color:"#991B1B", marginBottom:10 }}>{aiError}</div>}
                <button onClick={runAISearch} disabled={!apiKey||aiProcessing} style={{
                  width:"100%", padding:"12px", borderRadius:8, border:"none",
                  backgroundColor:(apiKey&&!aiProcessing)?"#065F46":"#D1D5DB",
                  color:"#fff", fontFamily:fonts, fontSize:13, fontWeight:600,
                  cursor:(apiKey&&!aiProcessing)?"pointer":"default",
                }}>
                  {aiProcessing ? "Searching..." : "Run AI Deep Search"}
                </button>
                {aiResults && (
                  <div style={{ marginTop:12, padding:14, borderRadius:8, backgroundColor:"#fff", border:"1px solid #E5E7EB", fontSize:13, color:"#374151", lineHeight:1.7, whiteSpace:"pre-wrap" }}>
                    {aiResults}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   BUILD MY CASE — Guided Dashboard
   ═══════════════════════════════════════════ */

function CaseDashboard({ setPage }) {
  const [caseState, setCaseState] = useState("");
  const [checks, setChecks] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await storage.get("gyr-case-progress");
        if (r?.value) {
          const saved = JSON.parse(r.value);
          setChecks(saved.checks || {});
          setCaseState(saved.caseState || "");
        }
      } catch(e) {}
      setLoaded(true);
    })();
  }, []);

  useEffect(() => {
    if (!loaded) return;
    (async () => { try { await storage.set("gyr-case-progress", JSON.stringify({ checks, caseState })); } catch(e){} })();
  }, [checks, caseState, loaded]);

  const toggle = (id) => setChecks(p => ({...p, [id]: !p[id]}));
  const sd = caseState ? STATES_DATA[STATE_ABBREVS[caseState]] : null;
  const hasActiveLookback = sd && sd.lk && (sd.lk.includes("ACTIVE") || sd.lk.includes("PERMANENT"));

  const steps = [
    {
      phase: "Step 0 — Get Your Documents",
      color: "#D97706",
      bg: "#FFFBEB",
      items: [
        { id:"get_id", label:"Make sure you have a photo ID", desc:"You need ID to request records. If you don't have one, we'll show you how to get it — free for former foster youth.", page:"documents" },
      ]
    },
    {
      phase: "Step 1 — Prepare",
      color: "#7C3AED",
      bg: "#F5F3FF",
      items: [
        { id:"read_prep", label:"Read the emotional preparation guide", desc:"What it's like to read your records, how to prepare, what to expect", page:"prep" },
        { id:"check_sol", label:"Check your state's legal deadlines", desc: sd ? `${caseState}: ${sd.sc?.substring(0,80)}${sd.sc?.length>80?"...":""}` : "Select your state above to see deadlines", page: sd ? null : null, action: sd ? ()=>setPage("records") : null },
      ]
    },
    {
      phase: "Step 2 — Request Your Records",
      color: "#2563EB",
      bg: "#EFF6FF",
      items: [
        { id:"req_welfare", label:"Send child welfare records request", desc:"Generate a letter to your state's child welfare agency asking for every record type by name", page:"records" },
        { id:"req_police", label:"Send police records FOIA request", desc:"Generate a FOIA request for incident reports, dispatch logs, and body camera footage", page:"records" },
        { id:"req_court", label:"Search for court records online", desc:"Check Judyrecords (manual search), CourtListener, state court portals, and PACER for criminal cases, protective orders, and divorce records", page:"records" },
      ]
    },
    {
      phase: "Step 3 — Investigate While You Wait",
      color: "#DC2626",
      bg: "#FEF2F2",
      items: [
        { id:"search_abuser", label:'Run "Who Else?" search on the abuser', desc:"Search public records for court cases, convictions, sex offender registry, and other survivors who filed", page:"search_tool" },
        { id:"prior_history", label:"Request abuser's prior CPS history", desc:"Ask the agency whether the abuser had prior reports or was in the Central Registry before they got to you", page:"records" },
        { id:"search_institution", label:"Search the institution (if applicable)", desc:"Check if the church, school, group home, or organization has faced other lawsuits or investigations", page:"search_tool" },
      ]
    },
    {
      phase: "Step 4 — Understand What You Got",
      color: "#059669",
      bg: "#F0FDF4",
      items: [
        { id:"read_guide", label:"Read the records interpretation guide", desc:"Learn agency language, spot gaps, understand when your file supports a legal claim", page:"records_guide" },
        { id:"analyze_ai", label:"Analyze your records with AI", desc:"Upload your file for a plain-language summary, timeline extraction, gap detection, and legal pattern analysis", page:"analyze" },
        { id:"build_timeline", label:"Build your timeline", desc:"Enter every event chronologically — from your records and your memory. Export it as a document.", page:"timeline" },
      ]
    },
    {
      phase: "Step 5 — Take Action",
      color: "#1a1a1a",
      bg: "#F8FAFC",
      items: [
        { id:"export_timeline", label:"Export your timeline for an attorney", desc:"Download your timeline as a document to bring to a free consultation", page:"timeline" },
      ]
    },
  ];

  const totalItems = steps.reduce((s,step) => s + step.items.length, 0);
  const completedItems = steps.reduce((s,step) => s + step.items.filter(item => checks[item.id]).length, 0);

  if (!loaded) return <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px", fontFamily:fonts, color:"#9CA3AF" }}>Loading...</div>;

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
      <h1 style={sectionTitle}>Build My Case</h1>
      <LegalDisclaimer />
      <p style={{ fontSize:14, color:"#6B7280", lineHeight:1.7, marginBottom:16 }}>
        Everything you need, in order. Work through the steps at your own pace. Check things off as you go — your progress is saved even if you close the page and come back later.
      </p>

      {completedItems > 0 && (
        <div style={{ marginBottom:20 }}>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#9CA3AF", marginBottom:6 }}>
            <span>{completedItems} of {totalItems} complete</span>
            <span>{Math.round(completedItems/totalItems*100)}%</span>
          </div>
          <div style={{ height:6, borderRadius:3, backgroundColor:"#E5E7EB" }}>
            <div style={{ height:6, borderRadius:3, backgroundColor:"#1a1a1a", width:`${completedItems/totalItems*100}%`, transition:"width 0.3s" }} />
          </div>
        </div>
      )}

      <div style={{ marginBottom:20 }}>
        <label style={labelSt}>Your state</label>
        <select value={caseState} onChange={e=>setCaseState(e.target.value)} style={{ ...inputSt, fontSize:15, padding:"13px 14px", cursor:"pointer", appearance:"auto" }}>
          <option value="">Choose a state...</option>
          {US_STATES.filter(s=>s!=="DC").map(s=><option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {sd && hasActiveLookback && (
        <div style={{ padding:12, borderRadius:10, backgroundColor:"#FEF2F2", border:"1.5px solid #DC2626", marginBottom:20, fontSize:13, color:"#991B1B", lineHeight:1.6 }}>
          <strong>Your state has an open window or eliminated the deadline.</strong> {sd.lk.length>120?sd.lk.substring(0,120)+"...":sd.lk}
          <div style={{ marginTop:6 }}>
            <button onClick={()=>setPage("records")} style={{ padding:"6px 12px", borderRadius:6, border:"1px solid #DC2626", backgroundColor:"#fff", color:"#991B1B", fontFamily:fonts, fontSize:11, fontWeight:600, cursor:"pointer" }}>
              See full details →
            </button>
          </div>
        </div>
      )}

      {steps.map((step, si) => (
        <div key={si} style={{ marginBottom:20 }}>
          <div style={{ fontSize:12, fontWeight:700, color:step.color, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10, paddingBottom:6, borderBottom:`2px solid ${step.color}20` }}>
            {step.phase}
          </div>
          {step.items.map(item => {
            const done = checks[item.id];
            return (
              <div key={item.id} style={{ display:"flex", gap:10, marginBottom:8, alignItems:"flex-start" }}>
                <button onClick={()=>toggle(item.id)} style={{
                  width:22, height:22, minWidth:22, borderRadius:6, marginTop:2,
                  border:`2px solid ${done?step.color:"#D1D5DB"}`,
                  backgroundColor:done?step.color:"#fff",
                  cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
                  color:"#fff", fontSize:12, fontWeight:700,
                }}>
                  {done?"✓":""}
                </button>
                <button onClick={()=>{if(item.page)setPage(item.page);else if(item.action)item.action();}} style={{
                  flex:1, padding:"10px 12px", borderRadius:8, border:"1px solid #E5E7EB",
                  backgroundColor:done?"#FAFAF9":"#fff", cursor:item.page?"pointer":"default",
                  textAlign:"left", opacity:done?0.6:1,
                }}>
                  <div style={{ fontFamily:fonts, fontSize:13, fontWeight:600, color:done?"#9CA3AF":"#1a1a1a", marginBottom:2, textDecoration:done?"line-through":"none" }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize:11, color:"#9CA3AF", lineHeight:1.5 }}>{item.desc}</div>
                </button>
              </div>
            );
          })}
        </div>
      ))}

      <div style={{ padding:10, borderRadius:8, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", fontSize:11, color:"#065F46", lineHeight:1.5 }}>
        Your progress is saved on your device. You can leave and come back anytime. Nothing is uploaded or shared.
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   ANALYZE MY RECORDS — AI Document Reader
   ═══════════════════════════════════════════ */

// Anthropic Sonnet 4 pricing (USD per token)
const PRICE_INPUT_PER_TOKEN = 3 / 1_000_000;
const PRICE_OUTPUT_PER_TOKEN = 15 / 1_000_000;

function formatCost(c) {
  if (c <= 0) return "$0.00";
  if (c < 0.01) return `$${c.toFixed(4)}`;
  if (c < 1)    return `$${c.toFixed(3)}`;
  return `$${c.toFixed(2)}`;
}

const MAX_FILES_SOFT = 10;
const MAX_BYTES_SOFT = 25 * 1024 * 1024; // 25 MB

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function AnalyzePage({ setPage }) {
  const [stage, setStage] = useState("intro"); // intro, setup, uploading, processing, results
  const [apiKey, setApiKey] = useState("");
  const [items, setItems] = useState([]); // [{ name, size, encoded }]
  const [analysisType, setAnalysisType] = useState("summary"); // summary, timeline, gaps, legal, full
  const [outputLang, setOutputLang] = useState("English");
  const [results, setResults] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const [showRaw, setShowRaw] = useState(false);
  const [streamedText, setStreamedText] = useState("");
  const [inputTokens, setInputTokens] = useState(0);
  const [outputTokens, setOutputTokens] = useState(0);
  const [keyStatus, setKeyStatus] = useState(""); // "", "testing", "ok", "bad"
  const [keyStatusMsg, setKeyStatusMsg] = useState("");
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [savedJustNow, setSavedJustNow] = useState(false);
  const streamRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const v = await storage.get("gyr-analyses");
        if (v?.value) setSavedAnalyses(JSON.parse(v.value));
      } catch (e) {}
    })();
  }, []);

  const saveCurrentAnalysis = async () => {
    if (!results) return;
    const entry = {
      id: Date.now().toString(36),
      savedAt: new Date().toISOString(),
      analysisType,
      outputLang,
      text: results,
      inputTokens,
      outputTokens,
      cost: inputTokens * PRICE_INPUT_PER_TOKEN + outputTokens * PRICE_OUTPUT_PER_TOKEN,
      preview: results.slice(0, 140).replace(/\s+/g, " ").trim(),
    };
    const next = [entry, ...savedAnalyses];
    setSavedAnalyses(next);
    setSavedJustNow(true);
    try { await storage.set("gyr-analyses", JSON.stringify(next)); } catch (e) {}
  };

  const openSavedAnalysis = (entry) => {
    setResults(entry.text);
    setInputTokens(entry.inputTokens || 0);
    setOutputTokens(entry.outputTokens || 0);
    setAnalysisType(entry.analysisType || "summary");
    setOutputLang(entry.outputLang || "English");
    setSavedJustNow(true);
    setStage("results");
  };

  const deleteSavedAnalysis = async (id) => {
    const next = savedAnalyses.filter(a => a.id !== id);
    setSavedAnalyses(next);
    try { await storage.set("gyr-analyses", JSON.stringify(next)); } catch (e) {}
  };

  const [stripping, setStripping] = useState(false);

  const handleFiles = async (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length === 0) return;
    setStripping(true);
    const newItems = [];
    try {
      for (const file of selected) {
        const isImage = file.type.startsWith("image/");
        let bytes;
        let mediaType;

        if (isImage) {
          bytes = await stripImageMetadata(file);
          if (!bytes) continue;
          mediaType = file.type === "image/png" ? "image/png" : "image/jpeg";
        } else {
          const ab = await file.arrayBuffer();
          bytes = await stripPdfMetadata(ab);
          mediaType = file.type || "application/pdf";
        }

        newItems.push({
          name: file.name,
          size: bytes.length,
          encoded: {
            type: isImage ? "image" : "document",
            source: { type: "base64", media_type: mediaType, data: uint8ToBase64(bytes) }
          },
        });
      }
      setItems(prev => [...prev, ...newItems]);
    } finally {
      setStripping(false);
      e.target.value = ""; // allow re-selecting the same file after removal
    }
  };

  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));
  const clearItems = () => setItems([]);

  const totalBytes = items.reduce((s, it) => s + it.size, 0);
  const overSoftCap = items.length > MAX_FILES_SOFT || totalBytes > MAX_BYTES_SOFT;

  const ANALYSIS_PROMPTS = {
    summary: `You are helping a survivor of childhood abuse or neglect understand their child welfare records. These records were written by caseworkers, investigators, and agency staff.

Analyze these documents and provide a PLAIN LANGUAGE summary. Be warm but honest. Do not use graphic language even if the source documents do — describe events factually but gently. This person may be reading about their own abuse for the first time through official documentation.

Structure your response:

**Overview**: How many reports/referrals are in this file? What date range do they cover? Which agencies were involved?

**What happened in your file**: Walk through the key events chronologically in plain language. Translate all agency jargon. Explain what each finding means (substantiated, unsubstantiated, closed at intake, etc.)

**Key people**: List caseworkers, investigators, and other professionals mentioned, with their roles.

**Decisions that were made**: What did the agency decide to do at each major point? Were services offered? Was the case opened or closed? Was removal considered?

**What stands out**: Note anything significant — patterns of repeated reports, investigations that were opened and closed quickly, risk assessments that were overridden, gaps in documentation.

End with: "This summary is based only on the documents you uploaded. There may be additional records that weren't included."

Remember: You're talking to the person this file is ABOUT. They are not a case. They are a person reading about their own childhood.`,

    timeline: `You are helping a survivor extract a timeline from their child welfare records.

Analyze these documents and extract every dated event into a chronological timeline. For each event include:
- **Date** (exact if available, approximate if not)
- **What happened** (in plain language — translate all agency jargon)
- **Who was involved** (caseworker names, agencies, other parties)
- **What was decided** (finding, action taken, case opened/closed)
- **Category** (use these: abuse_incident, system_contact, legal_action, placement_change, medical, family_event, investigation_opened, investigation_closed, report_filed)

Format as a clear chronological list. Flag any gaps where you'd expect records but don't see them.

Be gentle with language. This is someone's life.`,

    gaps: `You are helping a survivor identify gaps and missing information in their child welfare records.

Analyze these documents and identify:

**Timeline gaps**: Periods where records should exist but don't. Note the dates and what was happening before and after the gap.

**Missing attachments**: Any references to documents, reports, photos, or records that are mentioned but not included in the file (e.g., "see attached police report" with no attachment).

**Quick closures**: Investigations that were opened and closed unusually fast (same day, 1-2 days). Note dates and findings.

**Cross-references to other agencies**: Mentions of police reports, court cases, school reports, medical records, or other agency involvement that could be requested separately. List each one with enough detail to request it.

**Redaction patterns**: Note any sections that appear heavily redacted beyond standard reporter-identity redaction.

**Contradictions**: Places where different entries contradict each other — different caseworkers recording different observations, findings that don't match the evidence described.

**What to do about each gap**: For each gap identified, explain what the person can do — which agency to contact, what records to request, what questions to ask.

Frame everything constructively. These gaps are opportunities to build a more complete picture.`,

    legal: `You are helping a survivor understand whether their child welfare records may support a legal claim. You are NOT providing legal advice — you are identifying patterns that attorneys who specialize in these cases typically look for.

Analyze these documents and identify:

**Number of contacts**: How many times did the agency have contact with this family? Over how many years?

**Pattern of inaction**: Were there cases where abuse was substantiated or risk was assessed as high, but the case was closed or only voluntary services were offered?

**Known danger + continued access**: Did the agency know about a dangerous individual (prior findings, criminal history, sex offender status) and still allow them access to children?

**Repeated reports with same outcome**: Were similar reports filed multiple times with the same "closed" or "unsubstantiated" result?

**Retaliation after disclosure**: Is there evidence that a child disclosed abuse and was subsequently punished, moved, or returned to the abusive environment?

**Failed inter-agency communication**: Evidence that police, schools, medical providers, and child welfare each had pieces of information that were never connected?

**Risk assessments overridden**: Were structured decision-making tools (SDM) scored as high risk but the case was still closed?

For each pattern found, explain in plain language why it matters legally. End with: "This is not legal advice. If you see patterns here that concern you, consult an attorney who specializes in child welfare failure or childhood sexual abuse cases. Many offer free consultations. Check your state's filing deadlines at getmyrecords.org."`,

    full: `You are helping a survivor of childhood abuse or neglect fully understand their child welfare records. These records were written by caseworkers, investigators, and agency staff. The person reading your analysis is the person this file is ABOUT.

Provide a comprehensive analysis combining all of the following:

1. **PLAIN LANGUAGE SUMMARY**: Overview of the file — date range, number of reports, agencies involved, key events in chronological order. Translate ALL agency jargon.

2. **TIMELINE**: Every dated event extracted chronologically with what happened, who was involved, and what was decided.

3. **GAPS & MISSING INFORMATION**: Timeline gaps, missing attachments, quick closures, references to other agency records not included, contradictions.

4. **PATTERNS OF CONCERN**: Repeated reports with inaction, risk assessments overridden, known dangers ignored, retaliation after disclosure, failed inter-agency communication.

5. **RECORDS TO REQUEST NEXT**: Based on what's referenced in the file, list specific records from other agencies (police departments, courts, schools, medical providers) that could be requested to fill gaps.

6. **QUESTIONS TO ASK**: Specific questions this person should bring to an attorney or therapist based on what's in the file.

Be thorough but gentle. Use plain language throughout. Do not use graphic descriptions even if the source documents do. This person may be encountering their own story through official documentation for the first time.

End with crisis resources: 988 Suicide & Crisis Lifeline (call or text), RAINN 1-800-656-4673.`
  };

  const cancelAnalysis = () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  };

  const testApiKey = async () => {
    if (!apiKey || keyStatus === "testing") return;
    setKeyStatus("testing");
    setKeyStatusMsg("");
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1,
          messages: [{ role: "user", content: "hi" }],
        }),
      });
      if (res.ok) {
        setKeyStatus("ok");
        setKeyStatusMsg("Key works. You're ready to analyze.");
      } else {
        const errData = await res.json().catch(() => ({}));
        setKeyStatus("bad");
        setKeyStatusMsg(errData.error?.message || `Key rejected (HTTP ${res.status}).`);
      }
    } catch (e) {
      setKeyStatus("bad");
      setKeyStatusMsg(e.message || "Couldn't reach Anthropic. Check your connection.");
    }
  };

  const runAnalysis = async () => {
    if (!apiKey || items.length === 0) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setProcessing(true);
    setError("");
    setStreamedText("");
    setInputTokens(0);
    setOutputTokens(0);
    setSavedJustNow(false);
    setProgress("Sending your documents to Anthropic…");
    try {
      const messages = [{
        role: "user",
        content: [
          ...items.map(it => it.encoded),
          { type: "text", text: ANALYSIS_PROMPTS[analysisType] + (outputLang !== "English" ? `\n\nCRITICAL: Write your ENTIRE response in ${outputLang}. The source documents may be in English — read them in English but write all of your analysis, explanations, and findings in ${outputLang}. This person's primary language is ${outputLang}.` : "") }
        ]
      }];

      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 8000,
          stream: true,
          messages: messages,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error?.message || `API error: ${response.status}`);
      }

      setProgress("Reading your records…");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullText = "";
      let inTok = 0;
      let outTok = 0;
      let writingStarted = false;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (!payload) continue;
          try {
            const event = JSON.parse(payload);
            if (event.type === "message_start" && event.message?.usage) {
              inTok = event.message.usage.input_tokens || 0;
              outTok = event.message.usage.output_tokens || 0;
              setInputTokens(inTok);
              setOutputTokens(outTok);
            } else if (event.type === "content_block_delta" && event.delta?.type === "text_delta") {
              fullText += event.delta.text;
              setStreamedText(fullText);
              // Estimate output tokens from char count (~3.8 chars/token for English).
              // Snapped to the exact count by message_delta when the stream ends.
              setOutputTokens(Math.ceil(fullText.length / 3.8));
              if (!writingStarted) {
                writingStarted = true;
                setProgress("Writing your analysis…");
              }
            } else if (event.type === "message_delta" && event.usage) {
              outTok = event.usage.output_tokens || outTok;
              setOutputTokens(outTok);
            }
          } catch (e) { /* ignore malformed chunk */ }
        }
      }

      setResults(fullText || "No response received.");
      setStage("results");
    } catch (e) {
      if (e.name === "AbortError") {
        setProgress("");
        setStreamedText("");
        setInputTokens(0);
        setOutputTokens(0);
      } else {
        setError(e.message || "Something went wrong. Check your API key and try again.");
      }
    }
    abortRef.current = null;
    setProcessing(false);
    setProgress("");
  };

  const liveCost = inputTokens * PRICE_INPUT_PER_TOKEN + outputTokens * PRICE_OUTPUT_PER_TOKEN;

  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [streamedText]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [stage]);

  const copyResults = () => { navigator.clipboard.writeText(results || "").catch(()=>{}); };
  const downloadResults = () => {
    const b = new Blob([results || ""], { type:"text/plain" });
    const u = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = u;
    a.download = `records-analysis-${analysisType}.txt`;
    a.click();
    URL.revokeObjectURL(u);
  };

  // === Intro / Safety Gate ===
  if (stage === "intro") {
    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
        <h1 style={sectionTitle}>Understand Your Records</h1>
        <p style={{ fontSize:14, color:"#6B7280", lineHeight:1.7, marginBottom:20 }}>
          Upload your child welfare records and get a plain-language analysis. The AI reads your file and explains what happened — in words you can understand, not agency jargon.
        </p>

        <div style={{ padding:16, borderRadius:10, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", marginBottom:16, fontSize:13, color:"#065F46", lineHeight:1.7 }}>
          <strong>What we do — and don't — see.</strong>
          <p style={{margin:"8px 0 0"}}><strong>We never see your files.</strong> Your documents go straight from your browser to Anthropic's servers. Nothing passes through getmyrecords.org. We have no logs, no copies, no access to your analysis. We cannot be subpoenaed for what we never had.</p>
          <p style={{margin:"8px 0 0"}}><strong>Before you upload, this tool removes hidden metadata</strong> from your files (author names, GPS data, edit history) so that information doesn't reach the AI either.</p>
        </div>

        <div style={{ padding:16, borderRadius:10, backgroundColor:"#FFFBEB", border:"1px solid #FDE68A", marginBottom:16, fontSize:13, color:"#92400E", lineHeight:1.7 }}>
          <strong>What Anthropic sees — read this carefully.</strong>
          <p style={{margin:"8px 0 0"}}>Anthropic is the company that makes the AI (Claude) used here. Your files go to their servers to be analyzed. They have published policies about this:</p>
          <ul style={{margin:"6px 0 0 18px", padding:0}}>
            <li style={{marginBottom:4}}><strong>They do not train their AI on your data</strong> when you use the API (which is how this tool works).</li>
            <li style={{marginBottom:4}}><strong>They store API requests for up to 30 days</strong> for safety/abuse monitoring, then delete them.</li>
            <li style={{marginBottom:4}}><strong>They are SOC 2 Type II certified</strong> and based in the United States.</li>
            <li style={{marginBottom:4}}><strong>They can technically read what you send.</strong> The data is not encrypted in a way that prevents this.</li>
            <li><strong>U.S. legal process applies.</strong> A subpoena could compel Anthropic to disclose data still in the 30-day window. They say they push back and notify users when legally allowed.</li>
          </ul>
          <p style={{margin:"8px 0 0", fontSize:12}}>Read their full privacy policy at <strong>anthropic.com/legal/privacy</strong> before using this tool with the most sensitive files.</p>
        </div>

        <div style={{ padding:16, borderRadius:10, backgroundColor:"#FEF2F2", border:"1px solid #FECACA", marginBottom:16, fontSize:13, color:"#991B1B", lineHeight:1.7 }}>
          <strong>If you are at higher risk, do not use this tool.</strong>
          <p style={{margin:"8px 0 0"}}>If any of these apply to you, the safest thing is to read your records on a device that has never been connected to the internet, and not to upload them to any cloud AI service:</p>
          <ul style={{margin:"6px 0 0 18px", padding:0}}>
            <li style={{marginBottom:4}}>You are in active litigation where the other side could subpoena Anthropic.</li>
            <li style={{marginBottom:4}}>You are being actively pursued by an abuser, family member, or institution with legal resources.</li>
            <li style={{marginBottom:4}}>You believe a state actor or law enforcement may seek your records.</li>
            <li>Your safety depends on no third party — including Anthropic — ever holding a copy, even briefly.</li>
          </ul>
          <p style={{margin:"8px 0 0"}}>For these situations, consider running an AI model locally on your own computer instead. Free tools like <strong>Ollama</strong> (ollama.com) or <strong>LM Studio</strong> (lmstudio.ai) let you run a model that never sends anything over the internet. They are slower and the analysis isn't as good, but nothing leaves your device.</p>
        </div>

        <div style={{ padding:16, borderRadius:10, backgroundColor:"#F5F3FF", border:"1px solid #DDD6FE", marginBottom:16, fontSize:13, color:"#5B21B6", lineHeight:1.7 }}>
          <strong>Before you do this — emotionally.</strong>
          <p style={{margin:"8px 0 0"}}>This tool will read your records and tell you what's in them. That may include things that are hard to see in writing. If you haven't read the "Before You Read Your Records" guide, consider doing that first. You can stop at any time. The 988 Suicide & Crisis Lifeline (call or text 988) is available 24/7.</p>
        </div>

        <div style={cardSt}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>How it works</div>
          <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.8 }}>
            <p style={{margin:"0 0 6px"}}><strong>1.</strong> You provide an Anthropic API key (get one at console.anthropic.com — most analyses cost well under $1 depending on file size)</p>
            <p style={{margin:"0 0 6px"}}><strong>2.</strong> You upload your records (PDF or photos of pages)</p>
            <p style={{margin:"0 0 6px"}}><strong>3.</strong> You choose what kind of analysis you want</p>
            <p style={{margin:"0 0 6px"}}><strong>4.</strong> The AI reads your file and produces a plain-language report</p>
            <p style={{margin:0}}><strong>5.</strong> You can copy, download, or bring the report to a therapist or attorney</p>
          </div>
        </div>

        <div style={cardSt}>
          <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>What it costs</div>
          <div style={{ fontSize:13, color:"#6B7280", lineHeight:1.7 }}>
            <p style={{margin:"0 0 8px"}}>This tool uses the Anthropic API, which charges based on how much text the AI reads and writes. You pay Anthropic directly through your own API key — we don't charge anything and we don't see your billing. You can get a key at <strong>console.anthropic.com</strong> with as little as $5 credit, which is enough for many analyses.</p>
            <p style={{margin:"0 0 4px", fontWeight:600, color:"#374151"}}>Typical costs:</p>
            <p style={{margin:"0 0 2px"}}>• Short file (5–10 pages): ~$0.05–$0.15</p>
            <p style={{margin:"0 0 2px"}}>• Medium file (20–50 pages): ~$0.15–$0.50</p>
            <p style={{margin:0}}>• Large file (100+ pages): ~$0.50–$1.50</p>
          </div>
        </div>

        <button onClick={()=>setStage("setup")} style={{
          width:"100%", padding:"14px", borderRadius:10, border:"none", backgroundColor:"#1a1a1a",
          color:"#fff", fontFamily:fonts, fontSize:15, fontWeight:600, cursor:"pointer", marginTop:8, marginBottom:10,
        }}>
          I understand — let's start
        </button>
        <button onClick={()=>setPage("prep")} style={{
          width:"100%", padding:"12px", borderRadius:10, border:"1px solid #DDD6FE", backgroundColor:"#F5F3FF",
          color:"#5B21B6", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer",
        }}>
          Wait — I should read the preparation guide first
        </button>

        {savedAnalyses.length > 0 && (
          <div style={{ marginTop:24 }}>
            <div style={{ fontFamily:fonts, fontSize:13, fontWeight:700, color:"#1a1a1a", marginBottom:4 }}>
              Your saved analyses ({savedAnalyses.length})
            </div>
            <div style={{ fontSize:11, color:"#9CA3AF", marginBottom:10, lineHeight:1.5 }}>
              These are stored in this browser only, on this device. They don't sync, they don't go to our servers, and anyone with access to this device could open them.
            </div>
            {savedAnalyses.map(a => (
              <div key={a.id} style={{
                padding:"12px 14px", borderRadius:10, border:"1px solid #E5E7EB",
                backgroundColor:"#fff", marginBottom:8,
              }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:"#374151" }}>
                    {({summary:"Plain summary",timeline:"Timeline",gaps:"Gap detection",legal:"Legal patterns",full:"Full analysis"})[a.analysisType] || a.analysisType}
                    {a.outputLang && a.outputLang !== "English" ? ` · ${a.outputLang}` : ""}
                  </div>
                  <div style={{ fontSize:11, color:"#9CA3AF" }}>
                    {new Date(a.savedAt).toLocaleDateString(undefined, { year:"numeric", month:"short", day:"numeric" })}
                  </div>
                </div>
                <div style={{ fontSize:12, color:"#6B7280", lineHeight:1.5, marginBottom:8 }}>
                  {a.preview}{a.preview && a.preview.length >= 140 ? "…" : ""}
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  <button onClick={()=>openSavedAnalysis(a)} style={{
                    flex:1, padding:"7px", borderRadius:6, border:"1px solid #BFDBFE",
                    backgroundColor:"#EFF6FF", color:"#1E40AF",
                    fontFamily:fonts, fontSize:12, fontWeight:600, cursor:"pointer",
                  }}>Open</button>
                  <button onClick={()=>{
                    if (window.confirm("Delete this saved analysis from your device?")) deleteSavedAnalysis(a.id);
                  }} style={{
                    flex:"0 0 auto", padding:"7px 12px", borderRadius:6, border:"1px solid #FECACA",
                    backgroundColor:"#FEF2F2", color:"#991B1B",
                    fontFamily:fonts, fontSize:12, fontWeight:600, cursor:"pointer",
                  }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // === Setup: API Key + File Upload + Analysis Type ===
  if (stage === "setup") {
    return (
      <div style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
        <h2 style={{ fontFamily:serifFonts, fontSize:20, fontWeight:700, color:"#1a1a1a", marginBottom:20 }}>Set up your analysis</h2>

        <div style={{marginBottom:20}}>
          <label style={labelSt}>Your Anthropic API Key</label>
          <input
            type="password"
            value={apiKey}
            onChange={e=>{setApiKey(e.target.value); setKeyStatus(""); setKeyStatusMsg("");}}
            placeholder="sk-ant-..."
            style={inputSt}
          />
          <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:6 }}>
            <button
              onClick={testApiKey}
              disabled={!apiKey || keyStatus === "testing"}
              style={{
                padding:"6px 12px", borderRadius:6, border:"1px solid #D1D5DB",
                backgroundColor: apiKey && keyStatus !== "testing" ? "#fff" : "#F3F4F6",
                color: apiKey && keyStatus !== "testing" ? "#374151" : "#9CA3AF",
                fontFamily:fonts, fontSize:11, fontWeight:600,
                cursor: apiKey && keyStatus !== "testing" ? "pointer" : "default",
              }}
            >
              {keyStatus === "testing" ? "Testing…" : "Test key"}
            </button>
            {keyStatus === "ok" && (
              <span style={{ fontSize:11, color:"#059669", fontWeight:600 }}>✓ {keyStatusMsg}</span>
            )}
            {keyStatus === "bad" && (
              <span style={{ fontSize:11, color:"#B91C1C", fontWeight:600 }}>✕ {keyStatusMsg}</span>
            )}
          </div>
          <div style={{ fontSize:11, color:"#9CA3AF", marginTop:6, lineHeight:1.5 }}>Get one at console.anthropic.com. Your key goes directly to Anthropic's servers — we never see it. It is not stored. Testing it costs a fraction of a cent.</div>
        </div>

        <div style={{marginBottom:20}}>
          <label style={labelSt}>Upload your records</label>
          <input type="file" multiple accept=".pdf,image/*" onChange={handleFiles} style={{ ...inputSt, padding:"10px" }} />
          {stripping && (
            <div style={{ fontSize:12, color:"#5B21B6", marginTop:6 }}>
              Removing hidden metadata (author, GPS, edit history)…
            </div>
          )}
          {items.length > 0 && (
            <div style={{ marginTop:8, border:"1px solid #E5E7EB", borderRadius:8, overflow:"hidden" }}>
              {items.map((it, i) => (
                <div key={i} style={{
                  display:"flex", alignItems:"center", justifyContent:"space-between",
                  padding:"8px 10px", borderBottom: i < items.length-1 ? "1px solid #F3F4F6" : "none",
                  backgroundColor:"#fff",
                }}>
                  <div style={{ minWidth:0, flex:1, marginRight:8 }}>
                    <div style={{ fontSize:12, color:"#374151", fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{it.name}</div>
                    <div style={{ fontSize:11, color:"#9CA3AF" }}>{formatBytes(it.size)} · metadata stripped</div>
                  </div>
                  <button onClick={() => removeItem(i)} aria-label={`Remove ${it.name}`} style={{
                    flex:"0 0 auto", width:24, height:24, borderRadius:12, border:"1px solid #E5E7EB",
                    backgroundColor:"#fff", color:"#9CA3AF", cursor:"pointer", fontSize:14, lineHeight:1,
                    display:"flex", alignItems:"center", justifyContent:"center",
                  }}>×</button>
                </div>
              ))}
              <div style={{
                padding:"8px 10px", backgroundColor:"#F9FAFB", borderTop:"1px solid #F3F4F6",
                display:"flex", justifyContent:"space-between", alignItems:"center",
              }}>
                <div style={{ fontSize:11, color:"#6B7280" }}>
                  {items.length} file{items.length!==1?"s":""} · {formatBytes(totalBytes)} total
                </div>
                <button onClick={clearItems} style={{
                  border:"none", backgroundColor:"transparent", color:"#9CA3AF", fontSize:11, cursor:"pointer", padding:0,
                }}>Clear all</button>
              </div>
            </div>
          )}
          {overSoftCap && (
            <div style={{ marginTop:8, padding:10, borderRadius:8, backgroundColor:"#FFFBEB", border:"1px solid #FDE68A", fontSize:12, color:"#92400E", lineHeight:1.5 }}>
              <strong>This is a lot to send at once.</strong> Anthropic's API may reject requests over ~32 MB, and a single failed request still costs you for what was processed. Consider running this in two passes — try your most important file first, then add more if you want a combined analysis.
            </div>
          )}
          <div style={{ fontSize:11, color:"#9CA3AF", marginTop:6, lineHeight:1.5 }}>PDF files or photos/scans of pages. You can add files in multiple steps and remove any you change your mind about. Hidden metadata is removed in your browser before anything is sent. Files never touch our servers.</div>
        </div>

        <div style={{marginBottom:20}}>
          <label style={labelSt}>What kind of analysis?</label>
          {[
            { id:"summary", label:"Plain Language Summary", desc:"What's in your file, explained in words you can understand", color:"#059669" },
            { id:"timeline", label:"Timeline Extraction", desc:"Every dated event pulled into chronological order", color:"#2563EB" },
            { id:"gaps", label:"Gap Detection", desc:"Find what's missing — holes in the timeline, missing attachments, quick closures", color:"#D97706" },
            { id:"legal", label:"Legal Pattern Analysis", desc:"Identify patterns that attorneys look for in child welfare failure cases", color:"#DC2626" },
            { id:"full", label:"Full Analysis", desc:"Everything above in one report — summary, timeline, gaps, and legal patterns", color:"#7C3AED" },
          ].map(opt => (
            <button key={opt.id} onClick={()=>setAnalysisType(opt.id)} style={{
              width:"100%", padding:"12px 14px", borderRadius:8, textAlign:"left", cursor:"pointer", marginBottom:6,
              border:`1.5px solid ${analysisType===opt.id?opt.color:"#E5E7EB"}`,
              backgroundColor:analysisType===opt.id?`${opt.color}08`:"#fff",
            }}>
              <div style={{ fontSize:13, fontWeight:600, color:analysisType===opt.id?opt.color:"#374151", marginBottom:1 }}>
                {analysisType===opt.id?"✓ ":""}{opt.label}
              </div>
              <div style={{ fontSize:11, color:"#9CA3AF" }}>{opt.desc}</div>
            </button>
          ))}
        </div>

        <div style={{marginBottom:20}}>
          <label style={labelSt}>Response language</label>
          <select value={outputLang} onChange={e=>setOutputLang(e.target.value)} style={{ ...inputSt, cursor:"pointer", appearance:"auto" }}>
            {["English","Spanish / Español","Chinese / 中文","Haitian Creole / Kreyòl Ayisyen","Vietnamese / Tiếng Việt","Korean / 한국어","Arabic / العربية","Russian / Русский","Portuguese / Português","French / Français","Tagalog","Hindi / हिन्दी","Somali / Soomaali","Burmese / မြန်မာ","Nepali / नेपाली","Swahili / Kiswahili"].map(lang => (
              <option key={lang} value={lang.split(" / ")[0]}>{lang}</option>
            ))}
          </select>
          <div style={{ fontSize:11, color:"#9CA3AF", marginTop:4 }}>The AI will read your English-language records and write the analysis in your chosen language.</div>
        </div>

        {error && (
          <div style={{ padding:12, borderRadius:8, backgroundColor:"#FEF2F2", border:"1px solid #FECACA", fontSize:13, color:"#991B1B", marginBottom:16, lineHeight:1.6 }}>
            {error}
          </div>
        )}

        <button onClick={runAnalysis} disabled={!apiKey || items.length===0 || processing || stripping} style={{
          width:"100%", padding:"14px", borderRadius:10, border:"none",
          backgroundColor:(apiKey && items.length>0 && !processing && !stripping)?"#1a1a1a":"#D1D5DB",
          color:"#fff", fontFamily:fonts, fontSize:15, fontWeight:600,
          cursor:(apiKey && items.length>0 && !processing && !stripping)?"pointer":"default",
        }}>
          {processing ? progress : "Analyze My Records"}
        </button>

        {processing && (
          <div style={{ marginTop:12, padding:14, borderRadius:8, backgroundColor:"#F5F3FF", border:"1px solid #DDD6FE", lineHeight:1.6 }}>
            <div style={{ fontSize:13, color:"#5B21B6", fontWeight:600, textAlign:"center", marginBottom:8 }}>{progress}</div>

            <div style={{ display:"flex", justifyContent:"space-between", gap:8, fontSize:11, color:"#5B21B6", padding:"8px 10px", backgroundColor:"#fff", borderRadius:6, border:"1px solid #EDE9FE", marginBottom:10 }}>
              <div>
                <div style={{ fontSize:10, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.5 }}>Input</div>
                <div style={{ fontWeight:600 }}>{inputTokens ? `${inputTokens.toLocaleString()} tok` : "…"}</div>
              </div>
              <div>
                <div style={{ fontSize:10, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.5 }}>Output</div>
                <div style={{ fontWeight:600 }}>{outputTokens ? `${outputTokens.toLocaleString()} tok` : "…"}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:10, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.5 }}>Cost so far</div>
                <div style={{ fontWeight:700, color:"#1a1a1a" }}>{formatCost(liveCost)}</div>
              </div>
            </div>

            {streamedText && (
              <div
                ref={streamRef}
                style={{
                  maxHeight:240, overflowY:"auto", padding:"10px 12px", borderRadius:6,
                  backgroundColor:"#fff", border:"1px solid #EDE9FE",
                  fontSize:12, color:"#374151", lineHeight:1.6, whiteSpace:"pre-wrap",
                  fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {streamedText}
              </div>
            )}

            <button onClick={cancelAnalysis} style={{
              width:"100%", marginTop:10, padding:"10px", borderRadius:8, border:"1px solid #FECACA",
              backgroundColor:"#FEF2F2", color:"#991B1B", fontFamily:fonts, fontSize:12, fontWeight:600, cursor:"pointer",
            }}>
              Cancel — I forgot to add something
            </button>

            <div style={{fontSize:11, color:"#9CA3AF", marginTop:8, textAlign:"center"}}>
              This window is doing all the work. Your files are being sent directly to Anthropic. Nothing touches our servers. Cancelling stops sending — you'll only be charged for what was already generated.
            </div>
          </div>
        )}

        <button onClick={()=>setStage("intro")} style={{ width:"100%", marginTop:10, padding:"10px", borderRadius:8, border:"none", backgroundColor:"transparent", color:"#9CA3AF", fontFamily:fonts, fontSize:12, cursor:"pointer" }}>← Back</button>
      </div>
    );
  }

  // === Results ===
  if (stage === "results" && results) {
    // Simple markdown-ish rendering: bold, headers, paragraphs
    const renderResults = (text) => {
      return text.split("\n").map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} style={{height:8}} />;
        if (trimmed.startsWith("## ")) return <h2 key={i} style={{ fontFamily:serifFonts, fontSize:18, fontWeight:700, color:"#1a1a1a", margin:"20px 0 8px" }}>{trimmed.replace(/^## /,"")}</h2>;
        if (trimmed.startsWith("# ")) return <h1 key={i} style={{ fontFamily:serifFonts, fontSize:20, fontWeight:700, color:"#1a1a1a", margin:"24px 0 8px" }}>{trimmed.replace(/^# /,"")}</h1>;
        if (trimmed.startsWith("**") && trimmed.endsWith("**")) return <div key={i} style={{ fontSize:14, fontWeight:700, color:"#1a1a1a", margin:"16px 0 6px" }}>{trimmed.replace(/\*\*/g,"")}</div>;
        if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) return <div key={i} style={{ fontSize:13, color:"#374151", lineHeight:1.7, paddingLeft:16, margin:"4px 0", position:"relative" }}><span style={{position:"absolute",left:0}}>•</span>{trimmed.replace(/^[-•] /,"").replace(/\*\*(.*?)\*\*/g, (m,p1)=>`${p1}`)}</div>;
        // Inline bold
        const parts = trimmed.split(/\*\*(.*?)\*\*/g);
        return <p key={i} style={{ fontSize:13, color:"#374151", lineHeight:1.7, margin:"6px 0" }}>
          {parts.map((part, j) => j % 2 === 1 ? <strong key={j}>{part}</strong> : part)}
        </p>;
      });
    };

    return (
      <div data-printable="true" style={{ maxWidth:640, margin:"0 auto", padding:"28px 20px 40px" }}>
        <h1 style={sectionTitle}>Your Analysis</h1>

        <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:8 }} data-no-print="true">
          <button onClick={copyResults} style={{ flex:"1 1 100px", padding:"12px", borderRadius:8, border:"none", backgroundColor:"#1a1a1a", color:"#fff", fontFamily:fonts, fontSize:14, fontWeight:600, cursor:"pointer" }}>Copy</button>
          <button onClick={downloadResults} style={{ flex:"1 1 100px", padding:"12px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#374151", fontFamily:fonts, fontSize:14, fontWeight:500, cursor:"pointer" }}>Download</button>
          <button onClick={() => window.print()} style={{ flex:"1 1 100px", padding:"12px", borderRadius:8, border:"1px solid #D1D5DB", backgroundColor:"#fff", color:"#374151", fontFamily:fonts, fontSize:14, fontWeight:500, cursor:"pointer" }}>Print</button>
        </div>

        <div data-no-print="true" style={{ marginBottom:16 }}>
          {!savedJustNow ? (
            <button onClick={saveCurrentAnalysis} style={{
              width:"100%", padding:"10px", borderRadius:8, border:"1px solid #BFDBFE",
              backgroundColor:"#EFF6FF", color:"#1E40AF",
              fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer",
            }}>
              Save to my device
            </button>
          ) : (
            <div style={{
              padding:"10px 12px", borderRadius:8, backgroundColor:"#F0FDF4",
              border:"1px solid #A7F3D0", color:"#065F46", fontSize:12, lineHeight:1.5,
            }}>
              ✓ Saved on this device only. It stays in this browser, on this computer. Anyone with access to this device could open it. Clear it from the analyzer's home page if you need it gone.
            </div>
          )}
        </div>

        {(inputTokens > 0 || outputTokens > 0) && (
          <div style={{ display:"flex", justifyContent:"space-between", gap:8, fontSize:11, color:"#6B7280", padding:"10px 12px", backgroundColor:"#F9FAFB", borderRadius:8, border:"1px solid #E5E7EB", marginBottom:16 }}>
            <div>
              <div style={{ fontSize:10, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.5 }}>Input</div>
              <div style={{ fontWeight:600, color:"#374151" }}>{inputTokens.toLocaleString()} tok</div>
            </div>
            <div>
              <div style={{ fontSize:10, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.5 }}>Output</div>
              <div style={{ fontWeight:600, color:"#374151" }}>{outputTokens.toLocaleString()} tok</div>
            </div>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:10, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.5 }}>Total cost</div>
              <div style={{ fontWeight:700, color:"#1a1a1a" }}>{formatCost(liveCost)}</div>
            </div>
          </div>
        )}

        <div style={{ padding:14, borderRadius:10, backgroundColor:"#F5F3FF", border:"1px solid #DDD6FE", marginBottom:16, fontSize:12, color:"#5B21B6", lineHeight:1.6 }}>
          Take your time with this. You don't have to read it all now. If you need to talk to someone: <strong>988</strong> (call or text) or <strong>1-800-656-4673</strong> (RAINN).
        </div>

        <div style={{ ...cardSt, lineHeight:1.7 }}>
          {showRaw ? (
            <pre style={{ fontFamily:"monospace", fontSize:11, whiteSpace:"pre-wrap", wordBreak:"break-word", color:"#374151", margin:0 }}>{results}</pre>
          ) : (
            renderResults(results)
          )}
        </div>

        <button onClick={()=>setShowRaw(!showRaw)} style={{ padding:"6px 12px", border:"none", backgroundColor:"transparent", color:"#9CA3AF", fontFamily:fonts, fontSize:11, cursor:"pointer" }}>
          {showRaw ? "Show formatted" : "Show raw text"}
        </button>

        <div style={{ marginTop:20 }}>
          <button onClick={()=>{setStage("setup");setResults(null);}} style={{
            width:"100%", padding:"12px", borderRadius:10, border:"1px solid #D1D5DB", backgroundColor:"#fff",
            color:"#374151", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer", marginBottom:8,
          }}>
            Run another analysis on the same file
          </button>
          <button onClick={()=>setPage("records_guide")} style={{
            width:"100%", padding:"12px", borderRadius:10, border:"1px solid #BFDBFE", backgroundColor:"#EFF6FF",
            color:"#1E40AF", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer", marginBottom:8,
          }}>
            Help me understand what I'm reading →
          </button>
          <button onClick={()=>setPage("timeline")} style={{
            width:"100%", padding:"12px", borderRadius:10, border:"1px solid #E5E7EB", backgroundColor:"#fff",
            color:"#374151", fontFamily:fonts, fontSize:13, fontWeight:600, cursor:"pointer",
          }}>
            Build my timeline with what I've found →
          </button>
        </div>

        <div style={{ padding:10, borderRadius:8, backgroundColor:"#F0FDF4", border:"1px solid #A7F3D0", fontSize:11, color:"#065F46", lineHeight:1.5, marginTop:16 }}>
          This analysis was generated on your device and sent directly to Anthropic. Nothing was stored, logged, or saved. When you leave this page, it's gone — download or copy it if you want to keep it.
        </div>
      </div>
    );
  }

  return null;
}

/* ═══════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════ */

export default function App() {
  const [page, setPage] = useState("home");
  const [crisis, setCrisis] = useState(false);
  const [reports, setReports] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [readingMode, setReadingMode] = useState("normal"); // normal, large
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await storage.get("gyr-pattern-db", true);
        if (r?.value) setReports(JSON.parse(r.value));
        else { setReports([]); }
      } catch(e) { setReports([]); }
      try {
        const m = await storage.get("gyr-reading-mode");
        if (m?.value === "large") setReadingMode("large");
      } catch(e) {}
      setLoaded(true);
    })();
  }, []);

  const toggleReadingMode = async () => {
    const next = readingMode === "large" ? "normal" : "large";
    setReadingMode(next);
    try { await storage.set("gyr-reading-mode", next); } catch (e) {}
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    scrollRef.current?.scrollTo(0, 0);
  }, [page]);

  const navigate = (p) => { setPage(p); setCrisis(false); };

  // Quick exit on Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key !== "Escape") return;
      try {
        document.title = "Weather";
        document.documentElement.innerHTML =
          '<head><meta name="viewport" content="width=device-width, initial-scale=1"><title>Weather</title></head><body style="margin:0;background:#fff"></body>';
      } catch (err) {}
      try { window.history.replaceState(null, "", QUICK_EXIT_URL); } catch (err) {}
      window.location.replace(QUICK_EXIT_URL);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  if (!loaded) return (
    <div style={{ minHeight:"100vh", backgroundColor:"#FAFAF9", display:"flex", alignItems:"center", justifyContent:"center" }}>

      <div style={{ fontFamily:fonts, color:"#9CA3AF" }}>Loading...</div>
    </div>
  );

  return (
    <div ref={scrollRef} style={{
      minHeight:"100vh", backgroundColor:"#FAFAF9", fontFamily:fonts, paddingBottom:60, overflowY:"auto",
      zoom: readingMode === "large" ? 1.2 : 1,
    }}>

      <CrisisBar expanded={crisis} onToggle={() => setCrisis(!crisis)} readingMode={readingMode} onToggleReading={toggleReadingMode} />
      <LanguageBar />
      <BetaBanner setPage={navigate} />
      {page === "home" && <HomePage setPage={navigate} reportCount={reports.length} />}
      {page === "sense" && <SensePage setPage={navigate} />}
      {page === "prep" && <PrepPage setPage={navigate} />}
      {page === "records_guide" && <RecordsGuidePage setPage={navigate} />}
      {page === "documents" && <DocumentsGuidePage setPage={navigate} />}
      {page === "timeline" && <TimelinePage />}
      {page === "records" && <RecordsPage setPage={navigate} />}
      {page === "case" && <CaseDashboard setPage={navigate} />}
      {page === "analyze" && <AnalyzePage setPage={navigate} />}
      {page === "search_tool" && <SearchToolPage />}
      {/* "story" route disabled — StoryPage component kept for future re-enable */}
      {page === "pattern" && <PatternPage reports={reports} setReports={setReports} />}
      {page === "about" && <AboutPage setPage={navigate} />}
      {page === "privacy" && <PrivacyPage setPage={navigate} />}
      {page === "hide" && <HideYourselfPage setPage={navigate} />}
      {page === "contribute" && <ContributePage setPage={navigate} />}
      {page === "crisis" && <CrisisPage />}
      <NavBar page={page} setPage={navigate} />
    </div>
  );
}


const QUICK_EXIT_URL = "https://www.weather.com";

function QuickExitButton() {
  const handleExit = () => {
    try {
      // Wipe the visible DOM immediately so sensitive content disappears
      // regardless of how long the destination takes to load.
      document.title = "Weather";
      document.documentElement.innerHTML =
        '<head><meta name="viewport" content="width=device-width, initial-scale=1"><title>Weather</title></head><body style="margin:0;background:#fff"></body>';
    } catch (e) {}
    try { window.history.replaceState(null, "", QUICK_EXIT_URL); } catch (e) {}
    window.location.replace(QUICK_EXIT_URL);
  };
  return (
    <button
      onClick={handleExit}
      data-no-print="true"
      aria-label="Quick exit — leave this site immediately. You can also press the Escape key."
      title="Quick exit (or press Esc)"
      style={{
        padding:"5px 10px", borderRadius:14, border:"none",
        backgroundColor:"#DC2626", color:"#fff",
        fontFamily:fonts, fontSize:11, fontWeight:700,
        cursor:"pointer", boxShadow:"0 1px 3px rgba(220,38,38,0.3)",
        display:"flex", alignItems:"center", gap:4,
        whiteSpace:"nowrap",
      }}
    >
      <span aria-hidden="true">✕</span>
      Exit
      <span style={{
        fontSize:9, fontWeight:600, opacity:0.85,
        padding:"1px 4px", borderRadius:3,
        border:"1px solid rgba(255,255,255,0.4)",
      }}>Esc</span>
    </button>
  );
}
